import React, { useState, useEffect, useRef, useMemo } from "react";
import {
 BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
 XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend
} from "recharts";
import { ToastHost, SetupChecklistWidget, LocationSwitcher, TeamAndAccess, AddTeamMemberWizard, CompanyOnboarding, CompanyProfile, InviteAcceptScreen, toast } from "./iv/intelligence";
import { LegacyPlanPage, InsuranceStatusWidget, LegacyStatusWidget } from "./iv/r3";
import { ImportCenter, QuickReportsPanel, AdvancedFilterPanel } from "./iv/r4";
import { AppSwitcher, CombinedDashboardWidget, SyncManagementPanel, ARMWorkOrdersPage } from "./iv/bridge";
import { DocumentsCenter, ScheduleDayView, EquipmentTracking, ContactsDedupe, XactimateBoard, TimecardsPage, MaterialsPage, ServiceRequestsPage, ControlCenter, MapTechPins, GlobalClockButton, NotificationBell, JobSideOverlay } from "./iv/r5_r6";
import { BillingPage } from "./iv/r7";
import { EmailLogPage } from "./iv/r9";
import { CorporateMasterApp, shouldRenderCorporate } from "./iv/r10";
import { QuickBooksPage } from "./iv/qb";
import { MyProfilePage, PayrollProvidersPage, SalesQuarterlyAndMoM, LinkTeamMemberPicker } from "./iv/parked";
import { shouldRenderR11Public, R11PublicApp, RestorationOverlay, RestorationReportsPage, ScheduleAllViewsPage, PinPrintSendButtons } from "./iv/round11";
import { SaveNoteButton, BackButton, MentionEmailDropdown, CustomerPopout, JobSectionPercentages, AvgJobSizePage, TotalInvoicesCard, PnLPage as PnLv2Page, SpecialPermissionsPage } from "./iv/round12";
import { WaterCategoryClassFields, BrandingPanel, EquipmentR14, LinkedJobsBar, propagateSharedField } from "./iv/round14";
import { ChatNowPage, startChatPresence, useChatUnread } from "./iv/round17_chat";
import { FieldProIQPanel, ForgotPasswordLink, ResetPasswordPage, shouldRenderResetPassword, InviteRequiredNote } from "./iv/fieldproiq";

// ─── FONTS ───────────────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&family=DM+Sans:wght@300;400;500;600&display=swap";
document.head.appendChild(fontLink);

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const LOCATIONS = [
 { id: 1, name: "HQ – North Branch", address: "4821 Commerce Blvd, Suite 100", active: true },
 { id: 2, name: "South District", address: "1150 Industrial Way", active: true },
 { id: 3, name: "East Zone", address: "3300 Gateway Dr", active: true },
 { id: 4, name: "West Campus", address: "9090 Harbor Pkwy", active: false },
 { id: 5, name: "Downtown Hub", address: "200 Main St, Floor 5", active: true },
];

const TECHNICIANS = [
 { id: "T01", name: "Marcus Rivera", role: "Lead Tech", status: "On Job", location: "4821 Commerce Blvd", job: "J-2041", lat: 29.76, lng: -95.37, phone: "713-555-0101", cert: "IICRC WRT, ASD", vehicle: "Unit 7 – F-250", hours: 312, rating: 4.9 },
 { id: "T02", name: "DeShawn Carter", role: "Tech II", status: "En Route", location: "I-45 N", job: "J-2045", lat: 29.81, lng: -95.40, phone: "713-555-0102", cert: "IICRC WRT", vehicle: "Unit 3 – Ram 1500", hours: 245, rating: 4.7 },
 { id: "T03", name: "Priya Patel", role: "Tech III", status: "Available", location: "HQ North", job: "—", lat: 29.76, lng: -95.38, phone: "713-555-0103", cert: "IICRC WRT, FSRT", vehicle: "Unit 11 – Transit", hours: 188, rating: 4.8 },
 { id: "T04", name: "Kyle Hutchins", role: "Lead Tech", status: "On Job", location: "3300 Gateway Dr", job: "J-2038", lat: 29.74, lng: -95.42, phone: "713-555-0104", cert: "IICRC WRT, ASD, FSRT", vehicle: "Unit 2 – F-350", hours: 401, rating: 5.0 },
 { id: "T05", name: "Maria Santos", role: "Tech II", status: "Break", location: "1150 Industrial Way", job: "—", lat: 29.72, lng: -95.33, phone: "713-555-0105", cert: "IICRC WRT", vehicle: "Unit 9 – Sprinter", hours: 167, rating: 4.6 },
 { id: "T06", name: "James Okafor", role: "Tech I", status: "On Job", location: "9090 Harbor Pkwy", job: "J-2047", lat: 29.79, lng: -95.44, phone: "713-555-0106", cert: "IICRC WRT", vehicle: "Unit 15 – F-150", hours: 89, rating: 4.5 },
 { id: "T07", name: "Tiffany Broome", role: "Tech II", status: "Available", location: "HQ North", job: "—", lat: 29.77, lng: -95.36, phone: "713-555-0107", cert: "IICRC WRT, AMRT", vehicle: "Unit 6 – Transit", hours: 231, rating: 4.8 },
 { id: "T08", name: "Rodrigo Vega", role: "Lead Tech", status: "En Route", location: "Hwy 290 W", job: "J-2049", lat: 29.80, lng: -95.48, phone: "713-555-0108", cert: "IICRC WRT, ASD, AMRT", vehicle: "Unit 1 – F-350 XL", hours: 534, rating: 4.9 },
];

const DRIVERS = [
 { id: "D01", name: "Tony Marchetti", vehicle: "Unit 2 – Box Truck", status: "Delivering", route: "HQ → East Zone", miles: 14.2, load: "Drying Equipment – Job J-2038" },
 { id: "D02", name: "Keisha Brown", vehicle: "Unit 5 – Sprinter", status: "Available", route: "—", miles: 0, load: "—" },
 { id: "D03", name: "Earl Simmons", vehicle: "Unit 8 – F-350", status: "In Transit", route: "South District → Downtown Hub", miles: 8.7, load: "Inventory Restock" },
];

const CUSTOMERS = [
 { id: "C001", name: "Linda Hargrove", type: "Residential", phone: "713-555-2200", email: "lhargrove@email.com", address: "4200 Maple Ln", jobs: 3, balance: 0, lastJob: "J-2041", insurer: "State Farm", adjuster: "Tom Bridges" },
 { id: "C002", name: "Westbrook Plaza LLC", type: "Commercial", phone: "713-555-3300", email: "ops@westbrookplaza.com", address: "9900 Westbrook Blvd", jobs: 12, balance: 4850, lastJob: "J-2038", insurer: "Travelers", adjuster: "Sara Kline" },
 { id: "C003", name: "David Nguyen", type: "Residential", phone: "713-555-4401", email: "dnguyen@gmail.com", address: "510 Birchwood Ct", jobs: 1, balance: 1200, lastJob: "J-2047", insurer: "Allstate", adjuster: "Mike Ross" },
 { id: "C004", name: "Ridgeline Industrial", type: "Commercial", phone: "713-555-5500", email: "facility@ridgeline.com", address: "3300 Gateway Dr", jobs: 7, balance: 0, lastJob: "J-2045", insurer: "CNA", adjuster: "Janet Ford" },
 { id: "C005", name: "Patricia Wells", type: "Residential", phone: "713-555-6601", email: "pwells@yahoo.com", address: "720 Oakview Dr", jobs: 2, balance: 650, lastJob: "J-2049", insurer: "USAA", adjuster: "Chris Layne" },
];

const isoDaysAgo = (n, hh="09:15") => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10)+"T"+hh; };
const SEED_JOBS = [
 { id: "J-2041", customer: "Linda Hargrove", type: "Water Mitigation", status: "Active", priority: "Standard", phase: "Drying – Day 3", tech: "Marcus Rivera", location: "4200 Maple Ln", started: "05/17/26", estimate: 8400, billed: 3200, paid: 3200, balance: 5200, xact: "XA-9920", divisions: ["Demo", "Dry-out", "Contents"], equipment: ["4x LGR Dehumidifiers", "6x Air Movers"], moisture: "18% avg", addOns: [], calls: [{id:"CL-1",datetime:isoDaysAgo(2,"09:15"),contact:"Linda Hargrove",outcome:"Reached",notes:"Confirmed access for Day 3 readings. Customer satisfied.",reminderDays:2}] },
 { id: "J-2038", customer: "Westbrook Plaza LLC", type: "Mold Remediation", status: "Active", priority: "Commercial", phase: "Containment Set", tech: "Kyle Hutchins", location: "9900 Westbrook Blvd", started: "05/14/26", estimate: 22000, billed: 11000, paid: 6150, balance: 15850, xact: "XA-9911", divisions: ["Containment", "Demo", "Remediation", "HEPA"], equipment: ["2x Negative Air", "8x Air Scrubbers"], moisture: "42% avg", addOns: [], calls: [{id:"CL-2",datetime:isoDaysAgo(4,"14:30"),contact:"Property Manager",outcome:"Voicemail",notes:"Left VM re: containment walkthrough scheduling.",reminderDays:1}] },
 { id: "J-2045", customer: "Ridgeline Industrial", type: "Fire & Smoke", status: "Active", priority: "Commercial", phase: "Pack-Out", tech: "DeShawn Carter", location: "3300 Gateway Dr", started: "05/18/26", estimate: 31500, billed: 5000, paid: 5000, balance: 26500, xact: "XA-9934", divisions: ["Pack-Out", "Cleaning", "Deodorize"], equipment: ["3x Ozone Generators", "4x Air Scrubbers"], moisture: "N/A", addOns: [], calls: [{id:"CL-4",datetime:isoDaysAgo(0,"08:45"),contact:"Site Foreman",outcome:"Reached",notes:"Pack-out inventory walkthrough scheduled for tomorrow AM.",reminderDays:1}] },
 { id: "J-2047", customer: "David Nguyen", type: "Water Mitigation", status: "Active", priority: "Emergency", phase: "Demo", tech: "James Okafor", location: "510 Birchwood Ct", started: "05/19/26", estimate: 6200, billed: 0, paid: 0, balance: 6200, xact: "XA-9940", divisions: ["Demo", "Dry-out"], equipment: ["2x LGR Dehumidifiers", "4x Air Movers"], moisture: "29% avg", addOns: [], calls: [{id:"CL-3",datetime:isoDaysAgo(3,"08:00"),contact:"David Nguyen",outcome:"Reached",notes:"Confirmed insurance adjuster visit. Awaiting Xactimate.",reminderDays:3}] },
 { id: "J-2049", customer: "Patricia Wells", type: "Water Mitigation", status: "Pending", priority: "Emergency", phase: "Extraction", tech: "Rodrigo Vega", location: "720 Oakview Dr", started: "05/20/26", estimate: 5100, billed: 0, paid: 0, balance: 5100, xact: "Pending", divisions: ["Extract", "Demo", "Dry-out"], equipment: ["1x Truck Mount", "3x Air Movers"], moisture: "Pending", addOns: [], calls: [{id:"CL-5",datetime:isoDaysAgo(1,"11:20"),contact:"Patricia Wells",outcome:"No Answer",notes:"Need to confirm extraction completion + start drying phase.",reminderDays:5}] },
];

const INVENTORY = [
 { id: "INV-001", item: "LGR Dehumidifier – 90pt", category: "Drying", total: 28, deployed: 14, available: 14, reorder: 8, unit: "unit", cost: 1850, deployments: [
  {jobId:"J-2041", customer:"Linda Hargrove", address:"4200 Maple Ln", qty:4, deployedAt:"05/17/26", tech:"Marcus Rivera"},
  {jobId:"J-2047", customer:"David Nguyen", address:"510 Birchwood Ct", qty:2, deployedAt:"05/19/26", tech:"James Okafor"},
  {jobId:"J-2038", customer:"Westbrook Plaza LLC", address:"9900 Westbrook Blvd", qty:8, deployedAt:"05/14/26", tech:"Kyle Hutchins"} ] },
 { id: "INV-002", item: "Air Mover – Low Profile", category: "Drying", total: 60, deployed: 22, available: 38, reorder: 15, unit: "unit", cost: 280, deployments: [
  {jobId:"J-2041", customer:"Linda Hargrove", address:"4200 Maple Ln", qty:6, deployedAt:"05/17/26", tech:"Marcus Rivera"},
  {jobId:"J-2047", customer:"David Nguyen", address:"510 Birchwood Ct", qty:4, deployedAt:"05/19/26", tech:"James Okafor"},
  {jobId:"J-2049", customer:"Patricia Wells", address:"720 Oakview Dr", qty:3, deployedAt:"05/20/26", tech:"Rodrigo Vega"},
  {jobId:"J-2045", customer:"Ridgeline Industrial", address:"3300 Gateway Dr", qty:9, deployedAt:"05/18/26", tech:"DeShawn Carter"} ] },
 { id: "INV-003", item: "Air Scrubber – 500 CFM", category: "Air Quality", total: 20, deployed: 12, available: 8, reorder: 5, unit: "unit", cost: 920, deployments: [
  {jobId:"J-2038", customer:"Westbrook Plaza LLC", address:"9900 Westbrook Blvd", qty:8, deployedAt:"05/14/26", tech:"Kyle Hutchins"},
  {jobId:"J-2045", customer:"Ridgeline Industrial", address:"3300 Gateway Dr", qty:4, deployedAt:"05/18/26", tech:"DeShawn Carter"} ] },
 { id: "INV-004", item: "Negative Air Machine", category: "Air Quality", total: 8, deployed: 4, available: 4, reorder: 2, unit: "unit", cost: 1400, deployments: [
  {jobId:"J-2038", customer:"Westbrook Plaza LLC", address:"9900 Westbrook Blvd", qty:2, deployedAt:"05/14/26", tech:"Kyle Hutchins"},
  {jobId:"J-2045", customer:"Ridgeline Industrial", address:"3300 Gateway Dr", qty:2, deployedAt:"05/18/26", tech:"DeShawn Carter"} ] },
 { id: "INV-005", item: "HEPA Filter (replacement)", category: "Consumable", total: 150, deployed: 0, available: 150, reorder: 30, unit: "ea", cost: 45, deployments: [] },
 { id: "INV-006", item: "Moisture Meter – Pin", category: "Equipment", total: 15, deployed: 8, available: 7, reorder: 4, unit: "unit", cost: 120, deployments: [
  {jobId:"J-2041", customer:"Linda Hargrove", address:"4200 Maple Ln", qty:2, deployedAt:"05/17/26", tech:"Marcus Rivera"},
  {jobId:"J-2038", customer:"Westbrook Plaza LLC", address:"9900 Westbrook Blvd", qty:3, deployedAt:"05/14/26", tech:"Kyle Hutchins"},
  {jobId:"J-2047", customer:"David Nguyen", address:"510 Birchwood Ct", qty:2, deployedAt:"05/19/26", tech:"James Okafor"},
  {jobId:"J-2049", customer:"Patricia Wells", address:"720 Oakview Dr", qty:1, deployedAt:"05/20/26", tech:"Rodrigo Vega"} ] },
 { id: "INV-007", item: "Containment Poly (roll)", category: "Consumable", total: 40, deployed: 0, available: 40, reorder: 10, unit: "roll", cost: 68, deployments: [] },
 { id: "INV-008", item: "Ozone Generator", category: "Specialty", total: 6, deployed: 3, available: 3, reorder: 2, unit: "unit", cost: 2200, deployments: [
  {jobId:"J-2045", customer:"Ridgeline Industrial", address:"3300 Gateway Dr", qty:3, deployedAt:"05/18/26", tech:"DeShawn Carter"} ] },
 { id: "INV-009", item: "Truck Mount Extractor", category: "Extraction", total: 4, deployed: 1, available: 3, reorder: 1, unit: "unit", cost: 14000, deployments: [
  {jobId:"J-2049", customer:"Patricia Wells", address:"720 Oakview Dr", qty:1, deployedAt:"05/20/26", tech:"Rodrigo Vega"} ] },
];

const QB_INVOICES = [
 { id: "INV-4401", customer: "Westbrook Plaza LLC", amount: 22000, paid: 6150, due: 15850, status: "Partial", qbSynced: true },
 { id: "INV-4398", customer: "Linda Hargrove", amount: 8400, paid: 3200, due: 5200, status: "Partial", qbSynced: true },
 { id: "INV-4402", customer: "Ridgeline Industrial", amount: 31500, paid: 5000, due: 26500, status: "Partial", qbSynced: true },
 { id: "INV-4403", customer: "David Nguyen", amount: 6200, paid: 0, due: 6200, status: "Unpaid", qbSynced: false },
 { id: "INV-4404", customer: "Patricia Wells", amount: 5100, paid: 0, due: 5100, status: "Draft", qbSynced: false },
];

const GPS_LOG = [
 { time: "08:02", tech: "Marcus Rivera", event: "Departed HQ", location: "4821 Commerce Blvd", miles: 0 },
 { time: "08:34", tech: "Marcus Rivera", event: "Arrived On-Site", location: "4200 Maple Ln (J-2041)", miles: 12.4 },
 { time: "08:11", tech: "Kyle Hutchins", event: "Departed HQ", location: "4821 Commerce Blvd", miles: 0 },
 { time: "08:48", tech: "Kyle Hutchins", event: "Arrived On-Site", location: "9900 Westbrook Blvd (J-2038)", miles: 18.7 },
 { time: "09:15", tech: "DeShawn Carter", event: "En Route", location: "I-45 N → 3300 Gateway Dr", miles: 9.2 },
 { time: "09:42", tech: "DeShawn Carter", event: "Arrived On-Site", location: "3300 Gateway Dr (J-2045)", miles: 14.9 },
 { time: "10:05", tech: "James Okafor", event: "Departed HQ", location: "4821 Commerce Blvd", miles: 0 },
 { time: "10:48", tech: "James Okafor", event: "Arrived On-Site", location: "510 Birchwood Ct (J-2047)", miles: 22.1 },
 { time: "11:20", tech: "Rodrigo Vega", event: "En Route", location: "Hwy 290 W → 720 Oakview Dr", miles: 16.8 },
 { time: "12:05", tech: "Maria Santos", event: "Break Started", location: "1150 Industrial Way", miles: 0 },
];

const ALERTS = [
 { id:"AL-001", severity:"High", time:"09:14", category:"Inventory", message:"Air Scrubber – 500 CFM stock below reorder point (8 < 5)", job:"—" },
 { id:"AL-002", severity:"High", time:"08:55", category:"Job", message:"Emergency job J-2047 unassigned for >2 hours", job:"J-2047" },
 { id:"AL-003", severity:"Medium", time:"10:22", category:"Callback", message:"Overdue customer callback for Westbrook Plaza LLC (3 days late)", job:"J-2038" },
 { id:"AL-004", severity:"Medium", time:"11:01", category:"QuickBooks", message:"Invoice INV-4403 not synced to QuickBooks", job:"J-2047" },
 { id:"AL-005", severity:"Low", time:"12:30", category:"GPS", message:"Tech Maria Santos on extended break (>45 min)", job:"—" },
 { id:"AL-006", severity:"Low", time:"13:18", category:"Xactimate", message:"Estimate XA-9940 awaiting adjuster approval", job:"J-2047" },
];

const css = `
 * { box-sizing: border-box; margin: 0; padding: 0; }
 body { background: #c0c5cb; }
 :root {
 --bg: #c0c5cb; --bg2: #d8dde2; --bg3: #e8ecf0;
 --border: #a8aeb4; --border2: #8e949a;
 --cyan: #C0392B; --cyan2: #922B21;
 --amber: #1d4ed8; --green: #b30000; --red: #C0392B; --purple: #cc0000;
 --text: #111111; --text2: #2c2c2c; --text3: #5c6166;
 --rajdhani: 'Rajdhani', sans-serif;
 --mono: 'JetBrains Mono', monospace;
 --dm: 'DM Sans', sans-serif;
 }
 .app { display: flex; height: 100vh; background: var(--bg); color: var(--text); font-family: var(--dm); overflow: hidden; }
 .sidebar { width: 220px; min-width: 220px; background: var(--bg2); border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow-y: auto; }
 .sidebar-logo { padding: 0; border-bottom: 1px solid var(--border); background: #000; line-height: 0; height: 200px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
 .sidebar-logo-img { display: block; width: 180%; height: auto; max-width: none; }
 .sidebar-logo .brand { font-family: var(--rajdhani); font-size: 13px; font-weight: 700; color: var(--cyan); letter-spacing: 2px; text-transform: uppercase; }
 .sidebar-logo .sub { font-size: 10px; color: var(--text3); letter-spacing: 1.5px; text-transform: uppercase; margin-top: 2px; }
 .loc-select { margin: 10px 12px; padding: 6px 10px; background: var(--bg3); border: 1px solid var(--border); border-radius: 4px; color: var(--text2); font-size: 11px; font-family: var(--dm); width: calc(100% - 24px); cursor: pointer; }
 .nav-section { padding: 8px 12px 4px; font-size: 9px; letter-spacing: 2px; color: var(--text3); text-transform: uppercase; font-weight: 600; }
 .nav-item { display: flex; align-items: center; gap: 10px; padding: 9px 18px; cursor: pointer; font-size: 13px; color: var(--text2); transition: all .15s; border-left: 2px solid transparent; }
 .nav-item:hover { background: rgba(204,0,0,.06); color: var(--text); }
 .nav-item.active { background: rgba(204,0,0,.1); color: var(--cyan); border-left-color: var(--cyan); }
 .nav-item .badge { margin-left: auto; background: var(--cyan); color: #fff; font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 10px; }
 .nav-item .badge.amber { background: var(--amber); color: #000; }
 .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
 .topbar { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; border-bottom: 1px solid var(--border); background: var(--bg2); gap: 16px; flex-shrink: 0; }
 .page-title { font-family: var(--rajdhani); font-size: 22px; font-weight: 700; color: var(--text); letter-spacing: 1px; }
 .topbar-right { display: flex; align-items: center; gap: 12px; }
 .status-dot { width: 8px; height: 8px; border-radius: 50%; background: #cc0000; box-shadow: 0 0 8px #cc0000; animation: pulse 2s infinite; }
 @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
 .live-tag { font-family: var(--mono); font-size: 10px; color: var(--green); letter-spacing: 1px; }
 .btn { padding: 7px 14px; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; border: none; font-family: var(--dm); transition: all .15s; }
 .btn-primary { background: #C0392B; color: #fff; }
 .btn-primary:hover { background: #922B21; }
 .btn-outline { background: transparent; color: var(--cyan); border: 1px solid var(--cyan); }
 .btn-outline:hover { background: rgba(204,0,0,.1); }
 .btn-amber { background: var(--amber); color: #000; }
 .btn-danger { background: var(--red); color: #fff; }
 .btn-ghost { background: var(--bg3); color: var(--text2); border: 1px solid var(--border); }
 .btn-ghost:hover { border-color: var(--border2); color: var(--text); }
 .content { flex: 1; overflow-y: auto; padding: 24px; }
 .kpi-row { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; margin-bottom: 24px; }
 .kpi { background: var(--bg2); border: 1px solid var(--border); border-radius: 8px; padding: 18px; position: relative; overflow: hidden; }
 .kpi::before { content:''; position: absolute; top:0; left:0; right:0; height: 2px; background: var(--accent, var(--cyan)); }
 .kpi-label { font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text3); margin-bottom: 8px; }
 .kpi-value { font-family: var(--rajdhani); font-size: 32px; font-weight: 700; color: var(--text); line-height: 1; }
 .kpi-value.cyan { color: var(--cyan); }
 .kpi-value.green { color: var(--green); }
 .kpi-value.amber { color: var(--amber); }
 .kpi-value.red { color: var(--red); }
 .kpi-sub { font-size: 11px; color: var(--text3); margin-top: 6px; }
 .kpi-trend { display: inline-flex; align-items: center; gap: 4px; font-family: var(--mono); font-size: 11px; font-weight: 600; margin-top: 8px; padding: 2px 6px; border-radius: 3px; }
 .kpi-trend.up { background: rgba(204,0,0,.10); color: #1d4ed8; border: 1px solid rgba(204,0,0,.3); }
 .kpi-trend.down { background: rgba(60,60,60,.10); color: #555; border: 1px solid #b0b6bc; }
 .kpi-trend.flat { background: rgba(60,60,60,.08); color: var(--text3); border: 1px solid #b0b6bc; }
 .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
 .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
 .card { background: var(--bg2); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
 .card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--border); }
 .card-title { font-family: var(--rajdhani); font-size: 15px; font-weight: 700; letter-spacing: .5px; color: var(--text); }
 .card-body { padding: 18px; }
 table { width: 100%; border-collapse: collapse; }
 thead th { padding: 8px 12px; text-align: left; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text3); border-bottom: 1px solid var(--border); font-weight: 600; }
 tbody td { padding: 10px 12px; font-size: 12px; color: var(--text2); border-bottom: 1px solid rgba(140,148,156,.5); vertical-align: middle; }
 tbody tr:hover td { background: rgba(204,0,0,.04); color: var(--text); }
 tbody tr:last-child td { border-bottom: none; }
 .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; }
 .badge-green { background: rgba(204,0,0,.15); color: #0a3a8a; border: 1px solid rgba(204,0,0,.35); }
 .badge-amber { background: rgba(180,0,0,.12); color: #1d4ed8; border: 1px solid rgba(180,0,0,.3); }
 .badge-red { background: rgba(255,0,0,.18); color: #aa0000; border: 1px solid rgba(255,0,0,.4); }
 .badge-cyan { background: rgba(204,0,0,.15); color: #aa0000; border: 1px solid rgba(204,0,0,.3); }
 .badge-purple { background: rgba(160,0,0,.15); color: #1d4ed8; border: 1px solid rgba(160,0,0,.3); }
 .badge-gray { background: rgba(60,60,60,.10); color: var(--text3); border: 1px solid #b0b6bc; }
 .tech-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
 .tech-card { background: var(--bg3); border: 1px solid var(--border); border-radius: 8px; padding: 16px; position: relative; }
 .tech-card.on-job { border-color: rgba(204,0,0,.5); }
 .tech-card.available { border-color: rgba(255,50,50,.4); }
 .tech-card.en-route { border-color: rgba(59,130,246,.4); }
 .tech-card.break { border-color: rgba(80,80,80,.5); }
 .tech-name { font-family: var(--rajdhani); font-size: 17px; font-weight: 700; }
 .tech-id { font-family: var(--mono); font-size: 10px; color: var(--text3); }
 .tech-row { display: flex; justify-content: space-between; font-size: 11px; color: var(--text2); margin-top: 6px; }
 .gps-map { background: #0d0808; border: 1px solid var(--border); border-radius: 8px; height: 320px; position: relative; overflow: hidden; }
 .gps-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(204,0,0,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(204,0,0,.06) 1px, transparent 1px); background-size: 40px 40px; }
 .gps-dot { position: absolute; width: 10px; height: 10px; border-radius: 50%; transform: translate(-50%, -50%); cursor: pointer; }
 .gps-dot::after { content:''; position: absolute; inset: -4px; border-radius: 50%; border: 1px solid currentColor; opacity: .4; animation: ripple 2s infinite; }
 @keyframes ripple { 0%{transform:scale(1);opacity:.4} 100%{transform:scale(2.5);opacity:0} }
 .gps-label { position: absolute; font-size: 9px; font-family: var(--mono); white-space: nowrap; transform: translate(-50%, 100%); margin-top: 4px; padding: 2px 5px; background: var(--bg2); border: 1px solid var(--border); border-radius: 2px; color: var(--text2); }
 .progress-bar { width: 100%; height: 6px; background: var(--bg3); border-radius: 3px; overflow: hidden; margin-top: 4px; }
 .progress-fill { height: 100%; border-radius: 3px; }
 .section-divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }
 .form-control { padding: 8px 10px; background: var(--bg); border: 1px solid var(--border); border-radius: 4px; color: var(--text); font-size: 12px; font-family: var(--dm); }
 .form-control:focus { outline: none; border-color: var(--cyan); }
 .form-control:disabled { opacity: .55; cursor: not-allowed; }
 .report-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 20px; background: var(--bg3); border: 1px solid var(--border); border-radius: 8px; cursor: pointer; text-align: center; transition: all .2s; min-height: 100px; }
 .report-btn:hover { border-color: var(--cyan); background: rgba(204,0,0,.06); }
 .report-btn .icon { font-size: 24px; }
 .report-btn .label { font-size: 12px; font-weight: 600; color: var(--text2); }
 .report-btn .sub { font-size: 10px; color: var(--text3); }
 .integration-card { background: var(--bg3); border: 1px solid var(--border); border-radius: 8px; padding: 20px; }
 .int-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
 .int-logo { width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; }
 .int-name { font-family: var(--rajdhani); font-size: 18px; font-weight: 700; }
 .int-status { font-size: 11px; color: var(--green); }
 .stat-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid var(--border); font-size: 12px; }
 .stat-row:last-child { border-bottom: none; }
 .tab-row { display: flex; gap: 2px; margin-bottom: 20px; background: var(--bg2); border-radius: 6px; padding: 4px; border: 1px solid var(--border); width: fit-content; }
 .tab { padding: 6px 14px; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; color: var(--text3); transition: all .15s; }
 .tab.active { background: var(--bg3); color: var(--cyan); }
 .mono { font-family: var(--mono); }
 .text-right { text-align: right; }
 .text-cyan { color: var(--cyan); }
 .text-green { color: var(--green); }
 .text-amber { color: var(--amber); }
 .text-red { color: var(--red); }
 .text-sm { font-size: 11px; }
 .gap8 { gap: 8px; }
 .flex { display: flex; align-items: center; }
 .mb20 { margin-bottom: 20px; }
 .job-detail { background: var(--bg3); border: 1px solid var(--border); border-radius: 8px; padding: 16px; margin-top: 12px; }
 .detail-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
 .detail-item .key { font-size: 10px; color: var(--text3); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; }
 .detail-item .val { color: var(--text); font-weight: 500; font-size: 12px; }
 .jobnum-banner { display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: rgba(204,0,0,.06); border: 1px solid rgba(204,0,0,.3); border-radius: 6px; margin-bottom: 14px; }
 .jobnum-banner .lbl { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--text3); }
 .jobnum-input { background: var(--bg); border: 1px solid var(--cyan); border-radius: 4px; color: var(--cyan); font-family: var(--rajdhani); font-size: 22px; font-weight: 700; letter-spacing: 1px; padding: 6px 12px; width: 200px; }
 .jobnum-input:focus { outline: none; box-shadow: 0 0 0 2px rgba(204,0,0,.25); }
 .jobnum-input:disabled { border-color: var(--border2); color: var(--text2); background: var(--bg3); cursor: not-allowed; }
 .lock-tag { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 10px; color: var(--text3); letter-spacing: 1px; }
 .addon-row { display: grid; grid-template-columns: 110px 1fr 110px 1.5fr auto; gap: 8px; align-items: center; padding: 10px; background: var(--bg); border: 1px solid var(--border); border-radius: 6px; margin-bottom: 8px; }
 .addon-row .type-badge { font-family: var(--rajdhani); font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 4px 8px; border-radius: 3px; text-align: center; }
 .addon-row .type-tech { background: rgba(204,0,0,.18); color: #60a5fa; }
 .addon-row .type-service { background: rgba(59,130,246,.18); color: #1d4ed8; }
 .addon-row .type-lineitem { background: rgba(160,0,0,.18); color: #2563eb; }
 .addon-row .type-note { background: rgba(80,80,80,.18); color: #aaa; }
 .addon-actions { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
 .icon-btn { background: transparent; border: 1px solid var(--border); color: var(--text3); border-radius: 4px; padding: 5px 8px; cursor: pointer; font-size: 14px; line-height: 1; }
 .icon-btn:hover { border-color: var(--red); color: var(--red); }
 .call-row { background: var(--bg); border: 1px solid var(--border); border-radius: 6px; padding: 10px; margin-bottom: 8px; }
 .call-meta { display: grid; grid-template-columns: 180px 1.4fr 1fr 1fr auto; gap: 8px; align-items: center; }
 .call-row textarea, .call-row .notes-input { width: 100%; margin-top: 8px; min-height: 32px; }
 .reminder-badge { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; padding: 4px 10px; font-family: var(--mono); font-size: 10px; border-radius: 4px; background: rgba(59,130,246,.12); border: 1px solid rgba(59,130,246,.4); color: #60a5fa; letter-spacing: 1px; }
 .reminder-badge.overdue { background: rgba(204,0,0,.15); border-color: rgba(204,0,0,.5); color: #ff3333; }
 .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.7); backdrop-filter: blur(4px); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; }
 .modal { background: var(--bg2); border: 1px solid var(--border); border-radius: 8px; width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.6); }
 .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid var(--border); background: var(--bg3); }
 .form-group { margin-bottom: 14px; }
 .form-lbl { font-size: 10px; letter-spacing: 1.5px; color: var(--text3); text-transform: uppercase; margin-bottom: 4px; font-weight: 600; }
 .row-clickable:hover td { background: rgba(204,0,0,.08); }
 .row-clickable:hover td:first-child { box-shadow: inset 2px 0 0 var(--cyan); }
 .search-input { padding: 8px 12px; background: var(--bg3); border: 1px solid var(--border); border-radius: 4px; color: var(--text); font-size: 12px; font-family: var(--dm); width: 280px; }
 .search-input:focus { outline: none; border-color: var(--cyan); box-shadow: 0 0 0 2px rgba(204,0,0,.2); }
 ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: var(--bg); } ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fullstack-preview-25/artifacts/xh4haevj_APEX%20Business%20Intelligence%20logo.PNG";

const computeJobTotal = (j) => (j.estimate || 0) + ((j.addOns || []).reduce((s, a) => s + (parseFloat(a.cost) || 0), 0));
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0,10); };
const isOverdue = (dateStr) => dateStr && new Date(dateStr) < new Date(new Date().toISOString().slice(0,10));

// ─── EXPORT HELPERS ───────────────────────────────────────────────────────────
const csvEscape = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
const toCSV = (cols, rows) => [cols.map(csvEscape).join(","), ...rows.map(r=>r.map(csvEscape).join(","))].join("\n");
const downloadBlob = (filename, content, mime="text/csv;charset=utf-8") => {
 const blob = new Blob([content], {type: mime});
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click();
 setTimeout(()=>{ URL.revokeObjectURL(url); a.remove(); }, 100);
};
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
const todayISO = () => new Date().toISOString().slice(0,10);
const exportCSV = (label, columns, rows) => downloadBlob(`${slugify(label)}-${todayISO()}.csv`, toCSV(columns, rows));
const exportPDF = (label, columns, rows) => {
 const html = `<!doctype html><html><head><meta charset="utf-8"><title>${label}</title>
  <style>body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;margin:24px;color:#111}
  h1{font-size:18px;margin:0 0 4px}.meta{color:#666;font-size:11px;margin-bottom:16px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{text-align:left;background:#f3f4f6;padding:6px 8px;border-bottom:2px solid #111;text-transform:uppercase;font-size:9px;letter-spacing:.6px}
  td{padding:6px 8px;border-bottom:1px solid #e5e7eb}tr:nth-child(even) td{background:#fafafa}
  .footer{margin-top:18px;font-size:9px;color:#999}</style></head>
  <body><h1>${label}</h1><div class="meta">Apex Business Intelligence · Generated ${new Date().toLocaleString()} · ${rows.length} rows</div>
  <table><thead><tr>${columns.map(c=>`<th>${c}</th>`).join("")}</tr></thead>
  <tbody>${rows.map(row=>`<tr>${row.map(c=>`<td>${typeof c==="number"&&c>1000?c.toLocaleString():c}</td>`).join("")}</tr>`).join("")}</tbody></table>
  <div class="footer">Apex Business Intelligence — Confidential</div>
  <script>window.onload=()=>setTimeout(()=>window.print(),300);</script></body></html>`;
 const w = window.open("", "_blank"); if(!w){ alert("Pop-up blocked. Allow pop-ups to print."); return; }
 w.document.open(); w.document.write(html); w.document.close();
};
const exportEmail = (label, columns, rows) => {
 const summary = `${label}\nGenerated: ${new Date().toLocaleString()}\nRows: ${rows.length}\n\n${columns.join(" | ")}\n${"-".repeat(60)}\n${rows.slice(0,12).map(row=>row.join(" | ")).join("\n")}${rows.length>12?`\n... and ${rows.length-12} more rows`:""}\n\n— Apex Business Intelligence`;
 window.location.href = `mailto:?subject=${encodeURIComponent(`[Apex] ${label} — ${todayISO()}`)}&body=${encodeURIComponent(summary)}`;
};

function StatusBadge({ status }) {
 const map = { "On Job":"cyan","En Route":"amber","Available":"green","Break":"gray","Active":"cyan","Completed":"green","Pending":"amber","Cancelled":"red","Delivering":"amber","In Transit":"amber","Partial":"amber","Unpaid":"red","Draft":"gray","Paid":"green","Connected":"green" };
 return <span className={`badge badge-${map[status]||"gray"}`}>{status}</span>;
}

function Trend({ value }) {
 const pct = parseFloat(value);
 if (isNaN(pct) || pct === 0) return <span className="kpi-trend flat">— 0%</span>;
 const up = pct > 0;
 return <span className={`kpi-trend ${up?"up":"down"}`}>{up?"▲":"▼"} {Math.abs(pct).toFixed(1)}%</span>;
}

function KPI({ label, value, sub, color="cyan", accent, trend }) {
 return (
 <div className="kpi" style={{"--accent": accent||"var(--cyan)"}}>
 <div className="kpi-label">{label}</div>
 <div className={`kpi-value ${color}`}>{value}</div>
 {sub && <div className="kpi-sub">{sub}</div>}
 {trend !== undefined && <Trend value={trend}/>}
 </div>
 );
}

function GPSMap({ techs }) {
 const bounds = { minLat:29.70, maxLat:29.85, minLng:-95.52, maxLng:-95.30 };
 const colors = { "On Job":"#cc0000","En Route":"#2563eb","Available":"#ff3333","Break":"#555555" };
 return (
 <div className="gps-map">
 <div className="gps-grid"/>
 <div style={{position:"absolute",top:10,left:10,fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)"}}>LIVE GPS — {techs.length} UNITS TRACKED</div>
 {techs.map(t => {
 const x = ((t.lng-bounds.minLng)/(bounds.maxLng-bounds.minLng))*100;
 const y = (1-(t.lat-bounds.minLat)/(bounds.maxLat-bounds.minLat))*100;
 const col = colors[t.status];
 return (
 <div key={t.id} title={`${t.name} – ${t.status}`}>
 <div className="gps-dot" style={{left:`${x}%`,top:`${y}%`,background:col,color:col}}/>
 <div className="gps-label" style={{left:`${x}%`,top:`${y}%`,marginTop:12}}>{t.id}</div>
 </div>
 );
 })}
 <div style={{position:"absolute",bottom:10,right:10,display:"flex",gap:10,fontSize:10,fontFamily:"var(--mono)"}}>
 {Object.entries(colors).map(([s,c]) => <span key={s} style={{color:c}}>● {s}</span>)}
 </div>
 </div>
 );
}

// ─── EXECUTIVE DASHBOARD ─────────────────────────────────────────────────────
function ExecutiveDashboard({ jobs, setJobs, onOpenJob }) {
 const [revRange, setRevRange] = useState("month");
 const today = new Date().toISOString().slice(0,10);
 const updateCall = (jobId, callId, patch) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, calls:(j.calls||[]).map(c => c.id===callId ? {...c, ...patch} : c)} : j));
 const markDone = (jobId, callId) => updateCall(jobId, callId, {completed:true, completedAt: today});
 const snoozeCall = (jobId, callId, days) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, calls:(j.calls||[]).map(c => c.id===callId ? {...c, reminderDays:(c.reminderDays||0)+days, snoozeCount:(c.snoozeCount||0)+1} : c)} : j));
 const bulkAction = (targets, op) => setJobs(prev => prev.map(j => {
  const hits = targets.filter(t=>t.jobId===j.id); if(!hits.length) return j;
  const ids = new Set(hits.map(t=>t.id));
  return {...j, calls:(j.calls||[]).map(c => ids.has(c.id) ? op(c) : c)};
 }));
 const markAllDone = (list) => { if(!list.length || !window.confirm(`Mark ${list.length} callback${list.length>1?"s":""} as complete?`)) return; bulkAction(list, c=>({...c, completed:true, completedAt:today})); };
 const snoozeAll = (list, days) => { if(!list.length) return; bulkAction(list, c=>({...c, reminderDays:(c.reminderDays||0)+days, snoozeCount:(c.snoozeCount||0)+1})); };
 const reminders = jobs.flatMap(j => (j.calls||[]).filter(c=>c.reminderDays>0 && !c.completed).map(c => {
  const due = addDays(c.datetime, c.reminderDays);
  return {...c, jobId:j.id, customer:j.customer, address:j.location, tech:j.tech, due, overdue: due < today, dueToday: due === today};
 }));
 const dueToday = reminders.filter(r => r.dueToday);
 const overdue = reminders.filter(r => r.overdue);
 const upcoming = reminders.filter(r => r.due > today).sort((a,b)=>a.due.localeCompare(b.due)).slice(0,5);
 const totalCustomers = CUSTOMERS.length;
 const totalJobs = jobs.length;
 const newToday = jobs.filter(j=>j.started==="05/20/26").length;
 const wip = jobs.filter(j=>j.status==="Active").length;
 const completed = jobs.filter(j=>j.status==="Completed").length;
 const emergencyOpen = jobs.filter(j=>j.priority==="Emergency"&&j.status!=="Completed").length;
 const callbacks = dueToday.length + overdue.length;
 const pending = jobs.filter(j=>j.status==="Pending").length;
 const commercialOpen = jobs.filter(j=>{ const c=CUSTOMERS.find(x=>x.name===j.customer); return c?.type==="Commercial"&&j.status!=="Completed"; }).length;
 const totalRev = jobs.reduce((a,j)=>a+computeJobTotal(j),0);
 const revByRange = revRange==="week"?totalRev*0.23:revRange==="month"?totalRev:totalRev*12;
 return (
 <div>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:18}}>
 <div>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:24,fontWeight:700,letterSpacing:1,color:"var(--text)"}}>Executive Dashboard</div>
 <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>Live business intelligence across every integrated system.</div>
 </div>
 <div className="flex gap8">
 <div className="status-dot"/><span className="live-tag">LIVE STREAM</span>
 </div>
 </div>

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",marginBottom:10,fontWeight:600}}>// Volume & Pipeline</div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))"}}>
 <KPI label="Total Customers" value={totalCustomers} color="cyan" trend="12.4"/>
 <KPI label="Total Jobs" value={totalJobs} color="cyan" trend="8.2"/>
 <KPI label="New Jobs Today" value={newToday} color="amber" accent="var(--amber)" trend="22.0"/>
 <KPI label="WIP (Work In Progress)" value={wip} color="cyan" trend="3.6"/>
 <KPI label="Completed Jobs" value={completed} color="green" accent="var(--green)" trend="14.5"/>
 <KPI label="Emergency Jobs Open" value={emergencyOpen} color="red" accent="var(--red)" trend="-9.1"/>
 <KPI label="Callbacks" value={callbacks} color="amber" accent="var(--amber)" trend="-25.0"/>
 <KPI label="Pending Jobs" value={pending} color="amber" accent="var(--amber)" trend="5.0"/>
 <KPI label="Commercial Jobs Open" value={commercialOpen} color="cyan" trend="11.3"/>
 </div>

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",margin:"24px 0 10px",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
 <span>// Callback &amp; Daily Reminders</span>
 <div className="mono text-sm" style={{color:"var(--text3)"}}>As of {today}</div>
 </div>
 <div className="grid2" style={{marginBottom:8}}>
 <div className="card">
  <div className="card-header">
   <span className="card-title">📞 Due Today</span>
   <div style={{display:"flex",alignItems:"center",gap:8}}>
    {dueToday.length>0 && <>
     <select onChange={e=>{ const d=+e.target.value; if(d>0){ snoozeAll(dueToday,d); e.target.value=""; } }} defaultValue="" className="form-control" style={{fontSize:10,padding:"4px 8px",height:"auto",minWidth:130}} title="Push all due-today reminders forward N days">
      <option value="">💤 Snooze All…</option>
      <option value={1}>+1 day</option>
      <option value={3}>+3 days</option>
      <option value={5}>+5 days</option>
      <option value={7}>+7 days</option>
     </select>
     <button className="btn btn-outline" onClick={()=>markAllDone(dueToday)} style={{fontSize:10,padding:"4px 10px"}} title="Mark all due-today callbacks complete">✓ Mark All Done</button>
    </>}
    <span className="badge badge-amber">{dueToday.length} reminders</span>
   </div>
  </div>
  <table><thead><tr><th>Job #</th><th>Customer</th><th>Contact</th><th>Outcome</th><th>Lead Tech</th><th style={{textAlign:"right"}}>Actions</th></tr></thead>
   <tbody>
    {dueToday.length===0 && <tr><td colSpan={6} style={{textAlign:"center",padding:"18px",color:"var(--text3)",fontSize:11}}>No callbacks scheduled for today.</td></tr>}
    {dueToday.map(r=>(
     <tr key={r.id+r.jobId} className="row-clickable" onClick={()=>onOpenJob(r.jobId)} style={{cursor:"pointer"}}>
      <td className="mono text-cyan">{r.jobId}</td>
      <td style={{fontSize:12,fontWeight:500}}>{r.customer}</td>
      <td style={{fontSize:11}}>{r.contact}</td>
      <td><span className="badge badge-cyan" style={{fontSize:9}}>{r.outcome}</span></td>
      <td style={{fontSize:11}}>{r.tech.split(" ")[0]}</td>
      <td style={{textAlign:"right",whiteSpace:"nowrap"}} onClick={e=>e.stopPropagation()}>
       <button className="btn btn-ghost" onClick={()=>snoozeCall(r.jobId,r.id,1)} style={{fontSize:10,padding:"3px 7px",marginRight:4}}>💤 +1d</button>
       <button className="btn btn-outline" onClick={()=>markDone(r.jobId,r.id)} style={{fontSize:10,padding:"3px 8px"}}>✓ Done</button>
      </td>
     </tr>
    ))}
   </tbody>
  </table>
 </div>
 <div className="card">
  <div className="card-header">
   <span className="card-title">⚠️ Overdue Callbacks</span>
   <div style={{display:"flex",alignItems:"center",gap:8}}>
    {overdue.length>0 && <>
     <select onChange={e=>{ const d=+e.target.value; if(d>0){ snoozeAll(overdue,d); e.target.value=""; } }} defaultValue="" className="form-control" style={{fontSize:10,padding:"4px 8px",height:"auto",minWidth:130}} title="Push all overdue reminders forward N days">
      <option value="">💤 Snooze All…</option>
      <option value={1}>+1 day</option>
      <option value={3}>+3 days</option>
      <option value={5}>+5 days</option>
      <option value={7}>+7 days</option>
     </select>
     <button className="btn btn-outline" onClick={()=>markAllDone(overdue)} style={{fontSize:10,padding:"4px 10px"}} title="Mark all overdue callbacks complete">✓ Mark All Done</button>
    </>}
    <span className="badge badge-red">{overdue.length} overdue</span>
   </div>
  </div>
  <table><thead><tr><th>Job #</th><th>Customer</th><th>Due</th><th>Days Late</th><th>Outcome</th><th style={{textAlign:"right"}}>Actions</th></tr></thead>
   <tbody>
    {overdue.length===0 && <tr><td colSpan={6} style={{textAlign:"center",padding:"18px",color:"var(--text3)",fontSize:11}}>No overdue callbacks. Nice work.</td></tr>}
    {overdue.map(r=>{
     const lateDays = Math.floor((new Date(today) - new Date(r.due))/(1000*60*60*24));
     return (
      <tr key={r.id+r.jobId} className="row-clickable" onClick={()=>onOpenJob(r.jobId)} style={{cursor:"pointer"}}>
       <td className="mono text-cyan">{r.jobId}</td>
       <td style={{fontSize:12,fontWeight:500}}>{r.customer}</td>
       <td className="mono text-sm text-red">{r.due}</td>
       <td><span className="badge badge-red" style={{fontSize:9}}>{lateDays}d late</span></td>
       <td><span className="badge badge-amber" style={{fontSize:9}}>{r.outcome}</span></td>
       <td style={{textAlign:"right",whiteSpace:"nowrap"}} onClick={e=>e.stopPropagation()}>
        <button className="btn btn-ghost" onClick={()=>snoozeCall(r.jobId,r.id,3)} style={{fontSize:10,padding:"3px 7px",marginRight:4}}>💤 +3d</button>
        <button className="btn btn-outline" onClick={()=>markDone(r.jobId,r.id)} style={{fontSize:10,padding:"3px 8px"}}>✓ Done</button>
       </td>
      </tr>
     );
    })}
   </tbody>
  </table>
 </div>
 </div>
 {upcoming.length>0 && (
  <div className="card" style={{marginBottom:8}}>
   <div className="card-header">
    <span className="card-title">🗓️ Upcoming Callbacks (next 7 days)</span>
    <span className="badge badge-cyan">{upcoming.length}</span>
   </div>
   <table><thead><tr><th>Due Date</th><th>Job #</th><th>Customer</th><th>Address</th><th>Contact</th><th>Outcome</th></tr></thead>
    <tbody>{upcoming.map(r=>(
     <tr key={r.id+r.jobId+"-up"} className="row-clickable" onClick={()=>onOpenJob(r.jobId)} style={{cursor:"pointer"}}>
      <td className="mono text-sm" style={{color:"#60a5fa"}}>{r.due}</td>
      <td className="mono text-cyan">{r.jobId}</td>
      <td style={{fontSize:12,fontWeight:500}}>{r.customer}</td>
      <td style={{fontSize:11,color:"var(--text2)"}}>{r.address}</td>
      <td style={{fontSize:11}}>{r.contact}</td>
      <td><span className="badge badge-cyan" style={{fontSize:9}}>{r.outcome}</span></td>
     </tr>
    ))}</tbody>
   </table>
  </div>
 )}

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",margin:"24px 0 10px",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
 <span>// Revenue & Performance</span>
 <div className="tab-row" style={{marginBottom:0}}>
 {["week","month","year"].map(r=>(
 <div key={r} className={`tab ${revRange===r?"active":""}`} onClick={()=>setRevRange(r)}>{r==="week"?"This Week":r==="month"?"This Month":"This Year"}</div>
 ))}
 </div>
 </div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))"}}>
 <KPI label={`Total Revenue (${revRange})`} value={`$${(revByRange/1000).toFixed(1)}K`} color="amber" accent="var(--amber)" trend="18.7"/>
 <KPI label="Lead Conversion Rate" value="68%" color="cyan" trend="4.2"/>
 <KPI label="Intake → Booked Rate" value="82%" color="green" accent="var(--green)" trend="6.1"/>
 <KPI label="Emergency Resolved Rate" value="94%" color="green" accent="var(--green)" trend="2.3"/>
 <KPI label="Job Satisfaction Score" value="4.8 / 5" color="cyan" trend="1.5"/>
</div>

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",margin:"24px 0 10px",fontWeight:600}}>// Operational Throughput</div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))"}}>
 <KPI label="Avg Response Time" value="22 min" color="cyan" trend="-12.0"/>
 <KPI label="Avg Acceptance Time" value="38 min" color="cyan" trend="-7.5"/>
 <KPI label="Avg Completion Time" value="4.2 days" color="amber" accent="var(--amber)" trend="-3.1"/>
 <KPI label="Avg Jobs / Technician" value={(jobs.length/TECHNICIANS.length).toFixed(1)} color="green" accent="var(--green)" trend="8.0"/>
 </div>
 </div>
 );
}

// ─── ORIGINAL DASHBOARD (renamed to OperationsDashboard) ─────────────────────
function OperationsDashboard({ jobs }) {
 const totalRevenue = jobs.reduce((a,j)=>a+computeJobTotal(j),0);
 const totalBalance = jobs.reduce((a,j)=>a+j.balance,0);
 const activeJobs = jobs.filter(j=>j.status==="Active").length;
 const activeTechs = TECHNICIANS.filter(t=>t.status==="On Job"||t.status==="En Route").length;
 return (
 <div>
 <div className="kpi-row">
 <KPI label="Active Jobs" value={activeJobs} sub={`${jobs.length} total jobs`} color="cyan"/>
 <KPI label="Techs Deployed" value={`${activeTechs}/${TECHNICIANS.length}`} sub="GPS monitored" color="green" accent="var(--green)"/>
 <KPI label="Total Contract Value" value={`$${(totalRevenue/1000).toFixed(0)}K`} color="amber" accent="var(--amber)"/>
 <KPI label="Outstanding Balance" value={`$${(totalBalance/1000).toFixed(1)}K`} color="red" accent="var(--red)"/>
 <KPI label="QB Sync Status" value="4/5" sub="1 pending sync" color="cyan"/>
 </div>
 <div className="grid2" style={{marginBottom:20}}>
 <div className="card">
 <div className="card-header"><span className="card-title">Live Technician GPS</span><div className="flex gap8"><div className="status-dot"/><span className="live-tag">LIVE</span></div></div>
 <div style={{padding:14}}><GPSMap techs={TECHNICIANS}/></div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Active Jobs</span><span className="badge badge-cyan">{activeJobs} ACTIVE</span></div>
 <table><thead><tr><th>Job</th><th>Type</th><th>Tech</th><th>Phase</th><th>Est.</th></tr></thead>
 <tbody>{jobs.map(j=>(
 <tr key={j.id}><td className="mono text-cyan">{j.id}</td><td>{j.type}</td><td className="text-sm">{j.tech.split(" ")[0]}</td>
 <td><span className="badge badge-cyan" style={{fontSize:9}}>{j.phase}</span></td>
 <td className="mono text-right">${computeJobTotal(j).toLocaleString()}</td></tr>
 ))}</tbody></table>
 </div>
 </div>
 <div className="grid3">
 <div className="card">
 <div className="card-header"><span className="card-title">Inventory Alerts</span></div>
 <div className="card-body">
 {INVENTORY.filter(i=>i.available<=i.reorder).map(i=>(
 <div key={i.id} className="stat-row"><span style={{fontSize:12}}>{i.item.substring(0,22)}…</span><span className="badge badge-amber">{i.available} left</span></div>
 ))}
 {INVENTORY.filter(i=>i.available<=i.reorder).length===0&&<div style={{fontSize:12,color:"var(--text3)",textAlign:"center",padding:16}}>All levels OK</div>}
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Integration Status</span></div>
 <div className="card-body">
 {[{name:"QuickBooks Online",status:"Connected",color:"green"},{name:"Xactimate / Xactanalysis",status:"Connected",color:"green"},{name:"Apex Field Pro IQ",status:"Syncing",color:"amber"},{name:"GPS Dispatch",status:"Live",color:"green"}].map(i=>(
 <div key={i.name} className="stat-row"><span style={{fontSize:12}}>{i.name}</span><span style={{fontSize:11,color:`var(--${i.color})`}}>● {i.status}</span></div>
 ))}
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Revenue Summary</span></div>
 <div className="card-body">
 {[["Contracted",`$${totalRevenue.toLocaleString()}`,"cyan"],["Billed",`$${jobs.reduce((a,j)=>a+j.billed,0).toLocaleString()}`,"amber"],["Collected",`$${jobs.reduce((a,j)=>a+j.paid,0).toLocaleString()}`,"green"],["Outstanding",`$${totalBalance.toLocaleString()}`,"red"]].map(([l,v,c])=>(
 <div key={l} className="stat-row"><span style={{fontSize:12}}>{l}</span><span className={`mono text-${c}`}>{v}</span></div>
 ))}
 </div>
 </div>
 </div>
 </div>
 );
}

// ─── ANALYTICS ────────────────────────────────────────────────────────────────
function Analytics({ jobs }) {
 const [range, setRange] = useState("month");
 const [customStart, setCustomStart] = useState("");
 const [customEnd, setCustomEnd] = useState("");
 // R13d — full date filter spec (exact order)
 const ranges = [
  ["week", "This Week"],
  ["month", "This Month"],
  ["this_quarter", "This Quarter"],
  ["q1", "Quarter 1"],
  ["q2", "Quarter 2"],
  ["q3", "Quarter 3"],
  ["q4", "Quarter 4"],
  ["year", "This Year"],
  ["custom", "Custom Dates"],
 ];
 const factor = range==="week"?0.23:range==="month"?1:range==="this_quarter"||range==="q1"||range==="q2"||range==="q3"||range==="q4"?3.1:range==="year"?11.8:range==="custom"&&customStart&&customEnd?Math.max(0.1,(new Date(customEnd)-new Date(customStart))/(30*86400000)):1;

 const revenueTrend = [
 {label:"W1",revenue:18400*factor,jobs:3},
 {label:"W2",revenue:22100*factor,jobs:4},
 {label:"W3",revenue:31200*factor,jobs:5},
 {label:"W4",revenue:27800*factor,jobs:4},
 {label:"W5",revenue:36500*factor,jobs:6},
 {label:"W6",revenue:42100*factor,jobs:7},
 {label:"W7",revenue:38900*factor,jobs:6},
 {label:"W8",revenue:45200*factor,jobs:8},
 ];
 const jobVolume = [
 {month:"Jan",jobs:42},{month:"Feb",jobs:51},{month:"Mar",jobs:61},{month:"Apr",jobs:55},
 {month:"May",jobs:68},{month:"Jun",jobs:74},{month:"Jul",jobs:81},{month:"Aug",jobs:69},
 ];
 const techPerf = TECHNICIANS.map(t=>({name:t.name.split(" ")[0],hours:t.hours,rating:t.rating*10}));
 const jobTypes = (() => {
 const m={};
 jobs.forEach(j=>{ m[j.type]=(m[j.type]||0)+1; });
 return Object.entries(m).map(([name,value])=>({name,value}));
 })();
 const revByLocation = LOCATIONS.filter(l=>l.active).map((l,i)=>({location:l.name.split("–")[0].split("Zone")[0].trim(),revenue:[28400,19800,34100,22600][i]||15000}));
 const wowDelta = [["Revenue","$36.5K","$42.1K","+15.3"],["New Jobs","4","6","+50.0"],["Avg Response","26m","22m","-15.4"],["Satisfaction","4.7","4.8","+2.1"]];
 const momDelta = [["Revenue","$148K","$172K","+16.2"],["New Jobs","51","61","+19.6"],["Completion Rate","87%","91%","+4.6"],["Utilization","68%","74%","+8.8"]];
 const pieColors = ["#cc0000","#3b82f6","#ffffff","#ff3333","#1d4ed8"];
 const tooltipStyle = {background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:6,fontSize:12,color:"#2c2c2c"};

 return (
 <div>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:18}}>
 <div>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:24,fontWeight:700,letterSpacing:1}}>Analytics</div>
 <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>Interactive performance metrics & trend analysis.</div>
 </div>
 <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
 <select data-testid="analytics-date-range" value={range} onChange={e=>setRange(e.target.value)} style={{padding:"6px 10px",fontSize:12,border:"1px solid var(--border)",borderRadius:4,background:"var(--bg2)",color:"var(--text2)",fontWeight:600,minWidth:170}}>
  {ranges.map(([k,l])=>(<option key={k} value={k}>{l}</option>))}
 </select>
 {range==="custom" && (
  <>
   <input data-testid="analytics-custom-start" type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} style={{padding:"6px 10px",fontSize:12,border:"1px solid var(--border)",borderRadius:4}}/>
   <span style={{fontSize:11,color:"var(--text3)"}}>→</span>
   <input data-testid="analytics-custom-end" type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} style={{padding:"6px 10px",fontSize:12,border:"1px solid var(--border)",borderRadius:4}}/>
  </>
 )}
 </div>
 </div>

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",margin:"6px 0 10px",fontWeight:600}}>// Performance Metrics</div>
 <div className="grid2" style={{marginBottom:18}}>
 <div className="card">
 <div className="card-header"><span className="card-title">Revenue Trend</span><span className="badge badge-cyan">{(ranges.find(r=>r[0]===range)||["",""])[1].toUpperCase()}</span></div>
 <div style={{padding:14,height:280}}>
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={revenueTrend}>
 <CartesianGrid stroke="rgba(204,0,0,.08)" strokeDasharray="3 3"/>
 <XAxis dataKey="label" stroke="#666" tick={{fontSize:11}}/>
 <YAxis stroke="#666" tick={{fontSize:11}}/>
 <RTooltip contentStyle={tooltipStyle}/>
 <Line type="monotone" dataKey="revenue" stroke="#cc0000" strokeWidth={2.5} dot={{fill:"#ff3333",r:4}} activeDot={{r:6}}/>
 </LineChart>
 </ResponsiveContainer>
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Job Volume (Monthly)</span></div>
 <div style={{padding:14,height:280}}>
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={jobVolume}>
 <CartesianGrid stroke="rgba(204,0,0,.08)" strokeDasharray="3 3"/>
 <XAxis dataKey="month" stroke="#666" tick={{fontSize:11}}/>
 <YAxis stroke="#666" tick={{fontSize:11}}/>
 <RTooltip contentStyle={tooltipStyle}/>
 <Bar dataKey="jobs" fill="#cc0000" radius={[4,4,0,0]}/>
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>

 <div className="card mb20">
 <div className="card-header"><span className="card-title">Technician Performance Comparison</span></div>
 <div style={{padding:14,height:300}}>
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={techPerf}>
 <CartesianGrid stroke="rgba(204,0,0,.08)" strokeDasharray="3 3"/>
 <XAxis dataKey="name" stroke="#666" tick={{fontSize:11}}/>
 <YAxis stroke="#666" tick={{fontSize:11}}/>
 <RTooltip contentStyle={tooltipStyle}/>
 <Legend wrapperStyle={{fontSize:11}}/>
 <Bar dataKey="hours" name="Hours YTD" fill="#cc0000" radius={[4,4,0,0]}/>
 <Bar dataKey="rating" name="Rating (×10)" fill="#3b82f6" radius={[4,4,0,0]}/>
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>

 <div className="grid2" style={{marginBottom:18}}>
 <div className="card">
 <div className="card-header"><span className="card-title">Job Type Breakdown</span></div>
 <div style={{padding:14,height:280}}>
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie data={jobTypes} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
 {jobTypes.map((_,i)=><Cell key={i} fill={pieColors[i%pieColors.length]}/>)}
 </Pie>
 <RTooltip contentStyle={tooltipStyle}/>
 <Legend wrapperStyle={{fontSize:11}}/>
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Revenue by Location</span></div>
 <div style={{padding:14,height:280}}>
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={revByLocation} layout="vertical">
 <CartesianGrid stroke="rgba(204,0,0,.08)" strokeDasharray="3 3"/>
 <XAxis type="number" stroke="#666" tick={{fontSize:11}}/>
 <YAxis dataKey="location" type="category" stroke="#666" tick={{fontSize:10}} width={110}/>
 <RTooltip contentStyle={tooltipStyle}/>
 <Bar dataKey="revenue" fill="#cc0000" radius={[0,4,4,0]}/>
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>

 <div style={{fontSize:10,letterSpacing:2,color:"var(--text3)",textTransform:"uppercase",margin:"10px 0 10px",fontWeight:600}}>// Trend Analysis</div>
 <div className="grid2">
 <div className="card">
 <div className="card-header"><span className="card-title">Week over Week</span></div>
 <table><thead><tr><th>Metric</th><th>Prior</th><th>Current</th><th>Δ</th></tr></thead>
 <tbody>{wowDelta.map(([m,p,c,d])=>{
 const up=parseFloat(d)>=0;
 return <tr key={m}><td>{m}</td><td className="mono">{p}</td><td className="mono text-cyan">{c}</td><td><span className={`kpi-trend ${up?"up":"down"}`}>{up?"▲":"▼"} {Math.abs(parseFloat(d))}%</span></td></tr>;
 })}</tbody></table>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Month over Month</span></div>
 <table><thead><tr><th>Metric</th><th>Prior</th><th>Current</th><th>Δ</th></tr></thead>
 <tbody>{momDelta.map(([m,p,c,d])=>{
 const up=parseFloat(d)>=0;
 return <tr key={m}><td>{m}</td><td className="mono">{p}</td><td className="mono text-cyan">{c}</td><td><span className={`kpi-trend ${up?"up":"down"}`}>{up?"▲":"▼"} {Math.abs(parseFloat(d))}%</span></td></tr>;
 })}</tbody></table>
 </div>
 </div>
 </div>
 );
}

function Technicians() {
 const [selected, setSelected] = useState(null);
 const exportData = () => ({
  columns: ["ID","Name","Role","Status","Location","Current Job","Phone","Vehicle","Certifications","Hours YTD","Rating"],
  rows: TECHNICIANS.map(t=>[t.id,t.name,t.role,t.status,t.location,t.job,t.phone,t.vehicle,t.cert,t.hours,t.rating]),
 });
 return (
 <div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
 <KPI label="Total Techs" value={TECHNICIANS.length} sub="Up to 25 supported"/>
 <KPI label="On Job" value={TECHNICIANS.filter(t=>t.status==="On Job").length} color="cyan"/>
 <KPI label="En Route" value={TECHNICIANS.filter(t=>t.status==="En Route").length} color="amber" accent="var(--amber)"/>
 <KPI label="Available" value={TECHNICIANS.filter(t=>t.status==="Available").length} color="green" accent="var(--green)"/>
 </div>
 <div className="card mb20">
 <div className="card-header"><span className="card-title">Live GPS Tracking</span>
  <div style={{display:"flex",gap:6,alignItems:"center"}}>
   <div className="status-dot"/><span className="live-tag">LIVE</span>
   <span style={{margin:"0 6px",color:"var(--text3)",fontSize:11}}>·</span>
   <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportPDF("Technician Roster",columns,rows);}} data-testid="tech-export-pdf">PDF</button>
   <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportCSV("Technician Roster",columns,rows);}} data-testid="tech-export-csv">CSV</button>
   <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportEmail("Technician Roster",columns,rows);}} data-testid="tech-export-email">Email</button>
  </div>
 </div>
 <div style={{padding:14}}><GPSMap techs={TECHNICIANS}/></div>
 </div>
 <div className="tech-grid">
 {TECHNICIANS.map(t=>(
 <div key={t.id} className={`tech-card ${t.status.toLowerCase().replace(" ","-")}`} onClick={()=>setSelected(selected?.id===t.id?null:t)}>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
 <div><div className="tech-name">{t.name}</div><div className="tech-id">{t.id} · {t.role}</div></div>
 <StatusBadge status={t.status}/>
 </div>
 <div className="tech-row"><span>{t.location}</span></div>
 <div className="tech-row"><span>{t.job!=="—"?t.job:"Unassigned"}</span><span>★ {t.rating}</span></div>
 <div className="tech-row"><span>{t.vehicle}</span></div>
 {selected?.id===t.id&&(
 <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
 <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
 <div><div className="detail-item"><div className="key">Phone</div><div className="val mono">{t.phone}</div></div></div>
 <div><div className="detail-item"><div className="key">Hours YTD</div><div className="val mono">{t.hours}h</div></div></div>
 <div style={{gridColumn:"1/-1"}}><div className="detail-item"><div className="key">Certifications</div><div className="val">{t.cert}</div></div></div>
 </div>
 <div style={{display:"flex",gap:8}}>
 <button className="btn btn-outline" style={{flex:1,fontSize:11}}>Assign Job</button>
 <button className="btn btn-ghost" style={{flex:1,fontSize:11}}>Message</button>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 );
}

function Drivers() {
 return (
 <div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
 <KPI label="Total Drivers" value={DRIVERS.length}/>
 <KPI label="Active / In Transit" value={DRIVERS.filter(d=>d.status!=="Available").length} color="amber" accent="var(--amber)"/>
 <KPI label="Available" value={DRIVERS.filter(d=>d.status==="Available").length} color="green" accent="var(--green)"/>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Driver Roster & Dispatch</span><button className="btn btn-primary" style={{fontSize:11}}>+ Add Driver</button></div>
 <table><thead><tr><th>ID</th><th>Name</th><th>Vehicle</th><th>Status</th><th>Route</th><th>Miles</th><th>Load</th><th>Actions</th></tr></thead>
 <tbody>{DRIVERS.map(d=>(
 <tr key={d.id}>
 <td className="mono text-cyan">{d.id}</td><td style={{fontWeight:500}}>{d.name}</td>
 <td style={{fontSize:11}}>{d.vehicle}</td><td><StatusBadge status={d.status}/></td>
 <td style={{fontSize:11}}>{d.route}</td><td className="mono">{d.miles>0?`${d.miles} mi`:"—"}</td>
 <td style={{fontSize:11}}>{d.load}</td>
 <td><div style={{display:"flex",gap:6}}><button className="btn btn-outline" style={{padding:"4px 10px",fontSize:10}}>Dispatch</button><button className="btn btn-ghost" style={{padding:"4px 10px",fontSize:10}}>Track</button></div></td>
 </tr>
 ))}</tbody></table>
 </div>
 </div>
 );
}

function Customers() {
 const [sel, setSel] = useState(null);
 const [q, setQ] = useState("");
 // R13d — auto-focus a customer requested by another page (e.g. job detail link)
 useEffect(()=>{
  try {
   const focus = sessionStorage.getItem("r13_customer_focus");
   if (focus) {
    const c = CUSTOMERS.find(x => x.name === focus);
    if (c) { setSel(c); setQ(focus); }
    sessionStorage.removeItem("r13_customer_focus");
   }
  } catch {}
 }, []);
 const ql = q.trim().toLowerCase();
 const filtered = ql ? CUSTOMERS.filter(c => c.name.toLowerCase().includes(ql) || c.email.toLowerCase().includes(ql) || c.phone.includes(ql) || (c.address||"").toLowerCase().includes(ql) || (c.insurer||"").toLowerCase().includes(ql)) : CUSTOMERS;
 const exportData = () => ({
  columns: ["ID","Name","Type","Phone","Email","Address","Insurer","Adjuster","Jobs","Balance","Last Job"],
  rows: filtered.map(c=>[c.id,c.name,c.type,c.phone,c.email,c.address,c.insurer,c.adjuster,c.jobs,c.balance,c.lastJob]),
 });
 return (
 <div>
 <div className="flex gap8 mb20" style={{justifyContent:"space-between"}}>
 <div style={{display:"flex",gap:8,alignItems:"center"}}>
  <input className="search-input" placeholder="Search by name, email, phone, address, or insurer…" value={q} onChange={e=>setQ(e.target.value)}/>
  {q && <button className="btn btn-ghost" onClick={()=>setQ("")} style={{fontSize:11,padding:"5px 10px"}}>✕ Reset</button>}
 </div>
 <div style={{display:"flex",gap:6}}>
  <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportPDF("Customer Database",columns,rows);}} data-testid="cust-export-pdf">PDF</button>
  <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportCSV("Customer Database",columns,rows);}} data-testid="cust-export-csv">CSV</button>
  <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{const {columns,rows}=exportData();exportEmail("Customer Database",columns,rows);}} data-testid="cust-export-email">Email</button>
  <button className="btn btn-primary">+ New Customer</button>
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Customer Database</span><span className="badge badge-cyan">{filtered.length} of {CUSTOMERS.length} records</span></div>
 <table><thead><tr><th>ID</th><th>Name</th><th>Type</th><th>Phone</th><th>Insurer</th><th>Adjuster</th><th>Jobs</th><th>Balance</th><th></th></tr></thead>
 <tbody>{filtered.map(c=>(
 <React.Fragment key={c.id}>
 <tr onClick={()=>setSel(sel?.id===c.id?null:c)} style={{cursor:"pointer"}}>
 <td className="mono text-cyan">{c.id}</td><td style={{fontWeight:500}}>{c.name}</td>
 <td><span className={`badge badge-${c.type==="Commercial"?"purple":"cyan"}`}>{c.type}</span></td>
 <td className="mono text-sm">{c.phone}</td><td style={{fontSize:11}}>{c.insurer}</td>
 <td style={{fontSize:11}}>{c.adjuster}</td><td className="mono">{c.jobs}</td>
 <td className={`mono ${c.balance>0?"text-amber":"text-green"}`}>${c.balance.toLocaleString()}</td>
 <td><button className="btn btn-ghost" style={{padding:"3px 10px",fontSize:10}}>View</button></td>
 </tr>
 {sel?.id===c.id&&(
 <tr><td colSpan={9} style={{padding:0}}>
 <div className="job-detail">
 <div className="detail-grid">
 <div className="detail-item"><div className="key">Email</div><div className="val mono">{c.email}</div></div>
 <div className="detail-item"><div className="key">Address</div><div className="val">{c.address}</div></div>
 <div className="detail-item"><div className="key">Last Job</div><div className="val mono text-cyan">{c.lastJob}</div></div>
 </div>
 <div style={{display:"flex",gap:8,marginTop:12}}>
 <button className="btn btn-primary" style={{fontSize:11}}>Create Job</button>
 <button className="btn btn-outline" style={{fontSize:11}}>Send Invoice</button>
 <button className="btn btn-ghost" style={{fontSize:11}}>Job History</button>
 </div>
 </div>
 </td></tr>
 )}
 </React.Fragment>
 ))}</tbody></table>
 </div>
 </div>
 );
}

// ─── ADD-ON ROW ───────────────────────────────────────────────────────────────
function CallLogRow({ call, locked, onChange, onRemove }) {
 const reminderDate = call.reminderDays > 0 ? addDays(call.datetime, call.reminderDays) : null;
 const overdue = reminderDate && isOverdue(reminderDate);
 const completed = !!call.completed;
 const markComplete = () => onChange({...call, completed: !completed, completedAt: !completed ? new Date().toISOString().slice(0,10) : null});
 const snooze = (days) => onChange({...call, reminderDays: (call.reminderDays||0) + days, snoozeCount: (call.snoozeCount||0) + 1});
 return (
  <div className="call-row" style={completed?{opacity:.55,borderColor:"rgba(34,197,94,.4)"}:{}}>
   <div className="call-meta">
    <input className="form-control" type="datetime-local" disabled={locked||completed} value={(call.datetime||"").slice(0,16)} onChange={e=>onChange({...call,datetime:e.target.value})}/>
    <input className="form-control" disabled={locked||completed} value={call.contact} placeholder="Contact name" onChange={e=>onChange({...call,contact:e.target.value})}/>
    <select className="form-control" disabled={locked||completed} value={call.outcome} onChange={e=>onChange({...call,outcome:e.target.value})}>
     {["Reached","Voicemail","No Answer","Callback Requested","Scheduled","Escalated"].map(o=><option key={o} value={o}>{o}</option>)}
    </select>
    <select className="form-control" disabled={locked||completed} value={call.reminderDays} onChange={e=>onChange({...call,reminderDays:+e.target.value})}>
     <option value={0}>No reminder</option>
     {[1,2,3,4,5,6,7].map(n=><option key={n} value={n}>{n} day{n>1?"s":""}</option>)}
    </select>
    <button className="icon-btn" disabled={locked} onClick={onRemove} title="Remove">×</button>
   </div>
   <input className="form-control notes-input" disabled={locked||completed} value={call.notes} placeholder="Call notes / outcome details..." onChange={e=>onChange({...call,notes:e.target.value})}/>
   <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginTop:8,flexWrap:"wrap"}}>
    {completed
     ? <div className="reminder-badge" style={{background:"rgba(34,197,94,.12)",borderColor:"rgba(34,197,94,.4)",color:"#22c55e"}}>✓ Completed {call.completedAt?`on ${call.completedAt}`:""}</div>
     : reminderDate
       ? <div className={`reminder-badge ${overdue?"overdue":""}`}>⏰ Follow-up {overdue?"overdue":"due"}: {reminderDate}{call.snoozeCount>0?` · snoozed ${call.snoozeCount}×`:""}</div>
       : <div style={{flex:1}}/>}
    <div style={{display:"flex",gap:6}}>
     {!completed && reminderDate && <>
      <button className="btn btn-ghost" disabled={locked} onClick={()=>snooze(1)} title="Snooze +1 day" style={{fontSize:10,padding:"4px 8px"}}>💤 +1d</button>
      <button className="btn btn-ghost" disabled={locked} onClick={()=>snooze(3)} title="Snooze +3 days" style={{fontSize:10,padding:"4px 8px"}}>💤 +3d</button>
     </>}
     <button className="btn btn-outline" disabled={locked} onClick={markComplete} style={{fontSize:10,padding:"4px 10px",borderColor:completed?"rgba(34,197,94,.5)":"",color:completed?"#22c55e":""}}>{completed?"↺ Reopen":"✓ Mark Complete"}</button>
    </div>
   </div>
  </div>
 );
}

// ─── ADD-ON ROW ───────────────────────────────────────────────────────────────
function AddOnRow({ addon, locked, onChange, onRemove }) {
 const typeMap = {tech:"Technician",service:"Service",lineitem:"Line Item",note:"Note"};
 return (
 <div className="addon-row">
 <div className={`type-badge type-${addon.type}`}>{typeMap[addon.type]}</div>
 {addon.type==="tech" ? (
 <select className="form-control" disabled={locked} value={addon.label} onChange={e=>onChange({...addon,label:e.target.value})}>
 <option value="">— Select technician —</option>
 {TECHNICIANS.map(t=><option key={t.id} value={t.name}>{t.name} ({t.role})</option>)}
 </select>
 ) : (
 <input className="form-control" disabled={locked} value={addon.label} placeholder={addon.type==="note"?"Note":addon.type==="service"?"Service name":"Description"} onChange={e=>onChange({...addon,label:e.target.value})}/>
 )}
 <input className="form-control" disabled={locked||addon.type==="note"} type="number" value={addon.cost} placeholder="$ cost" onChange={e=>onChange({...addon,cost:e.target.value})}/>
 <input className="form-control" disabled={locked} value={addon.notes} placeholder="Notes" onChange={e=>onChange({...addon,notes:e.target.value})}/>
 <button className="icon-btn" disabled={locked} onClick={onRemove} title="Remove">×</button>
 </div>
 );
}

// ─── R13d — Inline NOTES section (replaces Add-Ons on the job detail page) ──
function JobNotesInline({ jobId, locked }) {
 const [notes, setNotes] = useState([]);
 const [subject, setSubject] = useState("");
 const [body, setBody] = useState("");
 const [tagged, setTagged] = useState("");
 const [busy, setBusy] = useState(false);
 const techs = TECHNICIANS;
 const load = React.useCallback(async () => {
  try {
   const r = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/iv/r12/jobs/${encodeURIComponent(jobId)}/notes`);
   if (!r.ok) { setNotes([]); return; }
   const d = await r.json();
   setNotes(d.notes || []);
  } catch { setNotes([]); }
 }, [jobId]);
 useEffect(() => { load(); }, [load]);
 const save = async () => {
  if (!subject.trim() && !body.trim()) { toast("Subject or body required", "err"); return; }
  setBusy(true);
  try {
   const r = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/iv/r12/jobs/notes`, {
    method: "POST",
    body: JSON.stringify({ job_id: jobId, subject, body, tagged_users: tagged ? [tagged] : [] }),
   });
   const d = await r.json();
   if (!r.ok) throw new Error(d.detail || "Save failed");
   toast("Note saved");
   setSubject(""); setBody(""); setTagged("");
   load();
  } catch (e) { toast(e.message || "Save failed", "err"); }
  finally { setBusy(false); }
 };
 const lblDark = { fontSize: 11, fontWeight: 600, color: "#2C2C2C", marginBottom: 4, display: "block", letterSpacing: 0.3 };
 const inDark = { width: "100%", padding: "7px 10px", border: "1px solid #c8c8c8", borderRadius: 4, fontSize: 12, color: "#2C2C2C", fontWeight: 500, background: "#fff" };
 return (
  <div data-testid={`job-notes-inline-${jobId}`}>
   <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:8}}>
    <div>
     <label style={lblDark}>Subject</label>
     <input data-testid={`note-subject-${jobId}`} style={inDark} value={subject} onChange={e=>setSubject(e.target.value)} disabled={locked||busy} placeholder="e.g. Customer requested ETA"/>
    </div>
    <div>
     <label style={lblDark}>Tag Technician</label>
     <select data-testid={`note-tag-tech-${jobId}`} style={inDark} value={tagged} onChange={e=>setTagged(e.target.value)} disabled={locked||busy}>
      <option value="">— No tag —</option>
      {techs.map(t=>(<option key={t.id} value={t.name}>{t.name} ({t.role})</option>))}
     </select>
    </div>
   </div>
   <label style={lblDark}>Body</label>
   <textarea data-testid={`note-body-${jobId}`} style={{...inDark, minHeight:70, fontFamily:"inherit"}} value={body} onChange={e=>setBody(e.target.value)} disabled={locked||busy} placeholder="Detailed note…"/>
   <div style={{marginTop:8,display:"flex",justifyContent:"flex-end"}}>
    <button data-testid={`note-save-${jobId}`} className="btn btn-primary" disabled={locked||busy} style={{fontSize:11}} onClick={save}>{busy?"Saving…":"Add Note"}</button>
   </div>
   <hr style={{border:"none",borderTop:"1px dashed #d8d8d8",margin:"12px 0"}}/>
   <div style={{fontSize:11,color:"#2C2C2C",fontWeight:600,marginBottom:6}}>Notes Timeline ({notes.length})</div>
   {notes.length === 0 && <div style={{fontSize:11,color:"#666",fontWeight:600,padding:"6px 0",textAlign:"center"}}>No notes yet. Use the form above.</div>}
   {notes.map(n => (
    <div key={n.id} data-testid={`note-item-${n.id}`} style={{background:"#fafafa",border:"1px solid #e0e0e0",borderRadius:6,padding:"10px 12px",marginBottom:8}}>
     <div style={{fontSize:13,fontWeight:700,color:"#2C2C2C"}}>{n.subject || <em style={{color:"#888"}}>(no subject)</em>}</div>
     {n.body && <div style={{fontSize:12,color:"#2C2C2C",fontWeight:500,marginTop:4,whiteSpace:"pre-wrap"}}>{n.body}</div>}
     <div style={{fontSize:10,color:"#555",fontWeight:600,marginTop:8,display:"flex",flexWrap:"wrap",gap:10}}>
      <span>📅 {(n.created_at||"").slice(0,16).replace("T"," ")}</span>
      <span>👤 By: <strong style={{color:"#2C2C2C"}}>{n.created_by_name||n.created_by_email}</strong></span>
      {(n.tagged_users||[]).length>0 && <span>🏷️ Tagged: <strong style={{color:"#1e40af"}}>{(n.tagged_users||[]).join(", ")}</strong></span>}
     </div>
    </div>
   ))}
  </div>
 );
}

function Jobs({ jobs, setJobs, selectedJobId, setSelectedJobId, user, locIdx, onOpenOverlay, onOpenRestoration }) {
 const [sel, setSel] = useState(selectedJobId || null);
 const [showNew, setShowNew] = useState(false);
 // R14 — job search (job #, customer name, phone, email, address, claim, tech)
 const [jobSearch, setJobSearch] = useState("");
 // Round 13 — job number buffered draft + status "Other" buffer + misc items panel
 const [jobNumDraft, setJobNumDraft] = useState({});  // { [jobId]: "J-9999" }
 const [statusOtherDraft, setStatusOtherDraft] = useState({}); // { [jobId]: "Custom status text" }
 const [showOtherStatus, setShowOtherStatus] = useState({});  // { [jobId]: bool }
 const matchesSearch = (j, q) => {
   if (!q) return true;
   const needle = q.toLowerCase().trim();
   const numeric = needle.replace(/[^0-9]/g, "");
   const haystack = [
     j.id, j.customer, j.customerPhone, j.customerEmail, j.location,
     j.propertyAddress, j.cityStateZip, j.claimNumber, j.tech,
   ].filter(Boolean).join(" ").toLowerCase();
   if (haystack.includes(needle)) return true;
   if (numeric && (j.id || "").toLowerCase().includes(numeric)) return true;
   return false;
 };
 const filteredBase = jobs.filter(j => matchesSearch(j, jobSearch));
 // R16 — Group sub-jobs under their master so they appear immediately below
 const filteredJobs = (() => {
  const result = [];
  const seen = new Set();
  const masters = filteredBase.filter(j => !j.parent_id);
  const orphans = filteredBase.filter(j => j.parent_id && !filteredBase.find(m => m.id === j.parent_id));
  for (const m of masters) {
   if (seen.has(m.id)) continue;
   result.push(m); seen.add(m.id);
   // Use the full jobs array to find children so children show even when only the master matched search
   const children = jobs.filter(c => c.parent_id === m.id);
   for (const c of children) { if (!seen.has(c.id)) { result.push(c); seen.add(c.id); } }
  }
  // Orphan sub-jobs that matched search but whose master didn't are appended at the end
  for (const o of orphans) { if (!seen.has(o.id)) { result.push(o); seen.add(o.id); } }
  return result;
 })();
 useEffect(()=>{ if(selectedJobId){ setSel(selectedJobId); setSelectedJobId&&setSelectedJobId(null); setTimeout(()=>{ const el=document.getElementById("job-"+selectedJobId); el&&el.scrollIntoView({behavior:"smooth",block:"center"}); },150); } },[selectedJobId, setSelectedJobId]);
 const createJob = (newJob) => { setJobs(prev => [newJob, ...prev]); setSel(newJob.id); };
 const SHARED = ["customer","customerPhone","customerEmail","location","propertyAddress","cityStateZip","insurer","claimNumber","policyNumber","adjuster","adjusterPhone","dateOfLoss","causeOfLoss","firstName","lastName"];
 const updateJob = (id, patch) => {
  setJobs(prev => prev.map(j => j.id===id ? {...j, ...patch} : j));
  // R16 — propagate shared fields to master + all siblings
  const sharedPatch = {};
  for (const k of SHARED) if (k in patch) sharedPatch[k] = patch[k];
  if (Object.keys(sharedPatch).length > 0) {
   propagateSharedField(id, sharedPatch).then(() => {
    // also update local sibling jobs (master + sub-jobs) so the UI reflects immediately
    setJobs(prev => {
     const target = prev.find(x => x.id === id);
     if (!target) return prev;
     const masterId = target.parent_id || target.id;
     return prev.map(j => (j.id === masterId || j.parent_id === masterId) ? {...j, ...sharedPatch} : j);
    });
   });
  }
 };
 const addAddon = (id, type) => {
 setJobs(prev => prev.map(j => j.id===id ? {...j, addOns:[...(j.addOns||[]),{id:`AO-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,type,label:"",cost:"",notes:""}]} : j));
 };
 const updateAddon = (jobId, addonId, next) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, addOns:j.addOns.map(a=>a.id===addonId?next:a)} : j));
 const removeAddon = (jobId, addonId) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, addOns:j.addOns.filter(a=>a.id!==addonId)} : j));
 const addCall = (id) => setJobs(prev => prev.map(j => j.id===id ? {...j, calls:[...(j.calls||[]), {id:`CL-${Date.now()}-${Math.random().toString(36).slice(2,5)}`, datetime:new Date().toISOString().slice(0,16), contact:j.customer, outcome:"Reached", notes:"", reminderDays:0}]} : j));
 const updateCall = (jobId, callId, next) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, calls:(j.calls||[]).map(c=>c.id===callId?next:c)} : j));
 const removeCall = (jobId, callId) => setJobs(prev => prev.map(j => j.id===jobId ? {...j, calls:(j.calls||[]).filter(c=>c.id!==callId)} : j));

 return (
 <div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(5,1fr)"}}>
 <KPI label="Active Jobs" value={jobs.filter(j=>j.status==="Active").length} color="cyan"/>
 <KPI label="Total Contracted" value={`$${jobs.reduce((a,j)=>a+computeJobTotal(j),0).toLocaleString()}`} color="amber" accent="var(--amber)"/>
 <KPI label="Total Billed" value={`$${jobs.reduce((a,j)=>a+j.billed,0).toLocaleString()}`} color="cyan"/>
 <KPI label="Collected" value={`$${jobs.reduce((a,j)=>a+j.paid,0).toLocaleString()}`} color="green" accent="var(--green)"/>
 <KPI label="Outstanding" value={`$${jobs.reduce((a,j)=>a+j.balance,0).toLocaleString()}`} color="red" accent="var(--red)"/>
 </div>
 <div className="flex gap8 mb20" style={{justifyContent:"space-between",alignItems:"center",flexWrap:"wrap"}}>
 <div className="tab-row">{["All Jobs","Water","Mold","Fire & Smoke"].map(t=><div key={t} className={`tab ${t==="All Jobs"?"active":""}`}>{t}</div>)}</div>
 <input data-testid="job-search-input" className="search-input" placeholder="Search by Job # (2049 or J-2049), customer, phone, email, address, claim #, or tech…" value={jobSearch} onChange={e=>setJobSearch(e.target.value)} style={{flex:"1 1 360px",maxWidth:520,padding:"8px 12px",fontSize:12,border:"1px solid var(--border)",borderRadius:4,fontWeight:600,color:"#2C2C2C"}}/>
 <button className="btn btn-primary" data-testid="open-new-job" onClick={()=>setShowNew(true)}>+ New Job</button>
 </div>
 <NewJobModal open={showNew} onClose={()=>setShowNew(false)} onCreate={createJob} user={user} existingIds={jobs.map(j=>j.id)} defaultWorkspaceId={user?.role==="owner" ? String(LOCATIONS[locIdx]?.id || "1") : user?.workspace_id}/>
 <div className="card">
 <div className="card-header"><span className="card-title">Job Management <span style={{fontSize:10,color:"var(--text3)",fontWeight:600,marginLeft:8}}>{filteredJobs.length} of {jobs.length}</span></span></div>
 <table><thead><tr><th>Job #</th><th>Customer</th><th>Type</th><th>Status</th><th>Phase</th><th>Tech</th><th>Total</th><th>Balance</th><th></th></tr></thead>
 <tbody>{filteredJobs.map(j=>{
 const locked = j.status === "Completed";
 const total = computeJobTotal(j);
 const addonCost = total - j.estimate;
 return (
 <React.Fragment key={j.id}>
 <tr id={"job-"+j.id} onClick={()=>setSel(sel===j.id?null:j.id)} style={{cursor:"pointer"}}>
 <td className="mono text-cyan">{j.parent_id && <span style={{display:"inline-block",width:16,color:"#9ca3af",marginRight:4,fontSize:10}}>└─</span>}{j.id}{locked&&<span className="lock-tag" style={{marginLeft:6}}>🔒</span>}</td>
 <td style={{fontWeight:500,fontSize:12}}><span data-testid={`job-cust-${j.id}`} onClick={e=>{e.stopPropagation(); setSel(j.id); setSelectedJobId(j.id);}} style={{cursor:"pointer",color:"#1e40af",textDecoration:"underline",fontWeight:600}}>{j.customer}</span></td>
 <td style={{fontSize:11}}>{j.type}</td>
 <td><StatusBadge status={j.status}/></td>
 <td><span className="badge badge-cyan" style={{fontSize:9}}>{j.phase}</span></td>
 <td style={{fontSize:11}}>{j.tech.split(" ")[0]}</td>
 <td className="mono text-right">${total.toLocaleString()}{addonCost>0&&<span className="text-amber text-sm"> +${addonCost.toLocaleString()}</span>}</td>
 <td className={`mono text-right ${j.balance>0?"text-amber":"text-green"}`}>${j.balance.toLocaleString()}</td>
 <td><button className="btn btn-ghost" style={{padding:"3px 10px",fontSize:10}}>Detail</button>{onOpenOverlay && <button data-testid={`job-overlay-btn-${j.id}`} className="btn btn-primary" style={{padding:"3px 10px",fontSize:10,marginLeft:4}} onClick={e=>{e.stopPropagation(); onOpenOverlay(j.id);}}>Notes</button>}{onOpenRestoration && <button data-testid={`job-restoration-btn-${j.id}`} className="btn btn-primary" style={{padding:"3px 10px",fontSize:10,marginLeft:4,background:"#1e40af"}} onClick={e=>{e.stopPropagation(); onOpenRestoration({id:j.id, customer:j.customer, address:j.address, phone:j.phone, email:j.email});}}>Job Files</button>}</td>
 </tr>
 {sel===j.id&&(
 <tr key={j.id+"-d"}><td colSpan={9} style={{padding:0}}>
 <div className="job-detail">
 {/* R16 — Linked Sub-Jobs (additive) */}
 <LinkedJobsBar masterId={j.parent_id || j.id} currentJobId={j.id} onPickJob={(id)=>{ setSel(id); setSelectedJobId(id); }} onCreated={(sj)=>{ setJobs(prev => prev.find(x=>x.id===sj.id) ? prev : [...prev, sj]); }} onDeleted={(deletedId)=>{ setJobs(prev => prev.filter(x => x.id !== deletedId)); }}/>
 <FieldProIQPanel key={"fpiq-"+j.id} jobId={j.id}/>
 {/* FIX 2 — Unsaved-changes banner + Save/Cancel buttons (Round 13) */}
 {(() => {
   const draftId = jobNumDraft[j.id];
   const dirty = draftId !== undefined && draftId !== j.id;
   const statusOther = statusOtherDraft[j.id];
   const statusDirty = (showOtherStatus[j.id] && statusOther && statusOther !== j.status);
   const anyDirty = dirty || statusDirty;
   return anyDirty ? (
     <div data-testid={`job-unsaved-banner-${j.id}`} style={{background:"#fff3c4",color:"#7a4c00",fontWeight:600,fontSize:12,padding:"8px 14px",borderBottom:"1px solid #f0c674",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
       <span>● You have unsaved changes</span>
       <div style={{display:"flex",gap:8}}>
         <button className="btn btn-ghost" style={{fontSize:11,color:"#2C2C2C",fontWeight:600}} data-testid={`job-cancel-top-${j.id}`} onClick={()=>{ setJobNumDraft(d=>{const n={...d}; delete n[j.id]; return n;}); setStatusOtherDraft(d=>{const n={...d}; delete n[j.id]; return n;}); setShowOtherStatus(d=>{const n={...d}; delete n[j.id]; return n;}); }}>Cancel Changes</button>
         <button className="btn btn-primary" style={{fontSize:11}} data-testid={`job-save-top-${j.id}`} onClick={()=>{
           const patch = {};
           if (dirty) patch.id = draftId;
           if (statusDirty) patch.status = statusOther;
           if (Object.keys(patch).length) { updateJob(j.id, patch); if (patch.id) setSel(patch.id); }
           setJobNumDraft(d=>{const n={...d}; delete n[j.id]; return n;});
         }}>Save Changes</button>
       </div>
     </div>
   ) : null;
 })()}
 <div className="jobnum-banner">
 <div>
 <div className="lbl" style={{color:"#2C2C2C",fontWeight:600}}>Job Number</div>
 <input className="jobnum-input" data-testid={`job-num-input-${j.id}`} value={jobNumDraft[j.id] !== undefined ? jobNumDraft[j.id] : j.id} disabled={locked} onClick={e=>e.stopPropagation()} onChange={e=>{ e.stopPropagation(); setJobNumDraft(d=>({...d,[j.id]:e.target.value.toUpperCase()})); }} style={{color:"#2C2C2C",fontWeight:600}}/>
 </div>
 <div style={{flex:1}}>
 <div className="lbl" style={{color:"#2C2C2C",fontWeight:600}}>Status</div>
 <select className="form-control" data-testid={`job-status-${j.id}`} value={showOtherStatus[j.id] ? "Other" : j.status} onChange={e=>{ const v = e.target.value; if (v === "Other") { setShowOtherStatus(d=>({...d,[j.id]:true})); setStatusOtherDraft(d=>({...d,[j.id]: j.status})); } else { setShowOtherStatus(d=>{const n={...d}; delete n[j.id]; return n;}); setStatusOtherDraft(d=>{const n={...d}; delete n[j.id]; return n;}); updateJob(j.id,{status:v}); } }} style={{minWidth:180,color:"#2C2C2C",fontWeight:600}}>
 {["Active","Cancelled","Completed","Completed Without Paperwork","Inspection","Invoice Pending","Other","Payment Received","Pending","Pending Sale","Pre-Production","Work In Progress (WIP)"].map(s=><option key={s} value={s}>{s}</option>)}
 </select>
 {showOtherStatus[j.id] && <input className="form-control" data-testid={`job-status-other-${j.id}`} value={statusOtherDraft[j.id] || ""} onChange={e=>setStatusOtherDraft(d=>({...d,[j.id]:e.target.value}))} placeholder="Specify other status…" style={{marginTop:6,color:"#2C2C2C",fontWeight:600}}/>}
 </div>
 <div style={{textAlign:"right"}}>
 <div className="lbl" style={{color:"#2C2C2C",fontWeight:600}}>{locked?"Job Locked":"Job Editable"}</div>
 <div style={{marginTop:6}}>
 {locked
 ? <span className="lock-tag">🔒 Read-only · Completed</span>
 : <span className="lock-tag" style={{color:"#60a5fa"}}>● Live edits enabled</span>}
 </div>
 </div>
 </div>

 <div className="detail-grid">
 <div className="detail-item"><div className="key">Customer</div><div className="val"><span data-testid={`job-detail-customer-link-${j.id}`} onClick={()=>{ try{sessionStorage.setItem("r13_customer_focus", j.customer);}catch{} window.dispatchEvent(new CustomEvent("apex:navigate",{detail:{page:"customers"}})); }} style={{cursor:"pointer",color:"#1e40af",textDecoration:"underline",fontWeight:600}}>{j.customer}</span></div></div>
 <div className="detail-item"><div className="key">Location</div><div className="val">{j.location}</div></div>
 <div className="detail-item"><div className="key">Started</div><div className="val mono">{j.started}</div></div>
 <div className="detail-item"><div className="key">Priority</div><div className="val">{j.priority||"Standard"}</div></div>
 <div className="detail-item"><div className="key">Lead Tech</div><div className="val">{j.tech}</div></div>
 <div className="detail-item"><div className="key">Moisture Avg</div><div className="val mono text-amber">{j.moisture}</div></div>
 <div className="detail-item" style={{gridColumn:"1/-1"}}><div className="key">Equipment On Site</div><div className="val">{j.equipment.join(" · ")}</div></div>
 </div>

 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"18px 0 14px"}}/>

 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:.5}}>📥 Field Pro IQ Upload <span className="badge badge-cyan" style={{marginLeft:8,fontSize:9}}>{(j.photos||[]).length} photos</span></div>
 <PhotoUploadButton locked={locked} onAdd={photos=>updateJob(j.id,{photos:[...(j.photos||[]),...photos]})}/>
 </div>
 <div className="detail-grid">
 <div className="detail-item"><div className="key">Customer Phone</div><input className="form-control" disabled={locked} value={j.customerPhone||""} onChange={e=>updateJob(j.id,{customerPhone:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Customer Email</div><input className="form-control" disabled={locked} value={j.customerEmail||""} onChange={e=>updateJob(j.id,{customerEmail:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Date of Loss</div><input className="form-control" disabled={locked} value={j.dateOfLoss||""} onChange={e=>updateJob(j.id,{dateOfLoss:e.target.value})} placeholder="MM/DD/YY"/></div>
 <div className="detail-item"><div className="key">Cause of Loss</div><input className="form-control" disabled={locked} value={j.causeOfLoss||""} onChange={e=>updateJob(j.id,{causeOfLoss:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Insurer</div><input className="form-control" disabled={locked} value={j.insurer||""} onChange={e=>updateJob(j.id,{insurer:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Adjuster</div><input className="form-control" disabled={locked} value={j.adjuster||""} onChange={e=>updateJob(j.id,{adjuster:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Adjuster Phone</div><input className="form-control" disabled={locked} value={j.adjusterPhone||""} onChange={e=>updateJob(j.id,{adjusterPhone:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Claim #</div><input className="form-control" disabled={locked} value={j.claimNumber||""} onChange={e=>updateJob(j.id,{claimNumber:e.target.value})} placeholder="—"/></div>
 <div className="detail-item"><div className="key">Policy #</div><input className="form-control" disabled={locked} value={j.policyNumber||""} onChange={e=>updateJob(j.id,{policyNumber:e.target.value})} placeholder="—"/></div>
 <div className="detail-item" style={{gridColumn:"1/-1"}}><div className="key">Issue Description (Field Pro IQ)</div><textarea className="form-control" disabled={locked} rows={3} value={j.issueDescription||""} onChange={e=>updateJob(j.id,{issueDescription:e.target.value})} placeholder="Narrative description from technician on-site…" style={{width:"100%",resize:"vertical",fontFamily:"var(--dm)"}}/></div>
 <div style={{gridColumn:"1/-1"}}><WaterCategoryClassFields jobId={j.id} initial={{water_category:j.water_category,water_class:j.water_class,affected_sq_ft:j.affected_sq_ft}} onChange={(p)=>updateJob(j.id,p)}/></div>
 <div className="detail-item" style={{gridColumn:"1/-1"}}><div className="key">Moisture Mapping</div><textarea className="form-control" disabled={locked} rows={2} value={j.moistureMapping||""} onChange={e=>updateJob(j.id,{moistureMapping:e.target.value})} placeholder="Readings, grid map URL, or notes…" style={{width:"100%",resize:"vertical",fontFamily:"var(--dm)"}}/></div>
 <div className="detail-item" style={{gridColumn:"1/-1"}}><div className="key">Mold Report</div><textarea className="form-control" disabled={locked} rows={2} value={j.moldReport||""} onChange={e=>updateJob(j.id,{moldReport:e.target.value})} placeholder="Lab report ID, species, spore count, or PDF URL…" style={{width:"100%",resize:"vertical",fontFamily:"var(--dm)"}}/></div>
 </div>
 {(j.photos||[]).length>0 && (
  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10,marginTop:14}}>
   {(j.photos||[]).map(ph=>(
    <div key={ph.id} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,overflow:"hidden",position:"relative"}}>
     <a href={ph.url} target="_blank" rel="noreferrer"><img src={ph.url} alt={ph.caption} style={{width:"100%",height:120,objectFit:"cover",display:"block"}} onError={e=>{e.target.style.display="none";}}/></a>
     <div style={{padding:8,fontSize:11,color:"var(--text2)"}}>
      <div style={{wordBreak:"break-word"}}>{ph.caption || <span style={{color:"var(--text3)"}}>(no caption)</span>}</div>
      <div className="mono text-sm" style={{color:"var(--text3)",marginTop:4}}>{ph.uploadedAt}</div>
     </div>
     <button className="icon-btn" disabled={locked} onClick={()=>updateJob(j.id,{photos:(j.photos||[]).filter(x=>x.id!==ph.id)})} style={{position:"absolute",top:4,right:4,padding:"2px 6px",fontSize:11}} title="Remove">×</button>
    </div>
   ))}
  </div>
 )}

 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"18px 0 14px"}}/>

 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:.5}}>🏛️ Permit
  <span className={`badge ${j.permit?.needed?(j.permit.status==="Approved"||j.permit.status==="Filed"?"badge-green":j.permit.status==="Denied"?"badge-red":"badge-amber"):"badge-gray"}`} style={{marginLeft:8,fontSize:9}}>{j.permit?.needed?j.permit.status||"Pending":"Not Required"}</span>
 </div>
 <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--text2)",cursor:locked?"default":"pointer"}}>
  <input type="checkbox" disabled={locked} checked={!!j.permit?.needed} onChange={e=>updateJob(j.id,{permit:{...(j.permit||{}),needed:e.target.checked,status:e.target.checked?(j.permit?.status||"Pending"):"Not Required"}})}/>
  Permit Needed
 </label>
 </div>
 {j.permit?.needed && (
  <div className="detail-grid">
   <div className="detail-item"><div className="key">Permit Type</div><input className="form-control" disabled={locked} value={j.permit?.type||""} onChange={e=>updateJob(j.id,{permit:{...j.permit,type:e.target.value}})} placeholder="e.g., Plumbing, Electrical, Abatement"/></div>
   <div className="detail-item"><div className="key">Status</div>
    <select className="form-control" disabled={locked} value={j.permit?.status||"Pending"} onChange={e=>updateJob(j.id,{permit:{...j.permit,status:e.target.value,filedAt:e.target.value==="Filed"&&!j.permit?.filedAt?new Date().toISOString().slice(0,10):j.permit?.filedAt}})}>
     {["Not Started","Pending","Filed","Approved","Denied"].map(s=><option key={s} value={s}>{s}</option>)}
    </select>
   </div>
   <div className="detail-item"><div className="key">Filed Date</div><input className="form-control" disabled={locked} value={j.permit?.filedAt||""} onChange={e=>updateJob(j.id,{permit:{...j.permit,filedAt:e.target.value}})} placeholder="YYYY-MM-DD"/></div>
   <div className="detail-item" style={{gridColumn:"1 / span 2"}}><div className="key">Permit Page / Document URL</div>
    <div style={{display:"flex",gap:6,alignItems:"center"}}>
     <input className="form-control" disabled={locked} value={j.permit?.link||""} onChange={e=>updateJob(j.id,{permit:{...j.permit,link:e.target.value}})} placeholder="https://permits.example.gov/…" style={{flex:1}}/>
     {j.permit?.link && <a href={j.permit.link} target="_blank" rel="noreferrer" className="btn btn-outline" style={{fontSize:10,padding:"5px 10px",textDecoration:"none"}}>↗ Open</a>}
    </div>
   </div>
   <div className="detail-item"><div className="key">Document Filename</div><input className="form-control" disabled={locked} value={j.permit?.documentName||""} onChange={e=>updateJob(j.id,{permit:{...j.permit,documentName:e.target.value}})} placeholder="e.g., permit-44120.pdf"/></div>
   <div className="detail-item" style={{gridColumn:"1/-1"}}><div className="key">Permit Notes</div><textarea className="form-control" disabled={locked} rows={2} value={j.permit?.notes||""} onChange={e=>updateJob(j.id,{permit:{...j.permit,notes:e.target.value}})} placeholder="Posting requirements, inspector contact, conditions…" style={{width:"100%",resize:"vertical",fontFamily:"var(--dm)"}}/></div>
  </div>
 )}

 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"18px 0 14px"}}/>

 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:.5,color:"#2C2C2C"}}>Notes</div>
 </div>
 <JobNotesInline jobId={j.id} locked={locked}/>

 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"18px 0 14px"}}/>

 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:.5}}>📞 Call Log {j.calls?.length>0&&<span className="badge badge-cyan" style={{marginLeft:8}}>{j.calls.length}</span>}{(j.calls||[]).some(c=>c.reminderDays>0&&isOverdue(addDays(c.datetime,c.reminderDays)))&&<span className="badge badge-red" style={{marginLeft:6}}>Overdue</span>}</div>
 <button className="btn btn-outline" onClick={()=>addCall(j.id)} style={{fontSize:10,padding:"5px 10px"}}>+ Log Call</button>
 </div>
 {(!j.calls||j.calls.length===0)&&<div style={{fontSize:11,color:"var(--text3)",padding:"10px 0",textAlign:"center"}}>No calls logged. Click "+ Log Call" to record customer contact and set a 1–7 day follow-up reminder.</div>}
 {(j.calls||[]).map(c=><CallLogRow key={c.id} call={c} locked={false} onChange={n=>updateCall(j.id,c.id,n)} onRemove={()=>removeCall(j.id,c.id)}/>)}

 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"14px 0"}}/>

 <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
 {[["Base Estimate",j.estimate],["Add-Ons",addonCost],["Job Total",total],["Billed",j.billed],["Paid",j.paid]].map(([k,v])=>(
 <div key={k} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,padding:"10px 14px"}}>
 <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{k}</div>
 <div style={{fontFamily:"var(--rajdhani)",fontSize:18,fontWeight:700,color:k==="Job Total"?"var(--cyan)":k==="Paid"?"var(--green)":k==="Add-Ons"&&v>0?"var(--amber)":"var(--text)"}}>${(v||0).toLocaleString()}</div>
 </div>
 ))}
 </div>
 <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
 <button className="btn btn-primary" disabled={locked} data-testid={`job-create-invoice-${j.id}`} style={{fontSize:11}} onClick={async()=>{
   try {
     const r = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/iv/r12/jobs/${encodeURIComponent(j.id)}/invoice/push_qb`,{method:"POST"});
     const d = await r.json();
     if (!r.ok) throw new Error(d.detail || "Failed to queue invoice");
     toast(`Invoice queued for QuickBooks · ${d.misc_lines_included||0} Misc line${d.misc_lines_included===1?"":"s"} included · Total $${(d.invoice?.total||0).toFixed(2)}`,"ok");
   } catch(e) { toast(e.message||"Could not queue invoice","err"); }
 }}>Create Invoice</button>
 <button className="btn btn-outline" disabled={locked} style={{fontSize:11}}>Open in Xactimate</button>
 <button className="btn btn-ghost" style={{fontSize:11}}>Moisture Reading</button>
 <button className="btn btn-ghost" style={{fontSize:11}}>Photo Gallery</button>
 </div>
 {/* FIX 8 — Miscellaneous Items table (Round 13) */}
 <hr style={{border:"none",borderTop:"1px solid var(--border)",margin:"18px 0 14px"}}/>
 <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
  <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:.5,color:"#2C2C2C"}}>Miscellaneous Items <span className="badge badge-cyan" style={{marginLeft:6,fontSize:9}}>{(j.misc_items||[]).length}</span></div>
  <button className="btn btn-outline" data-testid={`misc-add-${j.id}`} disabled={locked} onClick={()=>updateJob(j.id,{misc_items:[...(j.misc_items||[]),{id:`MI-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,description:"",price:0}]})} style={{fontSize:10,padding:"5px 10px"}}>+ Add Line</button>
 </div>
 {(j.misc_items||[]).length === 0 && <div style={{fontSize:11,color:"#666",fontWeight:600,padding:"8px 0",textAlign:"center"}}>No miscellaneous items yet. Click + Add Line to capture temp labor & other variable costs (shown on invoice as "Miscellaneous").</div>}
 {(j.misc_items||[]).length > 0 && (
  <table style={{width:"100%",fontSize:12}}><thead><tr>
   <th style={{textAlign:"left",padding:"6px 8px",color:"#2C2C2C",fontWeight:600,borderBottom:"1px solid var(--border)"}}>Description</th>
   <th style={{textAlign:"right",padding:"6px 8px",width:140,color:"#2C2C2C",fontWeight:600,borderBottom:"1px solid var(--border)"}}>Price ($)</th>
   <th style={{width:60,borderBottom:"1px solid var(--border)"}}></th>
  </tr></thead><tbody>
   {(j.misc_items||[]).map((mi,idx)=>(
    <tr key={mi.id} data-testid={`misc-row-${j.id}-${idx}`}>
     <td style={{padding:"4px 8px"}}><input className="form-control" disabled={locked} value={mi.description} onChange={e=>updateJob(j.id,{misc_items:(j.misc_items||[]).map(x=>x.id===mi.id?{...x,description:e.target.value}:x)})} placeholder="e.g. Temp labor — Saturday OT" style={{color:"#2C2C2C",fontWeight:600}}/></td>
     <td style={{padding:"4px 8px",textAlign:"right"}}><input className="form-control" disabled={locked} type="number" min={0} step={0.01} value={mi.price} onChange={e=>updateJob(j.id,{misc_items:(j.misc_items||[]).map(x=>x.id===mi.id?{...x,price:parseFloat(e.target.value)||0}:x)})} style={{textAlign:"right",color:"#2C2C2C",fontWeight:600}}/></td>
     <td style={{padding:"4px 8px",textAlign:"center"}}><button className="btn btn-ghost" data-testid={`misc-del-${j.id}-${idx}`} disabled={locked} onClick={()=>updateJob(j.id,{misc_items:(j.misc_items||[]).filter(x=>x.id!==mi.id)})} style={{fontSize:10,padding:"3px 8px",color:"#C0392B"}}>Delete</button></td>
    </tr>
   ))}
   <tr><td colSpan={2} style={{padding:"8px",textAlign:"right",fontWeight:700,color:"#2C2C2C",borderTop:"2px solid var(--border)"}}>Subtotal: ${((j.misc_items||[]).reduce((a,x)=>a+(parseFloat(x.price)||0),0)).toFixed(2)}</td><td/></tr>
  </tbody></table>
 )}
 {/* Bottom Save/Cancel — mirrors top banner (FIX 2) */}
 {(jobNumDraft[j.id] !== undefined && jobNumDraft[j.id] !== j.id) || (showOtherStatus[j.id] && statusOtherDraft[j.id] && statusOtherDraft[j.id] !== j.status) ? (
   <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end",gap:8}}>
     <button className="btn btn-ghost" data-testid={`job-cancel-bottom-${j.id}`} onClick={()=>{ setJobNumDraft(d=>{const n={...d}; delete n[j.id]; return n;}); setStatusOtherDraft(d=>{const n={...d}; delete n[j.id]; return n;}); setShowOtherStatus(d=>{const n={...d}; delete n[j.id]; return n;}); }} style={{fontSize:11,color:"#2C2C2C",fontWeight:600}}>Cancel Changes</button>
     <button className="btn btn-primary" data-testid={`job-save-bottom-${j.id}`} onClick={()=>{
       const patch={};
       const dNum = jobNumDraft[j.id]; if (dNum !== undefined && dNum !== j.id) patch.id = dNum;
       const dStat = statusOtherDraft[j.id]; if (showOtherStatus[j.id] && dStat && dStat !== j.status) patch.status = dStat;
       if (Object.keys(patch).length) { updateJob(j.id, patch); if (patch.id) setSel(patch.id); }
       setJobNumDraft(d=>{const n={...d}; delete n[j.id]; return n;});
     }} style={{fontSize:11}}>Save Changes</button>
   </div>
 ) : null}
 </div>
 </td></tr>
 )}
 </React.Fragment>
 );})}</tbody></table>
 </div>
 </div>
 );
}

function Inventory() {
 const [q, setQ] = useState("");
 const allDeployments = INVENTORY.flatMap(i => (i.deployments||[]).map(d => ({...d, invId:i.id, item:i.item, category:i.category})));
 const ql = q.trim().toLowerCase();
 const filtered = ql ? allDeployments.filter(d => d.jobId.toLowerCase().includes(ql) || d.customer.toLowerCase().includes(ql) || d.address.toLowerCase().includes(ql) || d.item.toLowerCase().includes(ql)) : allDeployments;
 const uniqueJobs = new Set(allDeployments.map(d=>d.jobId)).size;
 return (
  <div>
   <div className="kpi-row" style={{gridTemplateColumns:"repeat(5,1fr)"}}>
    <KPI label="Total SKUs" value={INVENTORY.length} color="cyan"/>
    <KPI label="Units Deployed" value={INVENTORY.reduce((a,i)=>a+i.deployed,0)} color="amber" accent="var(--amber)"/>
    <KPI label="Units Available" value={INVENTORY.reduce((a,i)=>a+i.available,0)} color="green" accent="var(--green)"/>
    <KPI label="Active Jobs w/ Equipment" value={uniqueJobs} color="cyan"/>
    <KPI label="Low Stock Alerts" value={INVENTORY.filter(i=>i.available<=i.reorder).length} color="red" accent="var(--red)"/>
   </div>
   <div className="card">
    <div className="card-header"><span className="card-title">Inventory Management</span>
    <div style={{display:"flex",gap:8}}><button className="btn btn-ghost" style={{fontSize:11}}>Transfer Equipment</button><button className="btn btn-primary" style={{fontSize:11}}>+ Add Item</button></div>
    </div>
    <table><thead><tr><th>ID</th><th>Item</th><th>Category</th><th>Total</th><th>Deployed</th><th>Available</th><th>Reorder At</th><th>Unit Cost</th><th>Status</th></tr></thead>
    <tbody>{INVENTORY.map(i=>{
     const pct=(i.deployed/i.total)*100; const low=i.available<=i.reorder;
     return (<tr key={i.id}>
      <td className="mono text-cyan text-sm">{i.id}</td><td style={{fontWeight:500}}>{i.item}</td>
      <td><span className="badge badge-gray">{i.category}</span></td><td className="mono">{i.total}</td>
      <td><div>{i.deployed}</div><div className="progress-bar" style={{width:60}}><div className="progress-fill" style={{width:`${pct}%`,background:pct>70?"#ff3333":"#cc0000"}}/></div></td>
      <td className={`mono ${low?"text-red":"text-green"}`}>{i.available}</td>
      <td className="mono text-sm">{i.reorder}</td><td className="mono text-sm">${i.cost.toLocaleString()}</td>
      <td>{low?<span className="badge badge-red">Low Stock</span>:<span className="badge badge-green">OK</span>}</td>
     </tr>);
    })}</tbody></table>
   </div>

   <div className="card" style={{marginTop:20}}>
    <div className="card-header" style={{flexWrap:"wrap",gap:12}}>
     <span className="card-title">📦 Equipment Deployment Report</span>
     <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
      <input className="search-input" placeholder="Search by job #, customer, or address…" value={q} onChange={e=>setQ(e.target.value)}/>
      {q && <button className="btn btn-ghost" onClick={()=>setQ("")} style={{fontSize:11,padding:"5px 10px"}} title="Clear search filter">✕ Reset Filters</button>}
      <span className="mono text-sm" style={{color:"var(--text3)"}}>{filtered.length} of {allDeployments.length} · {filtered.reduce((a,d)=>a+d.qty,0)} units</span>
      <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{
        const cols = ["Item","Inventory ID","Qty","Job #","Customer","Address","Deployed","Lead Tech"];
        const rows = filtered.map(d=>[d.item,d.invId,d.qty,d.jobId,d.customer,d.address,d.deployedAt,d.tech]);
        exportPDF("Equipment Deployment Report", cols, rows);
      }}>PDF</button>
      <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{
        const cols = ["Item","Inventory ID","Qty","Job #","Customer","Address","Deployed","Lead Tech"];
        const rows = filtered.map(d=>[d.item,d.invId,d.qty,d.jobId,d.customer,d.address,d.deployedAt,d.tech]);
        exportCSV("Equipment Deployment Report", cols, rows);
      }}>CSV</button>
      <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>{
        const cols = ["Item","Inventory ID","Qty","Job #","Customer","Address","Deployed","Lead Tech"];
        const rows = filtered.map(d=>[d.item,d.invId,d.qty,d.jobId,d.customer,d.address,d.deployedAt,d.tech]);
        exportEmail("Equipment Deployment Report", cols, rows);
      }}>Email</button>
     </div>
    </div>
    <table><thead><tr><th>Item</th><th>Qty</th><th>Job #</th><th>Customer</th><th>Address</th><th>Deployed</th><th>Lead Tech</th></tr></thead>
    <tbody>
     {filtered.length===0 && <tr><td colSpan={7} style={{textAlign:"center",padding:"20px",color:"var(--text3)",fontSize:11}}>No deployments match "{q}".</td></tr>}
     {filtered.map((d,i)=>(
      <tr key={d.invId+"-"+d.jobId+"-"+i}>
       <td style={{fontWeight:500,fontSize:12}}>{d.item}<div className="mono text-sm" style={{color:"var(--text3)"}}>{d.invId}</div></td>
       <td className="mono text-amber" style={{fontWeight:600}}>{d.qty}</td>
       <td className="mono text-cyan">{d.jobId}</td>
       <td style={{fontSize:12}}>{d.customer}</td>
       <td style={{fontSize:11,color:"var(--text2)"}}>{d.address}</td>
       <td className="mono text-sm">{d.deployedAt}</td>
       <td style={{fontSize:11}}>{d.tech}</td>
      </tr>
     ))}
    </tbody></table>
   </div>
  </div>
 );
}

function QuickBooks() {
 const [syncing, setSyncing] = useState(false);
 const handleSync = () => { setSyncing(true); setTimeout(()=>setSyncing(false),2200); };
 return (
 <div>
 <div className="integration-card mb20">
 <div className="int-header">
 <div className="int-logo" style={{background:"#8b0000",color:"#fff",fontSize:14}}>QB</div>
 <div style={{flex:1}}><div className="int-name">QuickBooks Online</div><div className="int-status">● Connected · Apex Infinity Ventures, Inc.</div></div>
 <button className="btn btn-primary" onClick={handleSync} style={{fontSize:11}}>{syncing?"↻ Syncing…":"↻ Sync Now"}</button>
 </div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
 {[["Invoices Synced","4/5","cyan"],["Total AR","$58,750","amber"],["Collected MTD","$14,200","green"],["Overdue","$15,850","red"]].map(([l,v,c])=>(
 <div key={l} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,padding:"12px 16px"}}>
 <div className="kpi-label">{l}</div><div className={`kpi-value text-${c}`} style={{fontSize:24}}>{v}</div>
 </div>
 ))}
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Invoices — QB Status</span><button className="btn btn-primary" style={{fontSize:11}}>+ Create Invoice</button></div>
 <table><thead><tr><th>Invoice</th><th>Customer</th><th>Amount</th><th>Paid</th><th>Due</th><th>Status</th><th>QB Sync</th><th>Actions</th></tr></thead>
 <tbody>{QB_INVOICES.map(inv=>(
 <tr key={inv.id}>
 <td className="mono text-cyan">{inv.id}</td><td style={{fontWeight:500,fontSize:12}}>{inv.customer}</td>
 <td className="mono">${inv.amount.toLocaleString()}</td><td className="mono text-green">${inv.paid.toLocaleString()}</td>
 <td className={`mono ${inv.due>0?"text-amber":"text-green"}`}>${inv.due.toLocaleString()}</td>
 <td><StatusBadge status={inv.status}/></td>
 <td>{inv.qbSynced?<span className="badge badge-green">✓ Synced</span>:<span className="badge badge-amber">Pending</span>}</td>
 <td><div style={{display:"flex",gap:6}}><button className="btn btn-outline" style={{padding:"3px 10px",fontSize:10}}>Send</button><button className="btn btn-ghost" style={{padding:"3px 10px",fontSize:10}}>QB Open</button></div></td>
 </tr>
 ))}</tbody></table>
 </div>
 </div>
 );
}

function Xactimate({ jobs }) {
 return (
 <div>
 <div className="integration-card mb20">
 <div className="int-header">
 <div className="int-logo" style={{background:"#6b0000",color:"#fff",fontSize:12}}>XA</div>
 <div><div className="int-name">Xactimate / Xactanalysis</div><div className="int-status">● Connected · Profile: INFINITY INTEL BIZ</div></div>
 </div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
 {[["Active Estimates","4","cyan"],["Pending Approval","1","amber"],["Total Estimated","$73,200","green"],["Avg Cycle Time","3.2 days","cyan"]].map(([l,v,c])=>(
 <div key={l} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,padding:"12px 16px"}}>
 <div className="kpi-label">{l}</div><div className={`kpi-value text-${c}`} style={{fontSize:24}}>{v}</div>
 </div>
 ))}
 </div>
 </div>
 <div className="card">
 <div className="card-header"><span className="card-title">Xactimate Estimates by Job</span><button className="btn btn-primary" style={{fontSize:11}}>+ New Estimate</button></div>
 <table><thead><tr><th>Estimate #</th><th>Job</th><th>Customer</th><th>Type</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
 <tbody>{jobs.map(j=>(
 <tr key={j.id}>
 <td className="mono text-cyan">{j.xact==="Pending"?"—":j.xact}</td><td className="mono">{j.id}</td>
 <td style={{fontSize:12}}>{j.customer}</td><td style={{fontSize:11}}>{j.type}</td>
 <td className="mono">${computeJobTotal(j).toLocaleString()}</td>
 <td>{j.xact==="Pending"?<span className="badge badge-amber">Pending</span>:<span className="badge badge-cyan">Active</span>}</td>
 <td><div style={{display:"flex",gap:6}}><button className="btn btn-outline" style={{padding:"3px 10px",fontSize:10}}>Open in XA</button><button className="btn btn-ghost" style={{padding:"3px 10px",fontSize:10}}>Submit</button></div></td>
 </tr>
 ))}</tbody></table>
 </div>
 </div>
 );
}

function Reports({ jobs }) {
 const [generated, setGenerated] = useState(null);
 const [advFilters, setAdvFilters] = useState({});
 // Round 13 — Executive Dashboard first, all other reports alphabetized
 const _execReport = { label: "Executive Dashboard", sub: "Monthly KPI snapshot · full BI", type: "kpi" };
 const _otherReports = [
  {label:"Job Summary Report",sub:"All active & completed jobs",type:"job"},
  {label:"Technician Performance",sub:"Hours, ratings, jobs completed",type:"tech"},
  {label:"Revenue & Collections",sub:"Billed, paid, outstanding",type:"revenue"},
  {label:"Inventory Report",sub:"Stock levels, deployed, reorders",type:"inventory"},
  {label:"GPS Activity Log",sub:"All technician GPS history",type:"gps"},
  {label:"QuickBooks Sync",sub:"Invoice & payment status",type:"qb"},
  {label:"Xactimate Summary",sub:"Estimate pipeline report",type:"xa"},
  {label:"Equipment Report",sub:"All deployed equipment",type:"equip"},
  {label:"Location Report",sub:"Per-location breakdown",type:"loc"},
  {label:"Exception Report",sub:"Alerts & anomalies",type:"exc"},
 ].sort((a, b) => a.label.localeCompare(b.label));
 const reports = [_execReport, ..._otherReports];

 const getReportData = (type) => {
  switch(type){
   case "job": return {columns:["Job #","Customer","Type","Status","Phase","Tech","Started","Estimate","Billed","Balance"],
    rows: jobs.map(j=>[j.id,j.customer,j.type,j.status,j.phase,j.tech,j.started,computeJobTotal(j),j.billed,j.balance])};
   case "tech": return {columns:["ID","Name","Role","Status","Current Job","Vehicle","Cert","Hours YTD","Rating"],
    rows: TECHNICIANS.map(t=>[t.id,t.name,t.role,t.status,t.job,t.vehicle,t.cert,t.hours,t.rating])};
   case "revenue": return {columns:["Job #","Customer","Estimate","Billed","Paid","Outstanding","% Collected"],
    rows: jobs.map(j=>[j.id,j.customer,computeJobTotal(j),j.billed,j.paid,j.balance, j.billed>0?Math.round(j.paid/j.billed*100)+"%":"0%"])};
   case "inventory": return {columns:["ID","Item","Category","Total","Deployed","Available","Reorder","Unit Cost","Status"],
    rows: INVENTORY.map(i=>[i.id,i.item,i.category,i.total,i.deployed,i.available,i.reorder,i.cost,i.available<=i.reorder?"Low Stock":"OK"])};
   case "gps": return {columns:["Time","Technician","Event","Location","Miles"],
    rows: GPS_LOG.map(g=>[g.time,g.tech,g.event,g.location,g.miles])};
   case "qb": return {columns:["Invoice #","Customer","Amount","Paid","Due","Status","QB Synced"],
    rows: QB_INVOICES.map(i=>[i.id,i.customer,i.amount,i.paid,i.due,i.status,i.qbSynced?"Yes":"No"])};
   case "xa": return {columns:["Job #","Customer","Type","Xact ID","Phase","Estimate","Status"],
    rows: jobs.map(j=>[j.id,j.customer,j.type,j.xact,j.phase,computeJobTotal(j),j.xact==="Pending"?"Awaiting":"Submitted"])};
   case "equip": { const all = INVENTORY.flatMap(i=>(i.deployments||[]).map(d=>({...d,item:i.item,invId:i.id})));
    return {columns:["Item","Inventory ID","Qty","Job #","Customer","Address","Deployed","Lead Tech"],
     rows: all.map(d=>[d.item,d.invId,d.qty,d.jobId,d.customer,d.address,d.deployedAt,d.tech])}; }
   case "kpi": return {columns:["Metric","Value","MoM Change"], rows:[
    ["Total Customers", CUSTOMERS.length, "+12.4%"],
    ["Total Active Jobs", jobs.filter(j=>j.status==="Active").length, "+8.2%"],
    ["Emergency Jobs Open", jobs.filter(j=>j.priority==="Emergency"&&j.status!=="Completed").length, "-9.1%"],
    ["Total Contracted", jobs.reduce((a,j)=>a+computeJobTotal(j),0), "+18.7%"],
    ["Outstanding A/R", jobs.reduce((a,j)=>a+j.balance,0), "-4.2%"],
    ["Avg Job Value", Math.round(jobs.reduce((a,j)=>a+computeJobTotal(j),0)/jobs.length), "+6.1%"],
    ["Avg Response Time", "22 min", "-12.0%"],
    ["Job Satisfaction Score", "4.8 / 5", "+1.5%"],
    ["Tech Avg Hours YTD", Math.round(TECHNICIANS.reduce((a,t)=>a+t.hours,0)/TECHNICIANS.length), "+8.0%"],
    ["Inventory Value", INVENTORY.reduce((a,i)=>a+i.cost*i.total,0), "—"],
   ]};
   case "loc": return {columns:["ID","Location","Address","Active Jobs","Techs On-Site","Status"],
    rows: LOCATIONS.map(l=>{
     const jobsAtLoc = jobs.filter(j=>j.location.includes(l.address.split(",")[0])||l.address.includes(j.location)).length;
     const techsAtLoc = TECHNICIANS.filter(t=>t.location.includes(l.address.split(",")[0])||l.address.includes(t.location)).length;
     return ["L-"+String(l.id).padStart(3,"0"), l.name, l.address, jobsAtLoc, techsAtLoc, l.active?"Active":"Inactive"];
    })};
   case "exc": return {columns:["Alert ID","Severity","Time","Category","Message","Related Job"],
    rows: ALERTS.map(a=>[a.id,a.severity,a.time,a.category,a.message,a.job])};
   default: return {columns:[],rows:[]};
  }
 };

 const handleCSV = () => {
  const r = reports.find(x=>x.label===generated); if(!r) return;
  const {columns, rows} = getReportData(r.type);
  exportCSV(r.label, columns, rows);
 };
 const handlePDF = () => {
  const r = reports.find(x=>x.label===generated); if(!r) return;
  const {columns, rows} = getReportData(r.type);
  exportPDF(r.label, columns, rows);
 };
 const handleEmail = () => {
  const r = reports.find(x=>x.label===generated); if(!r) return;
  const {columns, rows} = getReportData(r.type);
  exportEmail(r.label, columns, rows);
 };

 const today = todayISO();

 const renderPreview = () => {
  if (!generated) return null;
  const r = reports.find(x=>x.label===generated);
  if (!r) return null;
  switch (r.type) {
   case "job": return (
    <table><thead><tr><th>Job #</th><th>Customer</th><th>Type</th><th>Status</th><th>Phase</th><th>Tech</th><th>Started</th><th className="text-right">Estimate</th><th className="text-right">Billed</th><th className="text-right">Balance</th></tr></thead>
    <tbody>{jobs.map(j=>(<tr key={j.id}>
     <td className="mono text-cyan">{j.id}</td><td style={{fontWeight:500,fontSize:12}}>{j.customer}</td>
     <td style={{fontSize:11}}>{j.type}</td><td><StatusBadge status={j.status}/></td>
     <td><span className="badge badge-cyan" style={{fontSize:9}}>{j.phase}</span></td>
     <td style={{fontSize:11}}>{j.tech}</td><td className="mono text-sm">{j.started}</td>
     <td className="mono text-right">${computeJobTotal(j).toLocaleString()}</td>
     <td className="mono text-right">${j.billed.toLocaleString()}</td>
     <td className={`mono text-right ${j.balance>0?"text-amber":"text-green"}`}>${j.balance.toLocaleString()}</td>
    </tr>))}</tbody></table>
   );
   case "tech": return (
    <table><thead><tr><th>ID</th><th>Name</th><th>Role</th><th>Status</th><th>Current Job</th><th>Vehicle</th><th>Cert</th><th className="text-right">Hours YTD</th><th>Rating</th></tr></thead>
    <tbody>{TECHNICIANS.map(t=>(<tr key={t.id}>
     <td className="mono text-cyan text-sm">{t.id}</td><td style={{fontWeight:500}}>{t.name}</td>
     <td style={{fontSize:11}}>{t.role}</td>
     <td><span className={`badge ${t.status==="On Job"?"badge-cyan":t.status==="Available"?"badge-green":t.status==="En Route"?"badge-amber":"badge-gray"}`} style={{fontSize:9}}>{t.status}</span></td>
     <td className="mono text-sm">{t.job}</td><td style={{fontSize:11}}>{t.vehicle}</td>
     <td style={{fontSize:10,color:"var(--text3)"}}>{t.cert}</td>
     <td className="mono text-right">{t.hours}</td>
     <td className="mono text-green">{t.rating} ★</td>
    </tr>))}</tbody></table>
   );
   case "revenue": {
    const totalEst = jobs.reduce((a,j)=>a+computeJobTotal(j),0);
    const totalBilled = jobs.reduce((a,j)=>a+j.billed,0);
    const totalPaid = jobs.reduce((a,j)=>a+j.paid,0);
    const totalOut = jobs.reduce((a,j)=>a+j.balance,0);
    return (<>
     <div className="kpi-row" style={{padding:"14px 16px 0",gridTemplateColumns:"repeat(4,1fr)"}}>
      <KPI label="Total Contracted" value={`$${(totalEst/1000).toFixed(1)}K`} color="amber" accent="var(--amber)"/>
      <KPI label="Billed" value={`$${(totalBilled/1000).toFixed(1)}K`} color="cyan"/>
      <KPI label="Collected" value={`$${(totalPaid/1000).toFixed(1)}K`} color="green" accent="var(--green)"/>
      <KPI label="Outstanding" value={`$${(totalOut/1000).toFixed(1)}K`} color="red" accent="var(--red)"/>
     </div>
     <table><thead><tr><th>Job #</th><th>Customer</th><th className="text-right">Estimate</th><th className="text-right">Billed</th><th className="text-right">Paid</th><th className="text-right">Outstanding</th><th>% Collected</th></tr></thead>
     <tbody>{jobs.map(j=>{ const pct = j.billed>0 ? Math.round(j.paid/j.billed*100) : 0; return (
      <tr key={j.id}>
       <td className="mono text-cyan">{j.id}</td><td style={{fontWeight:500,fontSize:12}}>{j.customer}</td>
       <td className="mono text-right">${computeJobTotal(j).toLocaleString()}</td>
       <td className="mono text-right">${j.billed.toLocaleString()}</td>
       <td className="mono text-right text-green">${j.paid.toLocaleString()}</td>
       <td className={`mono text-right ${j.balance>0?"text-amber":"text-green"}`}>${j.balance.toLocaleString()}</td>
       <td><div className="progress-bar" style={{width:80}}><div className="progress-fill" style={{width:`${pct}%`,background:pct>=80?"#22c55e":pct>=40?"#3b82f6":"#cc0000"}}/></div></td>
      </tr>);})}</tbody></table>
    </>);
   }
   case "inventory": return (
    <table><thead><tr><th>ID</th><th>Item</th><th>Category</th><th className="text-right">Total</th><th className="text-right">Deployed</th><th className="text-right">Available</th><th className="text-right">Reorder</th><th className="text-right">Unit Cost</th><th>Status</th></tr></thead>
    <tbody>{INVENTORY.map(i=>{ const low=i.available<=i.reorder; return (<tr key={i.id}>
     <td className="mono text-cyan text-sm">{i.id}</td><td style={{fontWeight:500}}>{i.item}</td>
     <td><span className="badge badge-gray">{i.category}</span></td>
     <td className="mono text-right">{i.total}</td><td className="mono text-right text-amber">{i.deployed}</td>
     <td className={`mono text-right ${low?"text-red":"text-green"}`}>{i.available}</td>
     <td className="mono text-right text-sm">{i.reorder}</td>
     <td className="mono text-right text-sm">${i.cost.toLocaleString()}</td>
     <td>{low?<span className="badge badge-red">Low Stock</span>:<span className="badge badge-green">OK</span>}</td>
    </tr>);})}</tbody></table>
   );
   case "gps": return (
    <table><thead><tr><th>Time</th><th>Technician</th><th>Event</th><th>Location</th><th className="text-right">Miles</th></tr></thead>
    <tbody>{GPS_LOG.map((g,i)=>(<tr key={i}>
     <td className="mono text-sm text-cyan">{g.time}</td>
     <td style={{fontWeight:500,fontSize:12}}>{g.tech}</td>
     <td><span className={`badge ${g.event.includes("Arrived")?"badge-green":g.event.includes("En Route")?"badge-amber":g.event.includes("Break")?"badge-gray":"badge-cyan"}`} style={{fontSize:9}}>{g.event}</span></td>
     <td style={{fontSize:11}}>{g.location}</td>
     <td className="mono text-right">{g.miles.toFixed(1)} mi</td>
    </tr>))}</tbody></table>
   );
   case "qb": return (
    <table><thead><tr><th>Invoice #</th><th>Customer</th><th className="text-right">Amount</th><th className="text-right">Paid</th><th className="text-right">Due</th><th>Status</th><th>QB Sync</th></tr></thead>
    <tbody>{QB_INVOICES.map(i=>(<tr key={i.id}>
     <td className="mono text-cyan">{i.id}</td><td style={{fontWeight:500,fontSize:12}}>{i.customer}</td>
     <td className="mono text-right">${i.amount.toLocaleString()}</td>
     <td className="mono text-right text-green">${i.paid.toLocaleString()}</td>
     <td className={`mono text-right ${i.due>0?"text-amber":"text-green"}`}>${i.due.toLocaleString()}</td>
     <td><span className={`badge ${i.status==="Partial"?"badge-amber":i.status==="Unpaid"?"badge-red":i.status==="Paid"?"badge-green":"badge-gray"}`}>{i.status}</span></td>
     <td>{i.qbSynced?<span className="badge badge-green">● Synced</span>:<span className="badge badge-red">○ Pending</span>}</td>
    </tr>))}</tbody></table>
   );
   case "xa": return (
    <table><thead><tr><th>Job #</th><th>Customer</th><th>Type</th><th>Xact ID</th><th>Phase</th><th className="text-right">Estimate</th><th>Status</th></tr></thead>
    <tbody>{jobs.map(j=>(<tr key={j.id}>
     <td className="mono text-cyan">{j.id}</td><td style={{fontWeight:500,fontSize:12}}>{j.customer}</td>
     <td style={{fontSize:11}}>{j.type}</td>
     <td className="mono text-sm">{j.xact}</td>
     <td><span className="badge badge-cyan" style={{fontSize:9}}>{j.phase}</span></td>
     <td className="mono text-right">${computeJobTotal(j).toLocaleString()}</td>
     <td>{j.xact==="Pending"?<span className="badge badge-amber">Awaiting</span>:<span className="badge badge-green">Submitted</span>}</td>
    </tr>))}</tbody></table>
   );
   case "equip": {
    const all = INVENTORY.flatMap(i => (i.deployments||[]).map(d => ({...d, item:i.item, invId:i.id})));
    return (
     <table><thead><tr><th>Item</th><th className="text-right">Qty</th><th>Job #</th><th>Customer</th><th>Address</th><th>Deployed</th><th>Lead Tech</th></tr></thead>
     <tbody>{all.map((d,i)=>(<tr key={i}>
      <td style={{fontWeight:500,fontSize:12}}>{d.item}<div className="mono text-sm" style={{color:"var(--text3)"}}>{d.invId}</div></td>
      <td className="mono text-right text-amber" style={{fontWeight:600}}>{d.qty}</td>
      <td className="mono text-cyan">{d.jobId}</td>
      <td style={{fontSize:12}}>{d.customer}</td>
      <td style={{fontSize:11,color:"var(--text2)"}}>{d.address}</td>
      <td className="mono text-sm">{d.deployedAt}</td>
      <td style={{fontSize:11}}>{d.tech}</td>
     </tr>))}</tbody></table>
    );
   }
   case "kpi": {
    const kpis = [
     ["Total Customers", CUSTOMERS.length, "+12.4%"],
     ["Total Active Jobs", jobs.filter(j=>j.status==="Active").length, "+8.2%"],
     ["Emergency Jobs Open", jobs.filter(j=>j.priority==="Emergency"&&j.status!=="Completed").length, "-9.1%"],
     ["Total Contracted", `$${jobs.reduce((a,j)=>a+computeJobTotal(j),0).toLocaleString()}`, "+18.7%"],
     ["Outstanding A/R", `$${jobs.reduce((a,j)=>a+j.balance,0).toLocaleString()}`, "-4.2%"],
     ["Avg Job Value", `$${Math.round(jobs.reduce((a,j)=>a+computeJobTotal(j),0)/jobs.length).toLocaleString()}`, "+6.1%"],
     ["Avg Response Time", "22 min", "-12.0%"],
     ["Job Satisfaction Score", "4.8 / 5", "+1.5%"],
     ["Tech Avg Hours YTD", Math.round(TECHNICIANS.reduce((a,t)=>a+t.hours,0)/TECHNICIANS.length), "+8.0%"],
     ["Inventory Value", `$${INVENTORY.reduce((a,i)=>a+i.cost*i.total,0).toLocaleString()}`, "—"],
    ];
    return (
     <table><thead><tr><th>Metric</th><th className="text-right">Value</th><th>MoM Change</th></tr></thead>
     <tbody>{kpis.map(([k,v,t],i)=>(<tr key={i}>
      <td style={{fontWeight:500,fontSize:12}}>{k}</td>
      <td className="mono text-right text-cyan" style={{fontSize:14,fontWeight:600}}>{v}</td>
      <td><span className={`badge ${t.startsWith("+")?"badge-green":t.startsWith("-")?"badge-red":"badge-gray"}`}>{t}</span></td>
     </tr>))}</tbody></table>
    );
   }
   case "loc": return (
    <table><thead><tr><th>ID</th><th>Location</th><th>Address</th><th className="text-right">Active Jobs</th><th className="text-right">Techs On-Site</th><th>Status</th></tr></thead>
    <tbody>{LOCATIONS.map(l=>{
     const jobsAtLoc = jobs.filter(j=>j.location.includes(l.address.split(",")[0])||l.address.includes(j.location)).length;
     const techsAtLoc = TECHNICIANS.filter(t=>t.location.includes(l.address.split(",")[0])||l.address.includes(t.location)).length;
     return (<tr key={l.id}>
      <td className="mono text-cyan text-sm">L-{String(l.id).padStart(3,"0")}</td>
      <td style={{fontWeight:500}}>{l.name}</td>
      <td style={{fontSize:11,color:"var(--text2)"}}>{l.address}</td>
      <td className="mono text-right">{jobsAtLoc}</td>
      <td className="mono text-right">{techsAtLoc}</td>
      <td>{l.active?<span className="badge badge-green">Active</span>:<span className="badge badge-gray">Inactive</span>}</td>
     </tr>);
    })}</tbody></table>
   );
   case "exc": return (
    <table><thead><tr><th>Alert ID</th><th>Severity</th><th>Time</th><th>Category</th><th>Message</th><th>Related Job</th></tr></thead>
    <tbody>{ALERTS.map(a=>(<tr key={a.id}>
     <td className="mono text-cyan text-sm">{a.id}</td>
     <td><span className={`badge ${a.severity==="High"?"badge-red":a.severity==="Medium"?"badge-amber":"badge-gray"}`}>{a.severity}</span></td>
     <td className="mono text-sm">{a.time}</td>
     <td><span className="badge badge-cyan" style={{fontSize:9}}>{a.category}</span></td>
     <td style={{fontSize:12}}>{a.message}</td>
     <td className="mono text-sm text-cyan">{a.job}</td>
    </tr>))}</tbody></table>
   );
   default: return null;
  }
 };

 return (
  <div>
   <AdvancedFilterPanel onApply={setAdvFilters}/>
   <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
    <div>
     <div style={{fontFamily:"var(--rajdhani)",fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:4}}>One-Touch Reporting</div>
     <div style={{fontSize:12,color:"var(--text3)"}}>Click any report to preview live seed data. Each card pulls from the linked system module.{Object.keys(advFilters).length>0?` · ${Object.keys(advFilters).length} active filter(s)`:""}</div>
    </div>
    <div style={{display:"flex",gap:8}}>
     <button className="btn btn-ghost" style={{fontSize:11}}>Schedule Reports</button>
     <button className="btn btn-amber" style={{fontSize:11}}>Export All</button>
    </div>
   </div>
   <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12,marginBottom:20}}>
    {reports.map(r=>(
     <div key={r.type} className={`report-btn ${generated===r.label?"active":""}`} onClick={()=>setGenerated(generated===r.label?null:r.label)} style={generated===r.label?{borderColor:"var(--cyan)",background:"rgba(204,0,0,.08)"}:{}}>
      <div className="label">{r.label}</div><div className="sub">{r.sub}</div>
      <div style={{marginTop:8}} onClick={e=>e.stopPropagation()}><PinPrintSendButtons reportKey={r.type} reportName={r.label}/></div>
     </div>
    ))}
   </div>
   {generated && (
    <div className="card">
     <div className="card-header" style={{flexWrap:"wrap",gap:10}}>
      <span className="card-title">📊 {generated}</span>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
       <PinPrintSendButtons reportKey={(reports.find(r=>r.label===generated)||{}).type||generated} reportName={generated}/>
       <button className="btn btn-ghost" style={{fontSize:11}} onClick={handlePDF}>Download PDF</button>
       <button className="btn btn-ghost" style={{fontSize:11}} onClick={handleCSV}>Download CSV</button>
       <button className="btn btn-ghost" style={{fontSize:11}} onClick={handleEmail}>Email Report</button>
       <button className="btn btn-outline" style={{fontSize:11}} onClick={()=>setGenerated(null)}>Close</button>
      </div>
     </div>
     {renderPreview()}
    </div>
   )}
  </div>
 );
}

function LocationsPage() {
 return (
 <div>
 <div className="kpi-row" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
 <KPI label="Total Locations" value="5" sub="Max 5 supported" color="cyan"/>
 <KPI label="Active Locations" value="4" color="green" accent="var(--green)"/>
 <KPI label="Inactive" value="1" color="amber" accent="var(--amber)"/>
 </div>
 <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
 {LOCATIONS.map(loc=>(
 <div key={loc.id} className="card">
 <div className="card-header"><span className="card-title" style={{fontSize:14}}>Location {loc.id}</span><StatusBadge status={loc.active?"Active":"Inactive"}/></div>
 <div className="card-body">
 <div style={{fontWeight:600,marginBottom:4}}>{loc.name}</div>
 <div style={{fontSize:12,color:"var(--text3)",marginBottom:12}}>{loc.address}</div>
 <div className="stat-row"><span className="text-sm">Techs Assigned</span><span className="mono text-cyan">{[2,2,1,1,2][loc.id-1]}</span></div>
 <div className="stat-row"><span className="text-sm">Active Jobs</span><span className="mono text-amber">{[2,1,1,1,0][loc.id-1]}</span></div>
 <div style={{display:"flex",gap:8,marginTop:12}}>
 <button className="btn btn-outline" style={{flex:1,fontSize:11}}>View Jobs</button>
 <button className="btn btn-ghost" style={{flex:1,fontSize:11}}>Manage</button>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 );
}

function PhotoUploadButton({ locked, onAdd }) {
 const inputRef = useRef(null);
 const [busy, setBusy] = useState(false);
 const compress = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
   const img = new Image();
   img.onerror = reject;
   img.onload = () => {
    const max = 1280;
    let { width:w, height:h } = img;
    if (w > max || h > max) { const r = Math.min(max/w, max/h); w = Math.round(w*r); h = Math.round(h*r); }
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    c.getContext("2d").drawImage(img, 0, 0, w, h);
    resolve(c.toDataURL("image/jpeg", 0.78));
   };
   img.src = reader.result;
  };
  reader.readAsDataURL(file);
 });
 const handle = async (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;
  setBusy(true);
  try {
   const out = [];
   for (const f of files) {
    const dataUrl = await compress(f);
    out.push({ id: `P-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, url: dataUrl, caption: f.name.replace(/\.[^.]+$/,""), uploadedAt: new Date().toISOString().slice(0,10) });
   }
   onAdd(out);
  } catch (err) { alert("Upload failed: "+err.message); }
  setBusy(false);
  if (inputRef.current) inputRef.current.value = "";
 };
 return (
  <>
   <input ref={inputRef} type="file" accept="image/*" multiple capture="environment" style={{display:"none"}} onChange={handle} disabled={locked||busy} data-testid="photo-input"/>
   <button className="btn btn-ghost" disabled={locked||busy} onClick={()=>inputRef.current?.click()} style={{fontSize:10,padding:"5px 10px"}}>{busy?"Uploading…":"📸 + Add Photo"}</button>
  </>
 );
}

function NewJobModal({ open, onClose, onCreate, user, defaultWorkspaceId, existingIds = [] }) {
 // Round 13 — Expanded New Job form per 10-fix request
 const nextJobNumber = useMemo(() => {
  let maxN = 2049;
  for (const id of existingIds) { const m = String(id || "").match(/J-(\d+)/); if (m) { const n = parseInt(m[1], 10); if (!Number.isNaN(n) && n > maxN) maxN = n; } }
  return `J-${maxN + 1}`;
 }, [existingIds, open]);

 const EMPTY = {
  jobNumber: "",
  firstName: "", lastName: "", companyName: "",
  propertyAddress: "", cityStateZip: "",
  customerPhone: "", customerEmail: "",
  secondaryName: "", secondaryPhone: "",
  type: "Water Mitigation", typeOther: "",
  priority: "Normal",
  leadTech: "",
  dateOfLoss: "",
  causeOfLoss: "",
  description: "",
  insurer: "", claimNumber: "", policyNumber: "",
  adjuster: "", adjusterPhone: "",
  estimate: "",
  specialInstructions: "",
  source: "Phone Call", sourceOther: "",
 };
 const [form, setForm] = useState(EMPTY);
 const [busy, setBusy] = useState(false);
 const [dirty, setDirty] = useState(false);
 useEffect(() => {
  if (open) { setForm({ ...EMPTY, jobNumber: nextJobNumber, leadTech: TECHNICIANS[0]?.name || "" }); setDirty(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [open, nextJobNumber]);
 // Warn on unload if dirty
 useEffect(() => {
  if (!open) return;
  const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
  window.addEventListener("beforeunload", handler);
  return () => window.removeEventListener("beforeunload", handler);
 }, [open, dirty]);

 const upd = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };
 const tryClose = () => { if (dirty && !window.confirm("You have unsaved changes. Discard them and close this form?")) return; onClose(); };

 if (!open) return null;
 const REQUIRED = ["firstName", "lastName", "propertyAddress", "cityStateZip", "customerPhone", "customerEmail"];
 const missingRequired = REQUIRED.filter(k => !String(form[k] || "").trim());

 const buildJob = () => {
  const wsId = user?.role === "owner" ? defaultWorkspaceId : (user?.workspace_id || "1");
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const yy = String(today.getFullYear()).slice(-2);
  const fullCustomer = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
  const typeFinal = form.type === "Other" && form.typeOther.trim() ? form.typeOther.trim() : form.type;
  const sourceFinal = form.source === "Other" && form.sourceOther.trim() ? form.sourceOther.trim() : form.source;
  const fullAddress = `${form.propertyAddress}${form.cityStateZip ? ", " + form.cityStateZip : ""}`.trim();
  return {
   id: form.jobNumber.trim() || nextJobNumber,
   customer: fullCustomer || "(Unnamed Customer)",
   firstName: form.firstName.trim(), lastName: form.lastName.trim(),
   companyName: form.companyName.trim(),
   type: typeFinal,
   status: "Pending",
   priority: form.priority,
   phase: "Intake",
   tech: form.leadTech,
   location: fullAddress,
   propertyAddress: form.propertyAddress.trim(),
   cityStateZip: form.cityStateZip.trim(),
   started: `${mm}/${dd}/${yy}`,
   estimate: parseFloat(form.estimate) || 0, billed: 0, paid: 0,
   balance: parseFloat(form.estimate) || 0,
   xact: "Pending", divisions: [], equipment: [], moisture: "Pending",
   addOns: [], calls: [], photos: [],
   misc_items: [],
   workspace_id: String(wsId),
   issueDescription: form.description.trim(), moldReport: "", moistureMapping: "",
   insurer: form.insurer.trim(), adjuster: form.adjuster.trim(), adjusterPhone: form.adjusterPhone.trim(),
   claimNumber: form.claimNumber.trim(), policyNumber: form.policyNumber.trim(),
   customerPhone: form.customerPhone.trim(), customerEmail: form.customerEmail.trim(),
   secondaryContactName: form.secondaryName.trim(), secondaryContactPhone: form.secondaryPhone.trim(),
   causeOfLoss: form.causeOfLoss.trim(),
   dateOfLoss: form.dateOfLoss || `${mm}/${dd}/${yy}`,
   specialInstructions: form.specialInstructions.trim(),
   source: sourceFinal,
   permit: { needed: false, type: "", link: "", documentName: "", status: "Not Required", filedAt: null, notes: "" },
  };
 };

 const submit = (e, openAfter = false) => {
  if (e) e.preventDefault();
  if (missingRequired.length) { alert("Please fill required fields: " + missingRequired.join(", ")); return; }
  setBusy(true);
  const newJob = buildJob();
  onCreate(newJob);
  setBusy(false); setDirty(false);
  if (openAfter && typeof window !== "undefined") { setTimeout(() => { window.location.hash = `#jobs/${encodeURIComponent(newJob.id)}`; }, 50); }
  onClose();
 };

 const lblDark = { fontSize: 11, fontWeight: 600, color: "#2C2C2C", marginBottom: 4, display: "block", letterSpacing: 0.3 };
 const inDark = { width: "100%", padding: "7px 10px", border: "1px solid #c8c8c8", borderRadius: 4, fontSize: 12, color: "#2C2C2C", fontWeight: 500, background: "#fff" };
 const Field = ({ label, required, children }) => (
  <div className="form-group" style={{ marginBottom: 10 }}>
   <div style={lblDark}>{label}{required && <span style={{ color: "#C0392B" }}> *</span>}</div>
   {children}
  </div>
 );
 const JOB_TYPES = ["Water Mitigation", "Mold Remediation", "Fire and Smoke", "Roofing", "Contents", "Trauma", "Inspection", "Other"];
 const PRIORITIES = ["Emergency", "High", "Normal", "Low"];
 const SOURCES = ["Phone Call", "Walk In", "Website", "Referral", "Apex Response Management", "Apex IntelliVoice", "Other"];

 return (
  <div className="modal-overlay" onClick={tryClose} data-testid="new-job-modal">
   <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <div className="modal-header" style={{ background: "#f5f5f5", borderBottom: "1px solid #d8d8d8" }}>
     <button className="btn btn-ghost" onClick={tryClose} data-testid="new-job-back" style={{ fontSize: 11, color: "#2C2C2C", fontWeight: 600, marginRight: 10 }}>← Back</button>
     <div style={{ fontFamily: "var(--rajdhani)", fontSize: 17, fontWeight: 700, color: "#2C2C2C", letterSpacing: 1, flex: 1 }}>+ New Job</div>
     {dirty && <span data-testid="new-job-dirty" style={{ fontSize: 11, fontWeight: 600, color: "#9a5b00", background: "#fff3c4", padding: "4px 10px", borderRadius: 4, marginRight: 8 }}>● Unsaved changes</span>}
     <button className="icon-btn" onClick={tryClose} style={{ color: "#2C2C2C" }}>×</button>
    </div>
    <form onSubmit={e => submit(e, false)} style={{ padding: 20, overflowY: "auto", flex: 1, background: "#fff" }}>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
      <Field label="Job Number"><input style={inDark} value={form.jobNumber} onChange={e => upd("jobNumber", e.target.value)} data-testid="njf-jobnum" /></Field>
      <Field label="First Name" required><input style={inDark} value={form.firstName} onChange={e => upd("firstName", e.target.value)} required data-testid="njf-first" /></Field>
      <Field label="Last Name" required><input style={inDark} value={form.lastName} onChange={e => upd("lastName", e.target.value)} required data-testid="njf-last" /></Field>
     </div>
     <Field label="Company Name"><input style={inDark} value={form.companyName} onChange={e => upd("companyName", e.target.value)} placeholder="(optional)" data-testid="njf-company" /></Field>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Field label="Property Address" required><input style={inDark} value={form.propertyAddress} onChange={e => upd("propertyAddress", e.target.value)} required data-testid="njf-address" /></Field>
      <Field label="City, State ZIP" required><input style={inDark} value={form.cityStateZip} onChange={e => upd("cityStateZip", e.target.value)} required placeholder="Austin, TX 78704" data-testid="njf-csz" /></Field>
     </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Field label="Customer Phone" required><input style={inDark} value={form.customerPhone} onChange={e => upd("customerPhone", e.target.value)} required type="tel" data-testid="njf-phone" /></Field>
      <Field label="Customer Email" required><input style={inDark} value={form.customerEmail} onChange={e => upd("customerEmail", e.target.value)} required type="email" data-testid="njf-email" /></Field>
     </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Field label="Secondary Contact Name"><input style={inDark} value={form.secondaryName} onChange={e => upd("secondaryName", e.target.value)} data-testid="njf-sec-name" /></Field>
      <Field label="Secondary Contact Phone"><input style={inDark} value={form.secondaryPhone} onChange={e => upd("secondaryPhone", e.target.value)} type="tel" data-testid="njf-sec-phone" /></Field>
     </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
      <Field label="Job Type">
       <select style={inDark} value={form.type} onChange={e => upd("type", e.target.value)} data-testid="njf-type">
        {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
       </select>
       {form.type === "Other" && <input style={{ ...inDark, marginTop: 6 }} value={form.typeOther} onChange={e => upd("typeOther", e.target.value)} placeholder="Specify other type" data-testid="njf-type-other" />}
      </Field>
      <Field label="Priority">
       <select style={inDark} value={form.priority} onChange={e => upd("priority", e.target.value)} data-testid="njf-priority">
        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
       </select>
      </Field>
      <Field label="Lead Technician">
       <select style={inDark} value={form.leadTech} onChange={e => upd("leadTech", e.target.value)} data-testid="njf-tech">
        {TECHNICIANS.map(t => <option key={t.id} value={t.name}>{t.name} ({t.role})</option>)}
       </select>
      </Field>
     </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Field label="Date of Loss"><input style={inDark} type="date" value={form.dateOfLoss} onChange={e => upd("dateOfLoss", e.target.value)} data-testid="njf-dol" /></Field>
      <Field label="Cause of Loss"><input style={inDark} value={form.causeOfLoss} onChange={e => upd("causeOfLoss", e.target.value)} placeholder="e.g. Burst supply line" data-testid="njf-cause" /></Field>
     </div>
     <Field label="Description of Job">
      <textarea style={{ ...inDark, minHeight: 90, fontFamily: "inherit" }} value={form.description} onChange={e => upd("description", e.target.value)} placeholder="Detailed notes about the job…" data-testid="njf-desc" />
     </Field>
     <div style={{ marginTop: 8, padding: "10px 12px", border: "1px solid #d8d8d8", borderRadius: 6, background: "#fafafa" }}>
      <div style={{ fontFamily: "var(--rajdhani)", fontSize: 13, fontWeight: 700, color: "#2C2C2C", marginBottom: 8, letterSpacing: 0.5 }}>Insurance & Claim</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
       <Field label="Insurance Carrier"><input style={inDark} value={form.insurer} onChange={e => upd("insurer", e.target.value)} data-testid="njf-insurer" /></Field>
       <Field label="Claim Number"><input style={inDark} value={form.claimNumber} onChange={e => upd("claimNumber", e.target.value)} data-testid="njf-claim" /></Field>
       <Field label="Policy Number"><input style={inDark} value={form.policyNumber} onChange={e => upd("policyNumber", e.target.value)} data-testid="njf-policy" /></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
       <Field label="Adjuster Name"><input style={inDark} value={form.adjuster} onChange={e => upd("adjuster", e.target.value)} data-testid="njf-adj" /></Field>
       <Field label="Adjuster Phone"><input style={inDark} value={form.adjusterPhone} onChange={e => upd("adjusterPhone", e.target.value)} type="tel" data-testid="njf-adj-phone" /></Field>
      </div>
     </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
      <Field label="Estimated Job Value ($)"><input style={inDark} value={form.estimate} onChange={e => upd("estimate", e.target.value)} type="number" min={0} data-testid="njf-est" /></Field>
      <Field label="Source of Job">
       <select style={inDark} value={form.source} onChange={e => upd("source", e.target.value)} data-testid="njf-source">
        {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
       </select>
       {form.source === "Other" && <input style={{ ...inDark, marginTop: 6 }} value={form.sourceOther} onChange={e => upd("sourceOther", e.target.value)} placeholder="Specify source" data-testid="njf-source-other" />}
      </Field>
     </div>
     <Field label="Special Instructions">
      <textarea style={{ ...inDark, minHeight: 70, fontFamily: "inherit" }} value={form.specialInstructions} onChange={e => upd("specialInstructions", e.target.value)} data-testid="njf-special" />
     </Field>
     {user?.role === "owner" && (
      <div style={{ padding: "8px 12px", background: "#f5f5f5", borderRadius: 4, border: "1px solid #d8d8d8", marginBottom: 8 }}>
       <div style={{ fontSize: 10, color: "#555", fontWeight: 600, marginBottom: 2 }}>WORKSPACE</div>
       <div className="mono text-sm" style={{ color: "#2C2C2C", fontWeight: 600 }}>{LOCATIONS.find(l => String(l.id) === String(defaultWorkspaceId))?.name || `WS-${defaultWorkspaceId}`}</div>
      </div>
     )}
     <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14, paddingTop: 14, borderTop: "1px solid #e6e6e6" }}>
      <button type="button" className="btn btn-ghost" onClick={tryClose} data-testid="njf-cancel" style={{ fontSize: 12, color: "#2C2C2C", fontWeight: 600 }}>Cancel</button>
      <button type="button" className="btn btn-outline" disabled={busy} onClick={() => submit(null, true)} data-testid="njf-save-open" style={{ fontSize: 12 }}>{busy ? "…" : "Save and Open Job"}</button>
      <button type="submit" disabled={busy} data-testid="njf-save" className="btn btn-primary" style={{ fontSize: 12 }}>{busy ? "…" : "Save Job"}</button>
     </div>
    </form>
   </div>
  </div>
 );
}

function TeamPage() {
 const [members, setMembers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showInvite, setShowInvite] = useState(false);
 const [form, setForm] = useState({name:"",email:"",password:"",workspace_id:"1"});
 const [err, setErr] = useState("");
 const [busy, setBusy] = useState(false);

 const load = async () => {
  setLoading(true);
  try { const r = await apiFetch(`${FS_API}/members`); if(r.ok){ const d = await r.json(); setMembers(d.members||[]); } } catch {}
  setLoading(false);
 };
 useEffect(()=>{ load(); },[]);

 const invite = async (e) => {
  e.preventDefault(); setErr(""); setBusy(true);
  try {
   const r = await apiFetch(`${FS_API}/members`, {method:"POST", body: JSON.stringify(form)});
   const d = await r.json();
   if(!r.ok){ setErr(typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail)); }
   else { setShowInvite(false); setForm({name:"",email:"",password:"",workspace_id:"1"}); load(); }
  } catch(e) { setErr(e.message); }
  setBusy(false);
 };
 const remove = async (id, email) => {
  if(!window.confirm(`Remove ${email}? They will immediately lose access.`)) return;
  await apiFetch(`${FS_API}/members/${id}`, {method:"DELETE"});
  load();
 };
 const visible = members.filter(m => m.email && (m.role === "owner" || m.role === "member") && !m.email.startsWith("TEST_") && !m.email.startsWith("e2e_"));

 return (
  <div>
   <div className="kpi-row" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
    <KPI label="Total Members" value={visible.length} color="cyan"/>
    <KPI label="Owners" value={visible.filter(m=>m.role==="owner").length} color="green" accent="var(--green)"/>
    <KPI label="Members" value={visible.filter(m=>m.role==="member").length} color="cyan"/>
    <KPI label="Workspaces in Use" value={new Set(visible.map(m=>m.workspace_id).filter(Boolean)).size} color="amber" accent="var(--amber)"/>
   </div>
   <div className="card">
    <div className="card-header">
     <span className="card-title">Team Members</span>
     <div style={{display:"flex",gap:8}}>
      <button className="btn btn-ghost" onClick={load} style={{fontSize:11}}>↻ Refresh</button>
      <button className="btn btn-primary" data-testid="invite-btn" onClick={()=>setShowInvite(true)} style={{fontSize:11}}>+ Invite Member</button>
     </div>
    </div>
    {loading && <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:12}}>Loading…</div>}
    {!loading && (
     <table><thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Workspace</th><th>Joined</th><th></th></tr></thead>
      <tbody>{visible.map(m=>{
       const ws = LOCATIONS.find(l=>String(l.id)===String(m.workspace_id));
       return (<tr key={m.user_id}>
        <td style={{fontWeight:500,fontSize:12}}>{m.email}</td>
        <td style={{fontSize:12}}>{m.name||"—"}</td>
        <td><span className={`badge ${m.role==="owner"?"badge-green":"badge-cyan"}`} style={{fontSize:9}}>{m.role}</span></td>
        <td style={{fontSize:11}}>{m.role==="owner" ? <span className="text-green">All workspaces</span> : ws ? `${ws.name}` : `WS-${m.workspace_id||"?"}`}</td>
        <td className="mono text-sm" style={{color:"var(--text3)"}}>{(m.created_at||"").slice(0,10)}</td>
        <td style={{textAlign:"right"}}>
         {m.role !== "owner" && <button className="btn btn-ghost" onClick={()=>remove(m.user_id,m.email)} style={{fontSize:10,padding:"3px 8px",color:"var(--red)"}}>Remove</button>}
        </td>
       </tr>);
      })}</tbody></table>
    )}
   </div>

   {showInvite && (
    <div className="modal-overlay" onClick={()=>setShowInvite(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-header">
       <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>+ Invite New Member</div>
       <button className="icon-btn" onClick={()=>setShowInvite(false)}>×</button>
      </div>
      <form onSubmit={invite} style={{padding:18}}>
       <div className="form-group"><div className="form-lbl">Full Name</div><input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required style={{width:"100%"}}/></div>
       <div className="form-group"><div className="form-lbl">Email</div><input className="form-control" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required style={{width:"100%"}}/></div>
       <div className="form-group"><div className="form-lbl">Temp Password (share with them; they can change it later)</div><input className="form-control" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required minLength={6} placeholder="6+ characters" style={{width:"100%"}}/></div>
       <div className="form-group"><div className="form-lbl">Assigned Workspace</div>
        <select className="form-control" value={form.workspace_id} onChange={e=>setForm({...form,workspace_id:e.target.value})} style={{width:"100%"}}>
         {LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name} — {l.address}</option>)}
        </select>
       </div>
       {err && <div style={{marginBottom:12,padding:"8px 12px",background:"rgba(204,0,0,.12)",border:"1px solid rgba(204,0,0,.35)",borderRadius:4,fontSize:11,color:"#ff5555"}}>{err}</div>}
       <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:6}}>
        <button type="button" className="btn btn-outline" onClick={()=>setShowInvite(false)} style={{fontSize:11}}>Cancel</button>
        <button type="submit" disabled={busy} className="btn btn-primary" style={{fontSize:11}}>{busy?"…":"Create Member"}</button>
       </div>
      </form>
     </div>
    </div>
   )}
   <InviteHistory/>
  </div>
 );
}

function InviteHistory() {
 const [invites, setInvites] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showCreate, setShowCreate] = useState(false);
 const [form, setForm] = useState({email:"",name:"",workspace_id:"1",note:""});
 const [created, setCreated] = useState(null);
 const [err, setErr] = useState("");
 const load = async () => {
  setLoading(true);
  try { const r = await apiFetch(`${FS_API}/invites`); if(r.ok){ const d=await r.json(); setInvites(d.invites||[]); } } catch {}
  setLoading(false);
 };
 useEffect(()=>{ load(); },[]);
 const create = async (e) => {
  e.preventDefault(); setErr("");
  try {
   const r = await apiFetch(`${FS_API}/invites`, {method:"POST", body: JSON.stringify(form)});
   const d = await r.json();
   if(!r.ok){ setErr(typeof d.detail==="string"?d.detail:JSON.stringify(d.detail)); return; }
   setCreated(d.invite); setForm({email:"",name:"",workspace_id:"1",note:""}); load();
  } catch(e){ setErr(e.message); }
 };
 const revoke = async (id) => { if(!window.confirm("Revoke this invite link?")) return; await apiFetch(`${FS_API}/invites/${id}`, {method:"DELETE"}); load(); };
 const inviteUrl = (code) => `${window.location.origin}/?invite=${code}`;
 const pending = invites.filter(i=>i.status==="pending");
 return (
  <div className="card" style={{marginTop:20}}>
   <div className="card-header">
    <span className="card-title">📧 Invite History {pending.length>0 && <span className="badge badge-amber" style={{marginLeft:8}}>{pending.length} pending</span>}</span>
    <div style={{display:"flex",gap:8}}>
     <button className="btn btn-ghost" onClick={load} style={{fontSize:11}}>↻ Refresh</button>
     <button className="btn btn-primary" data-testid="create-invite" onClick={()=>{setShowCreate(true);setCreated(null);}} style={{fontSize:11}}>+ Send Invite Link</button>
    </div>
   </div>
   {loading && <div style={{padding:16,textAlign:"center",color:"var(--text3)",fontSize:12}}>Loading…</div>}
   {!loading && invites.length===0 && <div style={{padding:16,textAlign:"center",color:"var(--text3)",fontSize:11}}>No invites yet. Click "Send Invite Link" to share a signup link with a teammate.</div>}
   {!loading && invites.length>0 && (
    <table><thead><tr><th>Email</th><th>Workspace</th><th>Status</th><th>Sent</th><th>Invite Link</th><th></th></tr></thead>
     <tbody>{invites.map(i=>{
      const ws = LOCATIONS.find(l=>String(l.id)===String(i.workspace_id));
      return (<tr key={i.id}>
       <td style={{fontSize:12,fontWeight:500}}>{i.email}{i.name && <div className="text-sm" style={{color:"var(--text3)"}}>{i.name}</div>}</td>
       <td style={{fontSize:11}}>{ws?ws.name:`WS-${i.workspace_id}`}</td>
       <td><span className={`badge ${i.status==="pending"?"badge-amber":i.status==="accepted"?"badge-green":"badge-gray"}`}>{i.status}</span></td>
       <td className="mono text-sm" style={{color:"var(--text3)"}}>{(i.created_at||"").slice(0,10)}</td>
       <td>
        {i.status==="pending" ? <code style={{fontSize:10,padding:"3px 6px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:3,color:"var(--cyan)"}}>{inviteUrl(i.code).slice(0,42)}…</code>
         : i.status==="accepted" ? <span className="text-sm text-green">Accepted {(i.accepted_at||"").slice(0,10)}</span>
         : <span className="text-sm" style={{color:"var(--text3)"}}>—</span>}
       </td>
       <td style={{textAlign:"right",whiteSpace:"nowrap"}}>
        {i.status==="pending" && <>
         <button className="btn btn-ghost" onClick={()=>{navigator.clipboard.writeText(inviteUrl(i.code));alert("Invite link copied to clipboard");}} style={{fontSize:10,padding:"3px 8px",marginRight:4}}>Copy</button>
         <button className="btn btn-ghost" onClick={()=>{ const subject=`Join Apex Business Intelligence — ${ws?ws.name:"team"}`; const body=`Hi ${i.name||""},\n\nYou've been invited to join Apex Business Intelligence. Click this link to set your password and sign in:\n\n${inviteUrl(i.code)}\n\n— Apex Owner`; window.location.href=`mailto:${i.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`; }} style={{fontSize:10,padding:"3px 8px",marginRight:4}}>Email</button>
         <button className="btn btn-ghost" onClick={()=>revoke(i.id)} style={{fontSize:10,padding:"3px 8px",color:"var(--red)"}}>Revoke</button>
        </>}
       </td>
      </tr>);
     })}</tbody></table>
   )}

   {showCreate && (
    <div className="modal-overlay" onClick={()=>setShowCreate(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-header">
       <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>+ Send Invite Link</div>
       <button className="icon-btn" onClick={()=>setShowCreate(false)}>×</button>
      </div>
      <div style={{padding:18}}>
       {!created ? (
        <form onSubmit={create}>
         <div className="form-group"><div className="form-lbl">Email *</div><input className="form-control" data-testid="invite-email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required style={{width:"100%"}}/></div>
         <div className="form-group"><div className="form-lbl">Name (optional)</div><input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} style={{width:"100%"}}/></div>
         <div className="form-group"><div className="form-lbl">Workspace</div>
          <select className="form-control" value={form.workspace_id} onChange={e=>setForm({...form,workspace_id:e.target.value})} style={{width:"100%"}}>
           {LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </select>
         </div>
         <div className="form-group"><div className="form-lbl">Note (optional)</div><input className="form-control" value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="e.g., New foreman starting Monday" style={{width:"100%"}}/></div>
         {err && <div style={{marginBottom:12,padding:"8px 12px",background:"rgba(204,0,0,.12)",border:"1px solid rgba(204,0,0,.35)",borderRadius:4,fontSize:11,color:"#ff5555"}}>{err}</div>}
         <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button type="button" className="btn btn-outline" onClick={()=>setShowCreate(false)} style={{fontSize:11}}>Cancel</button>
          <button type="submit" data-testid="invite-submit" className="btn btn-primary" style={{fontSize:11}}>Generate Link</button>
         </div>
        </form>
       ) : (
        <div>
         <div style={{padding:"10px 14px",background:"rgba(34,197,94,.1)",border:"1px solid rgba(34,197,94,.4)",borderRadius:6,marginBottom:14,fontSize:11,color:"#22c55e"}}>✓ Invite created for {created.email}. Share this link:</div>
         <div style={{padding:"10px 12px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,wordBreak:"break-all",fontFamily:"var(--mono)",fontSize:11,color:"var(--cyan)",marginBottom:14}}>{inviteUrl(created.code)}</div>
         <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn btn-ghost" onClick={()=>{navigator.clipboard.writeText(inviteUrl(created.code));alert("Copied");}} style={{fontSize:11}}>Copy Link</button>
          <button className="btn btn-ghost" onClick={()=>{ const subject=`Join Apex Business Intelligence`; const body=`You've been invited to join. Set your password here:\n\n${inviteUrl(created.code)}`; window.location.href=`mailto:${created.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`; }} style={{fontSize:11}}>Email It</button>
          <button className="btn btn-primary" onClick={()=>setShowCreate(false)} style={{fontSize:11}}>Done</button>
         </div>
        </div>
       )}
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

// ─── PAYROLL ──────────────────────────────────────────────────────────────────
function PayrollPage() {
 const [emps, setEmps] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showAdd, setShowAdd] = useState(false);
 const [showImport, setShowImport] = useState(false);
 const [csvText, setCsvText] = useState("");
 const [form, setForm] = useState({full_name:"",job_title:"",department:"Field Ops",employment_type:"Full-Time",workspace_id:"1",city:"",zip:"",phone:"",email:"",pay_type:"Hourly",pay_rate:0,hours_per_week:40,hire_date:"",notes:"",linked_team_member_id:null,linked_team_member_email:null});
 const [err, setErr] = useState("");
 const [filterWs, setFilterWs] = useState("all");

 const load = async () => { setLoading(true); try { const r=await apiFetch(`${FS_API}/employees`); if(r.ok){ const d=await r.json(); setEmps(d.employees||[]); } } catch {} setLoading(false); };
 const seedDemo = async () => { if(!window.confirm("Reset employees + finance + sales + leads + goals to demo seed data?")) return; await apiFetch(`${FS_API}/biz/seed`, {method:"POST"}); load(); };
 useEffect(()=>{ load(); },[]);
 const submitAdd = async (e) => {
  e.preventDefault(); setErr("");
  const r = await apiFetch(`${FS_API}/employees`, {method:"POST", body: JSON.stringify(form)});
  const d = await r.json();
  if(!r.ok){ setErr(typeof d.detail==="string"?d.detail:JSON.stringify(d.detail)); return; }
  setShowAdd(false); setForm({full_name:"",job_title:"",department:"Field Ops",employment_type:"Full-Time",workspace_id:"1",city:"",zip:"",phone:"",email:"",pay_type:"Hourly",pay_rate:0,hours_per_week:40,hire_date:"",notes:"",linked_team_member_id:null,linked_team_member_email:null}); load();
 };
 const remove = async (id, name) => { if(!window.confirm(`Remove ${name}?`)) return; await apiFetch(`${FS_API}/employees/${id}`, {method:"DELETE"}); load(); };

 const parseCSV = (text) => {
  const lines = text.trim().split(/\r?\n/);
  if(lines.length < 2) return [];
  const headers = lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/\s+/g,"_"));
  return lines.slice(1).map(line=>{
   const cols = line.split(",").map(c=>c.trim());
   const obj = {};
   headers.forEach((h,i)=>{ obj[h] = cols[i] || ""; });
   return {
    full_name: obj.full_name || obj.name || "",
    job_title: obj.job_title || obj.title || "",
    department: obj.department || "Field Ops",
    employment_type: obj.employment_type || obj.type || "Full-Time",
    workspace_id: String(obj.workspace_id || obj.workspace || "1"),
    city: obj.city || "", zip: obj.zip || "", phone: obj.phone || "", email: obj.email || "",
    pay_type: obj.pay_type || (obj.salary ? "Salary" : "Hourly"),
    pay_rate: parseFloat(obj.pay_rate || obj.salary || obj.rate || 0) || 0,
    hours_per_week: parseFloat(obj.hours_per_week || obj.hours || 40) || 40,
    hire_date: obj.hire_date || obj.hired || "",
    notes: obj.notes || "",
   };
  }).filter(e => e.full_name);
 };
 const doImport = async () => {
  const employees = parseCSV(csvText);
  if(!employees.length) { alert("No valid rows found"); return; }
  if(!window.confirm(`Import ${employees.length} employees?`)) return;
  await apiFetch(`${FS_API}/employees/bulk-import`, {method:"POST", body: JSON.stringify({employees})});
  setShowImport(false); setCsvText(""); load();
 };
 const fakeQbSync = async () => {
  if(!window.confirm("This is a demo: pretend to pull a few employees from QuickBooks?")) return;
  const employees = [
   {full_name:"Sam Patterson (QB)", job_title:"Contractor", department:"Field Ops", employment_type:"Contractor", workspace_id:"2", city:"Pearland", zip:"77584", phone:"713-555-9001", email:"sam@qb-sync.example", pay_type:"Hourly", pay_rate:42, hours_per_week:28, hire_date:"2024-09-12"},
   {full_name:"Erin Walsh (QB)", job_title:"Bookkeeper", department:"Admin", employment_type:"Part-Time", workspace_id:"1", city:"Houston", zip:"77024", phone:"713-555-9002", email:"erin@qb-sync.example", pay_type:"Hourly", pay_rate:28, hours_per_week:20, hire_date:"2025-01-04"},
  ];
  await apiFetch(`${FS_API}/employees/bulk-import`, {method:"POST", body: JSON.stringify({employees})});
  load();
 };

 const exportData = () => ({columns:["Full Name","Job Title","Department","Type","Workspace","City","ZIP","Phone","Email","Pay Type","Pay Rate","Hours/Week","Hire Date"],
  rows: filteredEmps.map(e=>[e.full_name,e.job_title,e.department,e.employment_type,LOCATIONS.find(l=>String(l.id)===String(e.workspace_id))?.name||e.workspace_id,e.city,e.zip,e.phone,e.email,e.pay_type,e.pay_rate,e.hours_per_week,e.hire_date])});

 const filteredEmps = filterWs==="all" ? emps : emps.filter(e=>String(e.workspace_id)===filterWs);
 const monthlyPayroll = filteredEmps.reduce((a,e)=> a + (e.pay_type==="Hourly" ? (parseFloat(e.pay_rate||0) * parseFloat(e.hours_per_week||40) * 4.33) : (parseFloat(e.pay_rate||0)/12)), 0);
 const annualPayroll = monthlyPayroll * 12;

 return (
  <div>
   <div className="kpi-row" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
    <KPI label="People" value={filteredEmps.length} color="cyan"/>
    <KPI label="Monthly Payroll" value={`$${(monthlyPayroll/1000).toFixed(1)}K`} color="amber" accent="var(--amber)"/>
    <KPI label="Annual Payroll" value={`$${(annualPayroll/1000).toFixed(1)}K`} color="red" accent="var(--red)"/>
    <KPI label="Departments" value={new Set(filteredEmps.map(e=>e.department)).size} color="green" accent="var(--green)"/>
   </div>

   <div className="card" style={{marginBottom:16,padding:"14px 18px"}}>
    <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10,alignItems:"center"}}>
     <div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Our People</div>
      <div style={{fontSize:11,color:"var(--text3)"}}>Add your team manually, paste from a spreadsheet, or sync from QuickBooks. Payroll feeds directly into the Office P&L.</div>
     </div>
     <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      <select className="form-control" value={filterWs} onChange={e=>setFilterWs(e.target.value)} style={{fontSize:11,padding:"4px 8px"}}>
       <option value="all">All locations</option>
       {LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name}</option>)}
      </select>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportData();exportCSV("Team & Payroll",columns,rows);}} style={{fontSize:11}}>CSV</button>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportData();exportPDF("Team & Payroll",columns,rows);}} style={{fontSize:11}}>PDF</button>
      <button className="btn btn-ghost" onClick={fakeQbSync} style={{fontSize:11}} title="Demo: pulls a couple of records as if from QuickBooks">↻ QuickBooks Sync</button>
      <button className="btn btn-ghost" onClick={()=>setShowImport(true)} style={{fontSize:11}}>Import CSV</button>
      <button className="btn btn-primary" data-testid="add-employee" onClick={()=>setShowAdd(true)} style={{fontSize:11}}>+ Add Person</button>
     </div>
    </div>
   </div>

   <div className="card">
    <div className="card-header"><span className="card-title">Cross-Location Staffing</span>
     {loading ? <span className="badge badge-gray">Loading…</span> : <span className="badge badge-cyan">{filteredEmps.length} people · {LOCATIONS.length} locations</span>}
     <button className="btn btn-ghost" onClick={seedDemo} style={{fontSize:10,padding:"3px 8px",marginLeft:8}} title="One-shot demo seed (employees, finance, sales, leads, goals)">↻ Seed Demo</button>
    </div>
    <table><thead><tr><th>Name</th><th>Title</th><th>Dept</th><th>Type</th><th>Location</th><th>City / ZIP</th><th>Contact</th><th>Pay</th><th>Hire Date</th><th>Apex Link</th><th></th></tr></thead>
     <tbody>
      {filteredEmps.length===0 && <tr><td colSpan={11} style={{textAlign:"center",padding:20,color:"var(--text3)",fontSize:11}}>No team members yet. Add your first person above, or click "↻ Seed Demo" for sample data.</td></tr>}
      {filteredEmps.map(e=>{
       const ws = LOCATIONS.find(l=>String(l.id)===String(e.workspace_id));
       const monthly = e.pay_type==="Hourly" ? (parseFloat(e.pay_rate||0) * parseFloat(e.hours_per_week||40) * 4.33) : (parseFloat(e.pay_rate||0)/12);
       return (<tr key={e.id} data-testid={`payroll-row-${e.id}`}>
        <td style={{fontWeight:500,fontSize:12}}>{e.full_name}</td>
        <td style={{fontSize:11}}>{e.job_title}</td>
        <td style={{fontSize:11}}><span className="badge badge-gray" style={{fontSize:9}}>{e.department}</span></td>
        <td style={{fontSize:11}}>{e.employment_type}</td>
        <td style={{fontSize:11}} className="text-cyan">{ws?.name || `WS-${e.workspace_id}`}</td>
        <td style={{fontSize:11,color:"var(--text2)"}}>{e.city}{e.zip?` · ${e.zip}`:""}</td>
        <td style={{fontSize:11,color:"var(--text2)"}}>{e.phone}<div className="text-sm" style={{color:"var(--text3)"}}>{e.email}</div></td>
        <td className="mono text-sm">{e.pay_type==="Hourly"?`$${e.pay_rate}/hr · ${e.hours_per_week}h`:`$${(e.pay_rate||0).toLocaleString()}/yr`}<div className="text-sm" style={{color:"var(--text3)"}}>~${Math.round(monthly).toLocaleString()}/mo</div></td>
        <td className="mono text-sm" style={{color:"var(--text3)"}}>{e.hire_date}</td>
        <td style={{fontSize:10}}>
          {e.linked_team_member_id
            ? <span data-testid={`payroll-linked-${e.id}`} style={{background:"#1e40af",color:"#fff",padding:"2px 6px",borderRadius:3,fontSize:9,fontWeight:700,letterSpacing:0.5}} title={e.linked_team_member_email || ""}>LINKED</span>
            : <span style={{color:"var(--text3)"}}>—</span>}
        </td>
        <td style={{textAlign:"right"}}><button className="btn btn-ghost" onClick={()=>remove(e.id,e.full_name)} style={{fontSize:10,padding:"3px 8px",color:"var(--red)"}}>Remove</button></td>
       </tr>);
      })}
     </tbody></table>
   </div>

   {showAdd && (
    <div className="modal-overlay" onClick={()=>setShowAdd(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:680}}>
      <div className="modal-header"><div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>+ Add Team Member</div><button className="icon-btn" onClick={()=>setShowAdd(false)}>×</button></div>
      <form onSubmit={submitAdd} style={{padding:18}}>
       <div className="form-group" style={{marginBottom:14, padding:10, background:"rgba(30,64,175,.08)", borderRadius:6, border:"1px solid rgba(30,64,175,.25)"}}>
        <div className="form-lbl" style={{display:"flex", justifyContent:"space-between"}}>
         <span>Link to Apex Team Member</span>
         {form.linked_team_member_id && <span style={{fontSize:9,color:"#1e40af",fontWeight:700}}>LINKED ✓</span>}
        </div>
        <LinkTeamMemberPicker valueId={form.linked_team_member_id} onPick={(m)=>{
          if(!m){ setForm({...form, linked_team_member_id:null, linked_team_member_email:null}); return; }
          setForm({
            ...form,
            linked_team_member_id: m.id,
            linked_team_member_email: m.email,
            full_name: form.full_name || m.name || "",
            email: form.email || m.email || "",
            phone: form.phone || m.phone || "",
            job_title: form.job_title || m.job_title || "",
          });
        }}/>
        <div style={{fontSize:10, color:"var(--text3)", marginTop:6}}>Optional. Links this payroll record to a member in <strong>Team and Access</strong> so names, emails, and roles stay consistent.</div>
       </div>
       <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div className="form-group"><div className="form-lbl">Full Name *</div><input className="form-control" value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})} required style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Job Title</div><input className="form-control" value={form.job_title} onChange={e=>setForm({...form,job_title:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Department</div><input className="form-control" value={form.department} onChange={e=>setForm({...form,department:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Employment Type</div><select className="form-control" value={form.employment_type} onChange={e=>setForm({...form,employment_type:e.target.value})} style={{width:"100%"}}>{["Full-Time","Part-Time","Contractor","Seasonal"].map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><div className="form-lbl">Location</div><select className="form-control" value={form.workspace_id} onChange={e=>setForm({...form,workspace_id:e.target.value})} style={{width:"100%"}}>{LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name}</option>)}</select></div>
        <div className="form-group"><div className="form-lbl">Hire Date</div><input className="form-control" type="date" value={form.hire_date} onChange={e=>setForm({...form,hire_date:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">City</div><input className="form-control" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">ZIP</div><input className="form-control" value={form.zip} onChange={e=>setForm({...form,zip:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Phone</div><input className="form-control" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Email</div><input className="form-control" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Pay Type</div><select className="form-control" value={form.pay_type} onChange={e=>setForm({...form,pay_type:e.target.value})} style={{width:"100%"}}><option>Hourly</option><option>Salary</option></select></div>
        <div className="form-group"><div className="form-lbl">{form.pay_type==="Hourly"?"Rate ($/hr)":"Salary ($/yr)"}</div><input className="form-control" type="number" min={0} value={form.pay_rate} onChange={e=>setForm({...form,pay_rate:parseFloat(e.target.value)||0})} style={{width:"100%"}}/></div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><div className="form-lbl">Hours per Week (used for hourly monthly cost)</div><input className="form-control" type="number" min={0} max={80} value={form.hours_per_week} onChange={e=>setForm({...form,hours_per_week:parseFloat(e.target.value)||0})} style={{width:"100%"}}/></div>
       </div>
       {err && <div style={{padding:"8px 12px",background:"rgba(204,0,0,.12)",border:"1px solid rgba(204,0,0,.35)",borderRadius:4,fontSize:11,color:"#ff5555",marginBottom:12}}>{err}</div>}
       <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}><button type="button" className="btn btn-outline" onClick={()=>setShowAdd(false)} style={{fontSize:11}}>Cancel</button><button type="submit" className="btn btn-primary" style={{fontSize:11}}>Save Person</button></div>
      </form>
     </div>
    </div>
   )}

   {showImport && (
    <div className="modal-overlay" onClick={()=>setShowImport(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:720}}>
      <div className="modal-header"><div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>Import People from CSV</div><button className="icon-btn" onClick={()=>setShowImport(false)}>×</button></div>
      <div style={{padding:18}}>
       <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>Paste rows from a spreadsheet. Expected headers (first row): <code style={{color:"var(--cyan)"}}>full_name, job_title, department, employment_type, workspace_id, city, zip, phone, email, pay_type, pay_rate, hours_per_week, hire_date</code></div>
       <textarea value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder={"full_name,job_title,workspace_id,pay_type,pay_rate\nJohn Smith,Tech I,1,Hourly,28"} style={{width:"100%",minHeight:240,padding:10,background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,fontFamily:"var(--mono)",fontSize:11,color:"var(--text)",resize:"vertical"}}/>
       <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}><button type="button" className="btn btn-outline" onClick={()=>setShowImport(false)} style={{fontSize:11}}>Cancel</button><button onClick={doImport} className="btn btn-primary" style={{fontSize:11}}>Import {csvText.trim().split(/\r?\n/).length>1?`${csvText.trim().split(/\r?\n/).length-1} rows`:""}</button></div>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

// ─── OFFICE P&L ───────────────────────────────────────────────────────────────
function PnLPage() {
 const [data, setData] = useState(null);
 const [editing, setEditing] = useState(null); // workspace_id of finance being edited
 const [form, setForm] = useState({overhead_monthly:0,operating_monthly:0,notes:""});
 const load = async () => { const r = await apiFetch(`${FS_API}/pnl`); if(r.ok) setData(await r.json()); };
 useEffect(()=>{ load(); },[]);
 const startEdit = async (wid) => {
  const r = await apiFetch(`${FS_API}/office-finance`);
  const list = r.ok ? (await r.json()).finance : [];
  const f = list.find(x=>x.workspace_id===wid) || {workspace_id:wid,overhead_monthly:0,operating_monthly:0,notes:""};
  setForm({overhead_monthly:f.overhead_monthly,operating_monthly:f.operating_monthly,notes:f.notes||""});
  setEditing(wid);
 };
 const save = async () => { await apiFetch(`${FS_API}/office-finance/${editing}`, {method:"PUT", body: JSON.stringify({...form, workspace_id:editing})}); setEditing(null); load(); };
 if(!data) return <div style={{padding:24,color:"var(--text3)"}}>Loading P&L…</div>;
 const totalRev = data.pnl.reduce((a,c)=>a+c.revenue,0);
 const totalPay = data.pnl.reduce((a,c)=>a+c.payroll,0);
 const totalNet = data.pnl.reduce((a,c)=>a+c.net,0);
 const exportData = ()=>({columns:["Location","Revenue","Payroll","Overhead","Operating","Net","Status","Employees","Jobs"],
  rows: data.pnl.map(c=>[LOCATIONS.find(l=>String(l.id)===String(c.workspace_id))?.name||c.workspace_id,c.revenue,c.payroll,c.overhead,c.operating,c.net,c.status,c.employee_count,c.job_count])});

 return (
  <div>
   <div className="kpi-row" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
    <KPI label="Total Revenue (MTD)" value={`$${(totalRev/1000).toFixed(1)}K`} color="green" accent="var(--green)"/>
    <KPI label="Total Payroll (Monthly)" value={`$${(totalPay/1000).toFixed(1)}K`} color="amber" accent="var(--amber)"/>
    <KPI label="Total Net" value={`$${(totalNet/1000).toFixed(1)}K`} color={totalNet>=0?"green":"red"} accent={totalNet>=0?"var(--green)":"var(--red)"}/>
    <KPI label="Locations" value={data.pnl.length} color="cyan"/>
   </div>

   <div className="card" style={{marginBottom:16,padding:"14px 18px"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
     <div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>How are our offices doing?</div>
      <div style={{fontSize:11,color:"var(--text3)"}}>Revenue pulls from billed jobs · Payroll auto-calculated from Team & Payroll · Overhead and operating costs are owner-entered per location.</div>
     </div>
     <div style={{display:"flex",gap:6}}>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportData();exportCSV("Office P&L",columns,rows);}} style={{fontSize:11}}>CSV</button>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportData();exportPDF("Office P&L",columns,rows);}} style={{fontSize:11}}>PDF</button>
      <button className="btn btn-ghost" onClick={load} style={{fontSize:11}}>↻ Refresh</button>
     </div>
    </div>
   </div>

   <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
    {data.pnl.map(c=>{
     const ws = LOCATIONS.find(l=>String(l.id)===String(c.workspace_id));
     const color = c.status==="green"?"#22c55e":c.status==="yellow"?"#eab308":c.status==="red"?"#ef4444":"#666";
     const bg = c.status==="green"?"rgba(34,197,94,.08)":c.status==="yellow"?"rgba(234,179,8,.08)":c.status==="red"?"rgba(239,68,68,.08)":"transparent";
     return (
      <div key={c.workspace_id} className="card" style={{padding:0,borderLeft:`4px solid ${color}`,background:bg}}>
       <div style={{padding:"14px 16px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
         <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>{ws?.name || `Workspace ${c.workspace_id}`}</div>
         <div style={{fontSize:10,color:"var(--text3)"}}>{ws?.address || ""}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
         <span style={{width:10,height:10,borderRadius:"50%",background:color}}/>
         <span style={{fontSize:10,letterSpacing:1,textTransform:"uppercase",color:color,fontWeight:700}}>{c.status==="green"?"Healthy":c.status==="yellow"?"Tight":c.status==="red"?"Bleeding":"No Activity"}</span>
        </div>
       </div>
       <div style={{padding:"14px 16px"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
         <div><div style={{fontSize:9,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase"}}>Revenue</div><div className="mono" style={{fontSize:18,color:"var(--green)",fontWeight:600}}>${c.revenue.toLocaleString()}</div></div>
         <div><div style={{fontSize:9,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase"}}>Payroll</div><div className="mono" style={{fontSize:18,color:"var(--amber)",fontWeight:600}}>${c.payroll.toLocaleString()}</div></div>
         <div><div style={{fontSize:9,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase"}}>Overhead</div><div className="mono" style={{fontSize:14}}>${c.overhead.toLocaleString()}</div></div>
         <div><div style={{fontSize:9,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase"}}>Operating</div><div className="mono" style={{fontSize:14}}>${c.operating.toLocaleString()}</div></div>
        </div>
        <div style={{padding:"10px 0",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
         <div style={{fontSize:10,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase"}}>Net Profit / (Loss)</div>
         <div className="mono" style={{fontSize:20,color,fontWeight:700}}>${c.net.toLocaleString()}</div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
         <div style={{fontSize:11,color:"var(--text3)"}}>{c.employee_count} people · {c.job_count} jobs</div>
         <button className="btn btn-ghost" onClick={()=>startEdit(c.workspace_id)} style={{fontSize:10,padding:"3px 8px"}}>Edit Expenses</button>
        </div>
       </div>
      </div>
     );
    })}
   </div>

   {editing && (
    <div className="modal-overlay" onClick={()=>setEditing(null)}>
     <div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-header"><div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>Edit Monthly Expenses — {LOCATIONS.find(l=>String(l.id)===editing)?.name}</div><button className="icon-btn" onClick={()=>setEditing(null)}>×</button></div>
      <div style={{padding:18}}>
       <div className="form-group"><div className="form-lbl">Overhead (rent, utilities, insurance) — $/mo</div><input className="form-control" type="number" min={0} value={form.overhead_monthly} onChange={e=>setForm({...form,overhead_monthly:parseFloat(e.target.value)||0})} style={{width:"100%"}}/></div>
       <div className="form-group"><div className="form-lbl">Operating (fuel, materials, marketing) — $/mo</div><input className="form-control" type="number" min={0} value={form.operating_monthly} onChange={e=>setForm({...form,operating_monthly:parseFloat(e.target.value)||0})} style={{width:"100%"}}/></div>
       <div className="form-group"><div className="form-lbl">Notes</div><input className="form-control" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={{width:"100%"}}/></div>
       <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}><button className="btn btn-outline" onClick={()=>setEditing(null)} style={{fontSize:11}}>Cancel</button><button onClick={save} className="btn btn-primary" style={{fontSize:11}}>Save</button></div>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

// ─── SALES TRACKING ───────────────────────────────────────────────────────────
function SalesPage() {
 const [sales, setSales] = useState([]);
 const [leads, setLeads] = useState([]);
 const [goals, setGoals] = useState([]);
 const [emps, setEmps] = useState([]);
 const [showAdd, setShowAdd] = useState(false);
 const [showLead, setShowLead] = useState(false);
 const [form, setForm] = useState({date:new Date().toISOString().slice(0,10),amount:0,service_type:"Water Mitigation",team_member_id:"",team_member_name:"",workspace_id:"1",customer:"",notes:""});
 const [leadForm, setLeadForm] = useState({name:"",contact:"",status:"New",estimated_value:0,workspace_id:"1",team_member_id:"",team_member_name:"",source:""});
 const [view, setView] = useState("location"); // location | member

 const load = async () => {
  const [a,b,c,d] = await Promise.all([apiFetch(`${FS_API}/sales`),apiFetch(`${FS_API}/leads`),apiFetch(`${FS_API}/goals`),apiFetch(`${FS_API}/employees`)]);
  if(a.ok) setSales((await a.json()).sales||[]);
  if(b.ok) setLeads((await b.json()).leads||[]);
  if(c.ok) setGoals((await c.json()).goals||[]);
  if(d.ok) setEmps((await d.json()).employees||[]);
 };
 useEffect(()=>{ load(); },[]);

 const month = new Date().toISOString().slice(0,7); // YYYY-MM
 const monthSales = sales.filter(s => (s.date||"").startsWith(month));
 const monthTotal = monthSales.reduce((a,s)=>a+(parseFloat(s.amount)||0),0);
 const byLocation = LOCATIONS.map(l => {
  const ws = String(l.id);
  const wsSales = monthSales.filter(s=>String(s.workspace_id)===ws);
  const total = wsSales.reduce((a,s)=>a+parseFloat(s.amount||0),0);
  const goal = goals.find(g=>g.workspace_id===ws && g.period===month)?.target || 0;
  const pct = goal>0 ? Math.round(total/goal*100) : 0;
  const wsLeads = leads.filter(le=>String(le.workspace_id)===ws);
  const wonLeads = wsLeads.filter(le=>le.status==="Won").length;
  const closedLeads = wsLeads.filter(le=>["Won","Lost"].includes(le.status)).length;
  const conv = closedLeads>0 ? Math.round(wonLeads/closedLeads*100) : 0;
  const status = goal===0 ? "gray" : pct>=100 ? "green" : pct>=70 ? "yellow" : "red";
  return {ws, name:l.name, total, goal, pct, conv, salesCount:wsSales.length, leadCount:wsLeads.length, status};
 });
 const byMember = (() => {
  const map = {};
  monthSales.forEach(s => {
   const key = s.team_member_id || s.team_member_name || "(unassigned)";
   if(!map[key]) map[key] = {name: s.team_member_name||"(unassigned)", id: s.team_member_id, total:0, count:0, leads:0, won:0};
   map[key].total += parseFloat(s.amount||0);
   map[key].count += 1;
  });
  leads.forEach(le => {
   const key = le.team_member_id || le.team_member_name || "(unassigned)";
   if(!map[key]) map[key] = {name: le.team_member_name||"(unassigned)", id: le.team_member_id, total:0, count:0, leads:0, won:0};
   map[key].leads += 1; if(le.status==="Won") map[key].won += 1;
  });
  return Object.values(map).sort((a,b)=>b.total-a.total);
 })();

 const submitSale = async (e) => {
  e.preventDefault();
  const emp = emps.find(x=>x.id===form.team_member_id);
  const body = {...form, team_member_name: emp?.full_name || form.team_member_name};
  await apiFetch(`${FS_API}/sales`, {method:"POST", body: JSON.stringify(body)});
  setShowAdd(false); setForm({date:new Date().toISOString().slice(0,10),amount:0,service_type:"Water Mitigation",team_member_id:"",team_member_name:"",workspace_id:"1",customer:"",notes:""}); load();
 };
 const submitLead = async (e) => {
  e.preventDefault();
  const emp = emps.find(x=>x.id===leadForm.team_member_id);
  const body = {...leadForm, team_member_name: emp?.full_name || leadForm.team_member_name};
  await apiFetch(`${FS_API}/leads`, {method:"POST", body: JSON.stringify(body)});
  setShowLead(false); setLeadForm({name:"",contact:"",status:"New",estimated_value:0,workspace_id:"1",team_member_id:"",team_member_name:"",source:""}); load();
 };
 const updateLeadStatus = async (lead, status) => {
  await apiFetch(`${FS_API}/leads/${lead.id}`, {method:"PUT", body: JSON.stringify({...lead, status})});
  load();
 };
 const updateGoal = async (wsId, target) => {
  await apiFetch(`${FS_API}/goals`, {method:"PUT", body: JSON.stringify({workspace_id:String(wsId), period:month, period_type:"month", target:parseFloat(target)||0})});
  load();
 };

 const exportSalesData = ()=>({columns:["Date","Amount","Service","Team Member","Location","Customer"],
  rows: sales.map(s=>[s.date,s.amount,s.service_type,s.team_member_name,LOCATIONS.find(l=>String(l.id)===String(s.workspace_id))?.name||s.workspace_id,s.customer])});

 return (
  <div>
   <div className="kpi-row" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
    <KPI label="This Month" value={`$${(monthTotal/1000).toFixed(1)}K`} color="green" accent="var(--green)" sub={`${monthSales.length} sales`}/>
    <KPI label="Open Leads" value={leads.filter(l=>["New","In Progress"].includes(l.status)).length} color="amber" accent="var(--amber)" sub={`$${leads.filter(l=>["New","In Progress"].includes(l.status)).reduce((a,l)=>a+parseFloat(l.estimated_value||0),0).toLocaleString()} pipeline`}/>
    <KPI label="Conversion" value={`${leads.filter(l=>["Won","Lost"].includes(l.status)).length>0 ? Math.round(leads.filter(l=>l.status==="Won").length/leads.filter(l=>["Won","Lost"].includes(l.status)).length*100) : 0}%`} color="cyan"/>
    <KPI label="Active Sellers" value={new Set(monthSales.map(s=>s.team_member_id||s.team_member_name).filter(Boolean)).size} color="cyan"/>
   </div>

   <div className="card" style={{marginBottom:16,padding:"14px 18px"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
     <div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>How's the team selling?</div>
      <div style={{fontSize:11,color:"var(--text3)"}}>Log every sale, track leads, set monthly goals per location. Money rolls up into Office P&L automatically.</div>
     </div>
     <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportSalesData();exportCSV("Sales Tracking",columns,rows);}} style={{fontSize:11}}>CSV</button>
      <button className="btn btn-ghost" onClick={()=>{const {columns,rows}=exportSalesData();exportPDF("Sales Tracking",columns,rows);}} style={{fontSize:11}}>PDF</button>
      <button className="btn btn-ghost" onClick={()=>setShowLead(true)} style={{fontSize:11}}>+ Add Lead</button>
      <button className="btn btn-primary" data-testid="add-sale" onClick={()=>setShowAdd(true)} style={{fontSize:11}}>+ Log Sale</button>
     </div>
    </div>
   </div>

   <div className="card" style={{marginBottom:16}}>
    <div className="card-header">
     <span className="card-title">📊 This Month by Location · Goal Progress</span>
     <div className="tab-row" style={{marginBottom:0}}>
      <div className={`tab ${view==="location"?"active":""}`} onClick={()=>setView("location")}>By Location</div>
      <div className={`tab ${view==="member"?"active":""}`} onClick={()=>setView("member")}>By Team Member</div>
     </div>
    </div>
    {view==="location" ? (
     <table><thead><tr><th>Location</th><th>Sales</th><th>Goal</th><th>Progress</th><th>Conv. Rate</th><th>Status</th><th></th></tr></thead>
      <tbody>{byLocation.map(r=>{
       const color = r.status==="green"?"#22c55e":r.status==="yellow"?"#eab308":r.status==="red"?"#ef4444":"#666";
       return (<tr key={r.ws}>
        <td style={{fontWeight:500,fontSize:12}}>{r.name}</td>
        <td className="mono text-green">${r.total.toLocaleString()}<div className="text-sm" style={{color:"var(--text3)"}}>{r.salesCount} sales</div></td>
        <td className="mono text-sm">${r.goal.toLocaleString()}</td>
        <td><div style={{width:120}}><div className="progress-bar"><div className="progress-fill" style={{width:`${Math.min(r.pct,100)}%`,background:color}}/></div><div className="text-sm mono" style={{color,marginTop:2}}>{r.pct}%</div></div></td>
        <td className="mono text-sm">{r.conv}% <span style={{color:"var(--text3)"}}>({r.leadCount} leads)</span></td>
        <td><span className="badge" style={{background:color+"22",color,fontSize:9,border:`1px solid ${color}44`}}>{r.status==="green"?"On Track":r.status==="yellow"?"Behind":r.status==="red"?"At Risk":"No Goal"}</span></td>
        <td style={{textAlign:"right"}}><button className="btn btn-ghost" onClick={()=>{const nv=window.prompt(`Set monthly sales goal for ${r.name} ($)`, r.goal||0); if(nv!==null) updateGoal(r.ws, nv);}} style={{fontSize:10,padding:"3px 8px"}}>Edit Goal</button></td>
       </tr>);
      })}</tbody>
     </table>
    ) : (
     <table><thead><tr><th>Team Member</th><th>Sales</th><th>Count</th><th>Leads</th><th>Won</th><th>Conv. Rate</th></tr></thead>
      <tbody>{byMember.map(m=>{
       const closed = leads.filter(l=>(l.team_member_id===m.id || l.team_member_name===m.name) && ["Won","Lost"].includes(l.status)).length;
       const conv = closed>0 ? Math.round(m.won/closed*100) : 0;
       return (<tr key={m.name}>
        <td style={{fontWeight:500,fontSize:12}}>{m.name}{m.id && <div className="text-sm" style={{color:"var(--text3)"}}>{m.id}</div>}</td>
        <td className="mono text-green">${m.total.toLocaleString()}</td>
        <td className="mono text-sm">{m.count}</td>
        <td className="mono text-sm">{m.leads}</td>
        <td className="mono text-sm text-green">{m.won}</td>
        <td className="mono text-sm">{conv}%</td>
       </tr>);
      })}</tbody>
     </table>
    )}
   </div>

   <div className="grid2" style={{marginBottom:16}}>
    <div className="card">
     <div className="card-header"><span className="card-title">💵 Recent Sales</span><span className="badge badge-cyan">{sales.length}</span></div>
     <table><thead><tr><th>Date</th><th>Customer</th><th>Service</th><th>Team Member</th><th>Amount</th></tr></thead>
      <tbody>
       {sales.slice(0,8).map(s=>(<tr key={s.id}>
        <td className="mono text-sm">{s.date}</td>
        <td style={{fontSize:12,fontWeight:500}}>{s.customer}</td>
        <td><span className="badge badge-cyan" style={{fontSize:9}}>{s.service_type}</span></td>
        <td style={{fontSize:11}}>{s.team_member_name}</td>
        <td className="mono text-green" style={{fontWeight:600}}>${parseFloat(s.amount||0).toLocaleString()}</td>
       </tr>))}
       {sales.length===0 && <tr><td colSpan={5} style={{textAlign:"center",padding:14,color:"var(--text3)",fontSize:11}}>No sales logged yet.</td></tr>}
      </tbody>
     </table>
    </div>

    <div className="card">
     <div className="card-header"><span className="card-title">🎯 Lead Pipeline</span><span className="badge badge-amber">{leads.filter(l=>["New","In Progress"].includes(l.status)).length} open</span></div>
     <table><thead><tr><th>Lead</th><th>Est. Value</th><th>Team Member</th><th>Status</th></tr></thead>
      <tbody>
       {leads.slice(0,8).map(l=>(<tr key={l.id}>
        <td style={{fontSize:12,fontWeight:500}}>{l.name}<div className="text-sm" style={{color:"var(--text3)"}}>{l.source} · {l.contact}</div></td>
        <td className="mono text-sm">${parseFloat(l.estimated_value||0).toLocaleString()}</td>
        <td style={{fontSize:11}}>{l.team_member_name}</td>
        <td><select value={l.status} onChange={e=>updateLeadStatus(l,e.target.value)} className="form-control" style={{fontSize:10,padding:"3px 6px",height:"auto"}}>
         {["New","In Progress","Won","Lost"].map(s=><option key={s}>{s}</option>)}
        </select></td>
       </tr>))}
       {leads.length===0 && <tr><td colSpan={4} style={{textAlign:"center",padding:14,color:"var(--text3)",fontSize:11}}>No leads tracked yet.</td></tr>}
      </tbody>
     </table>
    </div>
   </div>

   {showAdd && (
    <div className="modal-overlay" onClick={()=>setShowAdd(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:620}}>
      <div className="modal-header"><div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>+ Log a Sale</div><button className="icon-btn" onClick={()=>setShowAdd(false)}>×</button></div>
      <form onSubmit={submitSale} style={{padding:18}}>
       <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div className="form-group"><div className="form-lbl">Date</div><input className="form-control" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Amount ($)</div><input className="form-control" type="number" min={0} value={form.amount} onChange={e=>setForm({...form,amount:parseFloat(e.target.value)||0})} required style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Service Type</div><select className="form-control" value={form.service_type} onChange={e=>setForm({...form,service_type:e.target.value})} style={{width:"100%"}}>{["Water Mitigation","Mold Remediation","Fire & Smoke","Sewage / Cat-3","Reconstruction","Contents Cleaning","Other"].map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><div className="form-lbl">Location</div><select className="form-control" value={form.workspace_id} onChange={e=>setForm({...form,workspace_id:e.target.value})} style={{width:"100%"}}>{LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name}</option>)}</select></div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><div className="form-lbl">Team Member</div><select className="form-control" value={form.team_member_id} onChange={e=>setForm({...form,team_member_id:e.target.value})} style={{width:"100%"}}><option value="">(unassigned)</option>{emps.map(e=><option key={e.id} value={e.id}>{e.full_name} — {e.job_title}</option>)}</select></div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><div className="form-lbl">Customer</div><input className="form-control" value={form.customer} onChange={e=>setForm({...form,customer:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group" style={{gridColumn:"1 / -1"}}><div className="form-lbl">Notes</div><input className="form-control" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={{width:"100%"}}/></div>
       </div>
       <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}><button type="button" className="btn btn-outline" onClick={()=>setShowAdd(false)} style={{fontSize:11}}>Cancel</button><button type="submit" className="btn btn-primary" style={{fontSize:11}}>Log Sale</button></div>
      </form>
     </div>
    </div>
   )}

   {showLead && (
    <div className="modal-overlay" onClick={()=>setShowLead(false)}>
     <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:560}}>
      <div className="modal-header"><div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,letterSpacing:1}}>+ Add Lead</div><button className="icon-btn" onClick={()=>setShowLead(false)}>×</button></div>
      <form onSubmit={submitLead} style={{padding:18}}>
       <div className="form-group"><div className="form-lbl">Lead Name *</div><input className="form-control" value={leadForm.name} onChange={e=>setLeadForm({...leadForm,name:e.target.value})} required style={{width:"100%"}}/></div>
       <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div className="form-group"><div className="form-lbl">Contact</div><input className="form-control" value={leadForm.contact} onChange={e=>setLeadForm({...leadForm,contact:e.target.value})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Source</div><input className="form-control" value={leadForm.source} onChange={e=>setLeadForm({...leadForm,source:e.target.value})} placeholder="Referral, Website, Cold call…" style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Est. Value ($)</div><input className="form-control" type="number" min={0} value={leadForm.estimated_value} onChange={e=>setLeadForm({...leadForm,estimated_value:parseFloat(e.target.value)||0})} style={{width:"100%"}}/></div>
        <div className="form-group"><div className="form-lbl">Status</div><select className="form-control" value={leadForm.status} onChange={e=>setLeadForm({...leadForm,status:e.target.value})} style={{width:"100%"}}>{["New","In Progress","Won","Lost"].map(s=><option key={s}>{s}</option>)}</select></div>
        <div className="form-group"><div className="form-lbl">Location</div><select className="form-control" value={leadForm.workspace_id} onChange={e=>setLeadForm({...leadForm,workspace_id:e.target.value})} style={{width:"100%"}}>{LOCATIONS.map(l=><option key={l.id} value={String(l.id)}>{l.name}</option>)}</select></div>
        <div className="form-group"><div className="form-lbl">Owner</div><select className="form-control" value={leadForm.team_member_id} onChange={e=>setLeadForm({...leadForm,team_member_id:e.target.value})} style={{width:"100%"}}><option value="">(unassigned)</option>{emps.map(e=><option key={e.id} value={e.id}>{e.full_name}</option>)}</select></div>
       </div>
       <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}><button type="button" className="btn btn-outline" onClick={()=>setShowLead(false)} style={{fontSize:11}}>Cancel</button><button type="submit" className="btn btn-primary" style={{fontSize:11}}>Save Lead</button></div>
      </form>
     </div>
    </div>
   )}

   {/* Parked feature 3: Quarterly goals + Month-over-Month (additive) */}
   <SalesQuarterlyAndMoM locations={LOCATIONS}/>
  </div>
 );
}

function Settings() {
 return (
 <div>
 <div className="grid2">
 {[
 {title:"Apex Field Pro IQ Sync",fields:[["API Endpoint","https://api.apexfieldpro.io"],["API Key","••••••••••••••••"],["Sync Interval","Every 5 minutes"],["Last Sync","05/20/26 14:32"]]},
 {title:"QuickBooks Integration",fields:[["Realm ID","9130355######"],["OAuth Status","Active"],["Sync Mode","Bidirectional"],["Last Push","05/20/26 14:28"]]},
 {title:"Xactimate / Xactanalysis",fields:[["Profile","INFINITY INTEL BIZ"],["Region","TX South"],["Auto-submit","Enabled"],["Connection","Secure"]]},
 {title:"GPS / Dispatch Settings",fields:[["Max Techs","25"],["Update Rate","30 seconds"],["Geo-fence Alerts","Enabled"],["History Retention","90 days"]]},
 {title:"Notification & Alerts",fields:[["Low Inventory","Email + SMS"],["Job Status Change","Push + Email"],["Payment Received","SMS"],["Daily Summary","7:00 AM Email"]]},
 ].map(s=>(
 <div key={s.title} className="integration-card">
 <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700,marginBottom:14,color:"var(--cyan)"}}>{s.title}</div>
 {s.fields.map(([k,v])=>(
 <div key={k} className="stat-row">
 <span style={{fontSize:11,color:"var(--text3)"}}>{k}</span>
 <span style={{fontSize:11,fontFamily:"var(--mono)",color:"var(--text2)"}}>{v}</span>
 </div>
 ))}
 <button className="btn btn-ghost" style={{width:"100%",marginTop:12,fontSize:11}}>Configure</button>
 </div>
 ))}
 </div>
 </div>
 );
}

const PAGE_TITLES = {executive:"Executive Dashboard",dashboard:"Operations Dashboard",technicians:"Technicians",drivers:"Drivers",customers:"Customers",jobs:"Jobs",inventory:"Inventory",quickbooks:"QuickBooks",xactimate:"Xactimate",analytics:"Analytics",reports:"Reports",locations:"Locations",team:"Team Management",payroll:"Team & Payroll",pnl:"Office P&L Snapshot",sales:"Sales Tracking",settings:"Settings","iv-team-access":"Team and Access","iv-company-profile":"Company Profile","iv-legacy-plan":"Legacy Plan","iv-import-center":"Import Center","iv-arm-wo":"ARM Work Orders","iv-sync":"Response Management Sync","iv-documents":"Documents","iv-schedule-day":"Schedule · Day View","iv-equipment":"Equipment Tracking","iv-contacts-dedupe":"Contacts · De-duplicate","iv-xactimate-board":"Xactimate Board","iv-timecards":"Timecards","iv-materials":"Materials","iv-service-requests":"Service Requests","iv-control-center":"Control Center","iv-map-pins":"Live Tech Map","iv-billing":"Billing & Subscription","iv-quickbooks":"QuickBooks Sync","iv-payroll-providers":"Payroll Providers","iv-restoration-reports":"Restoration Reports","iv-schedule-views":"Schedule Views","iv-my-profile":"My Profile","iv-email-log":"Email Log","iv-chat-now":"Chat Now","iv-avg-job-size":"Avg Job Size","iv-pnl-v2":"P&L (Operations)","iv-special-perms":"Special Permissions"};

const JOBS_STORAGE_KEY = "apex_jobs_v1";
const TOKEN_STORAGE_KEY = "apex_auth_token";
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const FS_API = `${API_BASE}/api/fs`;
const AUTH_API = `${API_BASE}/api/auth`;
const authHeaders = () => { const t = localStorage.getItem(TOKEN_STORAGE_KEY); return t ? {"Authorization":"Bearer "+t} : {}; };
const apiFetch = (path, opts={}) => fetch(path, {...opts, headers:{"Content-Type":"application/json", ...authHeaders(), ...(opts.headers||{})}});
const loadLocalJobs = () => {
 try { const raw = localStorage.getItem(JOBS_STORAGE_KEY); if(!raw) return SEED_JOBS; const parsed = JSON.parse(raw); return Array.isArray(parsed) && parsed.length ? parsed : SEED_JOBS; }
 catch { return SEED_JOBS; }
};

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onAuthed }) {
 const urlCode = (() => { try { return new URLSearchParams(window.location.search).get("invite"); } catch { return null; } })();
 const [mode, setMode] = useState(urlCode ? "invite" : "login"); // login | register | invite
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [name, setName] = useState("");
 const [workspaceId, setWorkspaceId] = useState("1");
 const [inviteCode] = useState(urlCode || "");
 const [inviteInfo, setInviteInfo] = useState(null);
 const [err, setErr] = useState("");
 const [busy, setBusy] = useState(false);

 useEffect(()=>{
  if(!urlCode) return;
  (async()=>{
   try {
    const r = await fetch(`${FS_API}/invites/lookup/${urlCode}`);
    if(r.ok){ const d = await r.json(); setInviteInfo(d.invite); setEmail(d.invite.email); setName(d.invite.name||""); }
    else { setErr("This invite link is invalid or has already been used."); }
   } catch(e){ setErr(e.message); }
  })();
 },[urlCode]);

 const formatErr = (d) => {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
 };
 const submit = async (e) => {
  e.preventDefault();
  setErr(""); setBusy(true);
  try {
   let url, body;
   if (mode === "invite") {
    url = `${FS_API}/invites/accept`;
    body = { code: inviteCode, password, name };
    const r = await fetch(url, {method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body)});
    const data = await r.json();
    if(!r.ok){ setErr(formatErr(data?.detail)); setBusy(false); return; }
    // Auto-login
    const lr = await fetch(`${AUTH_API}/login`, {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({email: data.email, password})});
    const ld = await lr.json();
    if (lr.ok) { localStorage.setItem(TOKEN_STORAGE_KEY, ld.token); window.history.replaceState({}, "", window.location.pathname); onAuthed(ld.user); }
    setBusy(false); return;
   }
   url = `${AUTH_API}/${mode === "login" ? "login" : "register"}`;
   body = mode === "login" ? {email, password} : {name, email, password, workspace_id: workspaceId};
   const r = await fetch(url, {method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body)});
   const data = await r.json();
   if(!r.ok){ setErr(formatErr(data?.detail)); setBusy(false); return; }
   localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
   onAuthed(data.user);
  } catch (e) { setErr(e.message); }
  setBusy(false);
 };
 return (
  <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg)",fontFamily:"var(--dm)"}}>
   <div style={{width:"100%",maxWidth:420,padding:32}}>
    <div style={{textAlign:"center",marginBottom:24}}>
     <img src={LOGO_URL} alt="Apex" style={{width:220,height:"auto",margin:"0 auto",display:"block"}}/>
    </div>
    <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:28}}>
     <div style={{fontFamily:"var(--rajdhani)",fontSize:20,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"var(--text)",marginBottom:4,textAlign:"center"}}>{mode === "login" ? "Sign In" : mode === "invite" ? "Accept Invite" : "Create Account"}</div>
     <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",marginBottom:20}}>Apex Business Intelligence · Business Operations</div>
     {mode === "invite" && inviteInfo && (
      <div style={{padding:"8px 12px",background:"rgba(59,130,246,.1)",border:"1px solid rgba(59,130,246,.3)",borderRadius:4,fontSize:11,color:"#60a5fa",marginBottom:16}}>You've been invited to <strong>{LOCATIONS.find(l=>String(l.id)===String(inviteInfo.workspace_id))?.name || "Apex"}</strong>. Set your password to finish.</div>
     )}
     <form onSubmit={submit}>
      {(mode === "register" || mode === "invite") && <>
       <div style={{marginBottom:12}}>
        <div style={{fontSize:10,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase",marginBottom:4}}>Full Name</div>
        <input className="form-control" data-testid="auth-name" value={name} onChange={e=>setName(e.target.value)} placeholder="Jane Smith" required style={{width:"100%"}}/>
       </div>
       {mode === "register" && (
        <div style={{marginBottom:12}}>
         <div style={{fontSize:10,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase",marginBottom:4}}>Assigned Workspace</div>
         <select className="form-control" data-testid="auth-workspace" value={workspaceId} onChange={e=>setWorkspaceId(e.target.value)} style={{width:"100%"}}>
          {LOCATIONS.map(l => <option key={l.id} value={String(l.id)}>{l.name} ({l.address})</option>)}
         </select>
        </div>
       )}
      </>}
      <div style={{marginBottom:12}}>
       <div style={{fontSize:10,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase",marginBottom:4}}>Email</div>
       <input className="form-control" data-testid="auth-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required disabled={mode==="invite"} style={{width:"100%"}}/>
      </div>
      <div style={{marginBottom:16}}>
       <div style={{fontSize:10,letterSpacing:1.5,color:"var(--text3)",textTransform:"uppercase",marginBottom:4}}>{mode==="invite" ? "Set Password" : "Password"}</div>
       <input className="form-control" data-testid="auth-password" type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={6} style={{width:"100%"}}/>
      </div>
      {err && <div data-testid="auth-error" style={{marginBottom:12,padding:"8px 12px",background:"rgba(204,0,0,.12)",border:"1px solid rgba(204,0,0,.35)",borderRadius:4,fontSize:11,color:"#ff5555"}}>{err}</div>}
      {mode === "login" && <ForgotPasswordLink/>}
      {mode === "register" && <InviteRequiredNote/>}
      <button type="submit" disabled={busy} data-testid="auth-submit" className="btn btn-primary" style={{width:"100%",fontSize:13,padding:"10px 16px",marginBottom:12}}>{busy ? "…" : mode === "login" ? "Sign In" : mode === "invite" ? "Accept & Sign In" : "Create Account"}</button>
      {mode !== "invite" && (
       <div style={{textAlign:"center",fontSize:11,color:"var(--text3)"}}>
        {mode === "login" ? <>New to Apex? <button type="button" onClick={()=>{setMode("register");setErr("");}} style={{background:"transparent",border:"none",color:"var(--cyan)",cursor:"pointer",fontSize:11,padding:0,textDecoration:"underline"}}>Create an account</button></>
         : <>Already have an account? <button type="button" onClick={()=>{setMode("login");setErr("");}} style={{background:"transparent",border:"none",color:"var(--cyan)",cursor:"pointer",fontSize:11,padding:0,textDecoration:"underline"}}>Sign in</button></>}
       </div>
      )}
     </form>
    </div>
    <div style={{textAlign:"center",marginTop:14,fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)",letterSpacing:1}}>v2.1 · INFINITY INTEL · SECURE</div>
   </div>
  </div>
 );
}

export default function App() {
 // ─── Round 10 Corporate Master View + Round 11 public sign/survey routes ────
 const [isCorpRoute, setIsCorpRoute] = useState(() => shouldRenderCorporate());
 const [isR11PublicRoute, setIsR11PublicRoute] = useState(() => shouldRenderR11Public());
 useEffect(() => {
  const onHash = () => { setIsCorpRoute(shouldRenderCorporate()); setIsR11PublicRoute(shouldRenderR11Public()); };
  window.addEventListener("hashchange", onHash);
  return () => window.removeEventListener("hashchange", onHash);
 }, []);
 if (shouldRenderResetPassword()) return <ResetPasswordPage />;
 if (isCorpRoute) return <CorporateMasterApp />;
 if (isR11PublicRoute) return <R11PublicApp />;
 return <SubscriberApp />;
}

function SubscriberApp() {
 const [page, setPageRaw] = useState("executive");
 // R15+1 — Custom company logo override (from branding doc)
 const [companyLogo, setCompanyLogo] = useState(null);
 useEffect(() => {
  const syncLocal = () => {
   try {
    const raw = localStorage.getItem("apex_branding_cache");
    if (!raw) return setCompanyLogo(null);
    const b = JSON.parse(raw);
    setCompanyLogo(b?.logo_data_uri || null);
   } catch { setCompanyLogo(null); }
  };
  const syncRemote = async () => {
   try {
    const tok = localStorage.getItem("apex_auth_token"); if (!tok) return;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/iv/r14/branding`, { headers: { Authorization: "Bearer " + tok } });
    if (!r.ok) return;
    const d = await r.json();
    if (d?.branding) {
     try { localStorage.setItem("apex_branding_cache", JSON.stringify(d.branding)); } catch {}
     setCompanyLogo(d.branding.logo_data_uri || null);
    }
   } catch {}
  };
  syncLocal();
  syncRemote();
  const tLocal = setInterval(syncLocal, 4000);
  const tRemote = setInterval(syncRemote, 60000);  // pull fresh branding every minute
  window.addEventListener("storage", syncLocal);
  return () => { clearInterval(tLocal); clearInterval(tRemote); window.removeEventListener("storage", syncLocal); };
 }, []);
 // R13d — page history stack for the global Back button (FIX 7)
 const [pageHistory, setPageHistory] = useState([]);
 const setPage = React.useCallback((next) => {
  setPageRaw(prev => {
   if (next !== prev) setPageHistory(h => [...h, prev].slice(-50));
   return next;
  });
 }, []);
 const goBack = React.useCallback(() => {
  setPageHistory(h => {
   if (!h.length) { setPageRaw("executive"); return h; }
   const prev = h[h.length - 1];
   setPageRaw(prev);
   return h.slice(0, -1);
  });
 }, []);
 const [locIdx, setLocIdx] = useState(0);
 const [time, setTime] = useState(new Date());
 const [jobs, setJobs] = useState(loadLocalJobs);
 const [selectedJobId, setSelectedJobId] = useState(null);
 const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | saved | error
 const [user, setUser] = useState(null); // null until hydrated
 const [authChecked, setAuthChecked] = useState(false);
 // Apex Business Intelligence — Rounds 1 & 2 additive state
 const [showAddTeamWizard, setShowAddTeamWizard] = useState(false);
 const [showOnboarding, setShowOnboarding] = useState(false);
 const [ivLocationId, setIvLocationId] = useState("all");
 const [overlayJobId, setOverlayJobId] = useState(null);
 const [restorationJob, setRestorationJob] = useState(null);
 const [navPerm, setNavPerm] = useState({ is_owner: false, allowed_nav: "*", permission_keys: ["*"] });
 const inviteToken = (() => { try { return new URLSearchParams(window.location.search).get("invite_token"); } catch { return null; } })();
 const openJob = (jobId) => { setSelectedJobId(jobId); setOverlayJobId(jobId); setPage("jobs"); };
 useEffect(()=>{ const id=setInterval(()=>setTime(new Date()),60000); return ()=>clearInterval(id); },[]);

 // Check existing session on mount
 useEffect(()=>{
  (async()=>{
   // If URL has an invite code, force logged-out state so the accept screen shows
   const hasInvite = (() => { try { return !!new URLSearchParams(window.location.search).get("invite"); } catch { return false; } })();
   if(hasInvite){ localStorage.removeItem(TOKEN_STORAGE_KEY); setAuthChecked(true); return; }
   const tok = localStorage.getItem(TOKEN_STORAGE_KEY);
   if(!tok){ setAuthChecked(true); return; }
   try {
    const r = await apiFetch(`${AUTH_API}/me`);
    if(r.ok){ setUser(await r.json()); } else { localStorage.removeItem(TOKEN_STORAGE_KEY); }
   } catch {}
   setAuthChecked(true);
  })();
 },[]);

 // Hydrate jobs from server whenever user is authenticated
 useEffect(()=>{
  if(!user) return;
  (async()=>{
   try {
    const r = await apiFetch(`${FS_API}/jobs`);
    if(r.ok){ const data = await r.json(); if(data?.jobs){ setJobs(data.jobs); localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(data.jobs)); } }
   } catch {}
  })();
 },[user]);

 // Fetch personalised nav permissions on login (Round 12 — dynamic RBAC visibility)
 useEffect(()=>{
  if(!user) return;
  (async()=>{
   try {
    const r = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/iv/r12/me/nav`);
    if(r.ok){ const d = await r.json(); setNavPerm(d); }
    else { setNavPerm({ is_owner: (user.role||"").toLowerCase()==="owner", allowed_nav: (user.role||"").toLowerCase()==="owner" ? "*" : [], permission_keys: [] }); }
   } catch { setNavPerm({ is_owner: false, allowed_nav: [], permission_keys: [] }); }
  })();
 },[user]);

 // Debounced save to server + localStorage on every change (only when authed)
 useEffect(()=>{
  try { localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobs)); } catch {}
  if(!user || !API_BASE) return;
  setSyncStatus("syncing");
  const t = setTimeout(async()=>{
   try {
    const r = await apiFetch(`${FS_API}/jobs/bulk`, { method:"PUT", body: JSON.stringify({jobs}) });
    setSyncStatus(r.ok ? "saved" : "error");
   } catch { setSyncStatus("error"); }
  }, 800);
  return ()=>clearTimeout(t);
 }, [jobs, user]);

 // R13 — listen for cross-component navigation requests (e.g. Notes "schedule prompt → Yes")
 useEffect(() => {
  const handler = (e) => { const p = e?.detail?.page; if (p && typeof p === "string") setPage(p); };
  window.addEventListener("apex:navigate", handler);
  return () => window.removeEventListener("apex:navigate", handler);
 }, []);

 // R17 — Start chat presence heartbeat once the user is signed in
 useEffect(() => { if (user) startChatPresence(); }, [user]);
 const chatUnread = useChatUnread();

 // Round 12 — Auto-bounce away from a hidden page when RBAC changes
 useEffect(()=>{
  if (!user) return;
  const allowed = navPerm?.allowed_nav;
  if (allowed === "*" || navPerm?.is_owner) return;
  if (!Array.isArray(allowed)) return;
  if (page === "executive") return;
  if (!allowed.includes(page)) setPage(allowed.includes("executive") ? "executive" : (allowed[0] || "executive"));
 }, [navPerm, page, user]);

 const resetJobs = async () => {
  try {
   const r = await apiFetch(`${FS_API}/jobs/reset`, { method:"POST" });
   if(r.ok){ const data = await r.json(); setJobs(data.jobs); localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(data.jobs)); return; }
  } catch {}
  localStorage.removeItem(JOBS_STORAGE_KEY); setJobs(SEED_JOBS);
 };
 const logout = async () => {
  try { await apiFetch(`${AUTH_API}/logout`, {method:"POST"}); } catch {}
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(JOBS_STORAGE_KEY);
  setUser(null); setJobs(SEED_JOBS);
 };

 if(!authChecked) return <><style>{css}</style><div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text3)",fontFamily:"var(--mono)",fontSize:11,letterSpacing:2}}>● APEX SYNC…</div></>;
 if(inviteToken && !user) return <><style>{css}</style><ToastHost/><InviteAcceptScreen token={inviteToken} onAccepted={u=>setUser(u)}/></>;
 if(!user) return <><style>{css}</style><LoginScreen onAuthed={u=>setUser(u)}/></>;

 // Round 13c — Flat alphabetical sidebar (Executive Dashboard pinned first)
 // All other nav items below it are sorted by label (case-insensitive).
 // Section headers removed entirely per user spec — single clean list.
 const _baseItems = [
  {id:"executive",label:"Executive Dashboard"},
  {id:"dashboard",label:"Operations"},
  {id:"analytics",label:"Analytics"},
  {id:"technicians",label:"Technicians",badge:TECHNICIANS.filter(t=>t.status==="On Job").length},
  {id:"drivers",label:"Drivers",badge:DRIVERS.filter(d=>d.status!=="Available").length},
  {id:"jobs",label:"Jobs",badge:jobs.length,badgeColor:"cyan"},
  {id:"iv-control-center",label:"Control Center"},
  {id:"iv-schedule-day",label:"Schedule · Day"},
  {id:"iv-map-pins",label:"Live Tech Map"},
  {id:"iv-service-requests",label:"Service Requests"},
  {id:"iv-timecards",label:"Timecards"},
  {id:"customers",label:"Customers"},
  {id:"iv-chat-now",label:"Chat Now",badge:chatUnread,badgeColor:"cyan"},
  {id:"inventory",label:"Inventory",badge:INVENTORY.filter(i=>i.available<=i.reorder).length,badgeColor:"amber"},
  {id:"iv-equipment",label:"Equipment"},
  {id:"iv-materials",label:"Materials"},
  {id:"iv-documents",label:"Documents"},
  {id:"iv-contacts-dedupe",label:"Dedupe Contacts"},
  {id:"quickbooks",label:"QuickBooks"},
  {id:"xactimate",label:"Xactimate"},
  {id:"iv-xactimate-board",label:"Xactimate Board"},
  {id:"iv-avg-job-size",label:"Avg Job Size"},
  {id:"iv-pnl-v2",label:"P&L (Operations)"},
  {id:"reports",label:"Reports"},
  {id:"locations",label:"Locations"},
  {id:"iv-my-profile",label:"My Profile"},
  {id:"settings",label:"Settings"},
  ...(user?.role !== "owner" && user?.system_role === "Admin" ? [{id:"iv-team-access",label:"Team and Access"}] : []),
  ...(user?.role === "owner" ? [
   {id:"team",label:"Team"},
   {id:"payroll",label:"Team & Payroll"},
   {id:"pnl",label:"Office P&L"},
   {id:"sales",label:"Sales Tracking"},
   {id:"iv-import-center",label:"Import Center"},
   // Administration items (owner-only) — merged into the flat alphabetized list
   {id:"iv-team-access",label:"Team and Access"},
   {id:"iv-company-profile",label:"Company Profile"},
   {id:"iv-legacy-plan",label:"Legacy Plan"},
   {id:"iv-billing",label:"Billing"},
   {id:"iv-quickbooks",label:"QuickBooks Sync"},
   {id:"iv-payroll-providers",label:"Payroll Providers"},
   {id:"iv-restoration-reports",label:"Restoration Reports"},
   {id:"iv-schedule-views",label:"Schedule Views"},
   {id:"iv-email-log",label:"Email Log"},
   {id:"iv-arm-wo",label:"ARM Work Orders"},
   {id:"iv-sync",label:"Response Management Sync"},
   {id:"iv-special-perms",label:"Special Permissions"},
  ] : []),
 ];
 const _execItem = _baseItems.find(x => x.id === "executive");
 const _rest = _baseItems
   .filter(x => x.id !== "executive")
   .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
 const NAV = [{ section: "", items: [_execItem, ..._rest] }];

 // Round 12 — Dynamic Navigation Visibility (RBAC + Special Permissions)
 // Owner sees everything (`allowed_nav === "*"`). Non-owners see only the
 // ids returned by /api/iv/r12/me/nav. Hidden items are completely removed
 // from the sidebar (not greyed-out, not locked) per ADDITIVE spec.
 const navAllowed = navPerm?.allowed_nav;
 const NAV_FILTERED = (navAllowed === "*" || navPerm?.is_owner)
   ? NAV
   : NAV.map(sec => ({ ...sec, items: sec.items.filter(it => it && Array.isArray(navAllowed) && navAllowed.includes(it.id)) }))
        .filter(sec => sec.items.length > 0);

 const renderPage = () => {
 switch(page) {
 case "executive": return <ExecutiveDashboard jobs={jobs} setJobs={setJobs} onOpenJob={openJob}/>;
 case "dashboard": return <OperationsDashboard jobs={jobs}/>;
 case "analytics": return <Analytics jobs={jobs}/>;
 case "technicians": return <Technicians/>;
 case "drivers": return <Drivers/>;
 case "customers": return <Customers/>;
 case "jobs": return <Jobs jobs={jobs} setJobs={setJobs} selectedJobId={selectedJobId} setSelectedJobId={setSelectedJobId} user={user} locIdx={locIdx} onOpenOverlay={setOverlayJobId} onOpenRestoration={setRestorationJob}/>;
 case "inventory": return <Inventory/>;
 case "quickbooks": return <QuickBooks/>;
 case "xactimate": return <Xactimate jobs={jobs}/>;
 case "reports": return <Reports jobs={jobs}/>;
 case "locations": return <LocationsPage/>;
 case "team": return user?.role === "owner" ? <TeamPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "payroll": return user?.role === "owner" ? <PayrollPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "pnl": return user?.role === "owner" ? <PnLPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "sales": return user?.role === "owner" ? <SalesPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "settings": return <Settings/>;
 case "iv-team-access": return (user?.role === "owner" || user?.system_role === "Admin") ? <TeamAndAccess onOpenWizard={()=>setShowAddTeamWizard(true)}/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-company-profile": return user?.role === "owner" ? <><CompanyProfile/><BrandingPanel user={user}/></> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-legacy-plan": return user?.role === "owner" ? <LegacyPlanPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-import-center": return user?.role === "owner" ? <ImportCenter/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-arm-wo": return user?.role === "owner" ? <ARMWorkOrdersPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-sync": return user?.role === "owner" ? <SyncManagementPanel/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-documents": return <DocumentsCenter/>;
 case "iv-schedule-day": return <ScheduleDayView/>;
 case "iv-equipment": return <><EquipmentTracking/><EquipmentR14/></>;
 case "iv-contacts-dedupe": return user?.role === "owner" ? <ContactsDedupe/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-xactimate-board": return <XactimateBoard/>;
 case "iv-timecards": return <TimecardsPage/>;
 case "iv-materials": return <MaterialsPage/>;
 case "iv-service-requests": return <ServiceRequestsPage/>;
 case "iv-control-center": return <ControlCenter/>;
 case "iv-map-pins": return <MapTechPins/>;
 case "iv-billing": return user?.role === "owner" ? <BillingPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-quickbooks": return user?.role === "owner" ? <QuickBooksPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-payroll-providers": return user?.role === "owner" ? <PayrollProvidersPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-restoration-reports": return user?.role === "owner" ? <RestorationReportsPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
 case "iv-schedule-views": return <ScheduleAllViewsPage/>;
 case "iv-my-profile": return <MyProfilePage user={user}/>;
 case "iv-email-log": return user?.role === "owner" ? <EmailLogPage/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
case "iv-avg-job-size": return <AvgJobSizePage onBack={()=>setPage("executive")}/>;
case "iv-pnl-v2": return user?.role === "owner" ? <PnLv2Page onBack={()=>setPage("executive")}/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
case "iv-special-perms": return user?.role === "owner" ? <SpecialPermissionsPage onBack={()=>setPage("executive")}/> : <div style={{padding:24,color:"var(--text3)"}}>Owner access only.</div>;
case "iv-chat-now": return <ChatNowPage currentUser={user}/>;
 default: return <ExecutiveDashboard jobs={jobs} setJobs={setJobs} onOpenJob={openJob}/>;
 }
 };

 return (
 <>
 <style>{css}</style>
 <div className="app">
 <div className="sidebar">
 <div className="sidebar-logo">
 <img src={companyLogo || LOGO_URL} alt="Company Logo" className="sidebar-logo-img" data-testid="sidebar-logo-img" style={companyLogo ? { width: "auto", maxWidth: "90%", maxHeight: "85%" } : undefined}/>
 </div>
 <select className="loc-select" value={locIdx} onChange={e=>setLocIdx(+e.target.value)}>
 {LOCATIONS.map((l,i)=><option key={i} value={i}>{l.name}</option>)}
 </select>
 {NAV_FILTERED.map(({section,items})=>(
 <div key={section || "__nav__"}>
 {section && <div className="nav-section">{section}</div>}
 {items.map(item=>(
 <div key={item.id} className={`nav-item ${page===item.id?"active":""}`} onClick={()=>setPage(item.id)}>
 <span>{item.label}</span>
 {item.badge>0&&<span className={`badge ${item.badgeColor==="amber"?"amber":""}`}>{item.badge}</span>}
 </div>
 ))}
 </div>
 ))}
 <div style={{marginTop:"auto",padding:"12px 18px",borderTop:"1px solid var(--border)",fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)"}}>
 <div style={{fontSize:10,color:"var(--cyan)",letterSpacing:1,marginBottom:4,wordBreak:"break-all"}} data-testid="user-email">{user?.email}</div>
 <div style={{fontSize:9,letterSpacing:1.5,textTransform:"uppercase",color:user?.role==="owner"?"var(--green)":"var(--text3)"}} data-testid="user-role">{user?.role === "owner" ? "owner" : (user?.system_role || user?.role || "member")}{user?.workspace_id ? ` · WS-${user.workspace_id}` : ""}</div>
 <div style={{marginTop:6,fontSize:9}}>APEX SYNC ● {syncStatus==="syncing"?"SAVING…":syncStatus==="saved"?"SAVED":syncStatus==="error"?"OFFLINE":"ACTIVE"}</div>
 {user?.role === "owner" && <button className="btn btn-ghost" onClick={()=>{ if(window.confirm("Reset all jobs, calls, and add-ons back to demo seed data on the server?")) resetJobs(); }} style={{marginTop:6,fontSize:9,padding:"3px 8px",width:"100%"}} title="Wipe server jobs collection and reseed">↻ Reset Demo Data</button>}
 <button className="btn btn-outline" data-testid="logout-btn" onClick={logout} style={{marginTop:6,fontSize:9,padding:"3px 8px",width:"100%"}}>↪ Sign Out</button>
 </div>
 </div>
 <div className="main">
 <div className="topbar">
 <div style={{display:"flex",alignItems:"center",gap:12}}>
 <button data-testid="global-back-btn" onClick={goBack} disabled={pageHistory.length===0} title={pageHistory.length?`Back to ${PAGE_TITLES[pageHistory[pageHistory.length-1]]||pageHistory[pageHistory.length-1]}`:"No history"} style={{padding:"6px 12px",fontSize:11,fontWeight:700,border:"1px solid var(--border)",borderRadius:4,background:pageHistory.length?"var(--bg)":"transparent",color:pageHistory.length?"var(--text)":"var(--text3)",cursor:pageHistory.length?"pointer":"not-allowed",letterSpacing:.5}}>← Back</button>
 <div className="page-title"><span>{PAGE_TITLES[page]}</span></div>
 </div>
 <div className="topbar-right">
 <div className="flex gap8"><div className="status-dot"/><span className="live-tag">LIVE · {time.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span></div>
 {user?.role === "owner" && <LocationSwitcher value={ivLocationId} onChange={setIvLocationId}/>}
 {user?.role === "owner" && <AppSwitcher/>}
 <GlobalClockButton/>
 <NotificationBell/>
 <button className="btn btn-ghost" style={{fontSize:11}}>Sync Apex</button>
 <button className="btn btn-primary" data-testid="topbar-new-job" style={{fontSize:11}} onClick={()=>setPage("jobs")} title="Go to Jobs page to create a new job">+ New Job</button>
 </div>
 </div>
 <div className="content">
 {page === "executive" && user?.role === "owner" && <SetupChecklistWidget onOpenWizard={()=>setShowOnboarding(true)}/>}
 {page === "executive" && user?.role === "owner" && <QuickReportsPanel onOpenReport={(k)=>setPage(k==="ar_aging"?"reports":k==="schedule_today"?"technicians":k==="equipment_on_job"?"inventory":"jobs")}/>}
 {page === "executive" && user?.role === "owner" && <InsuranceStatusWidget onManage={()=>setPage("iv-company-profile")}/>}
 {page === "executive" && user?.role === "owner" && <LegacyStatusWidget onOpen={()=>setPage("iv-legacy-plan")}/>}
 {page === "executive" && <TotalInvoicesCard onOpen={()=>setPage("reports")}/>}
 {renderPage()}
 </div>
 </div>
 </div>
 {showAddTeamWizard && <AddTeamMemberWizard onClose={()=>setShowAddTeamWizard(false)} onCreated={()=>{}}/>}
 {showOnboarding && <CompanyOnboarding onClose={()=>setShowOnboarding(false)}/>}
 {overlayJobId && <JobSideOverlay jobId={overlayJobId} onClose={()=>setOverlayJobId(null)}/>}
 {restorationJob && <RestorationOverlay job={restorationJob} onClose={()=>setRestorationJob(null)}/>}
 <ToastHost/>
 </>
 );
}
